/**
 * Crime Ops Terminal – /business/crime-ops
 *
 * Sektionen (Geldwäsche, Dealer, Labor, Schmuggel, Kartell) als Tab-Buttons oben.
 * Rotes Design – nur für diesen Tab, sehr schlicht.
 */

import { CRIME_OPS_CATEGORIES, getCategoryForSection } from './crimeOps.categories.js';
import { selectRunningOrders } from './crimeOps.orders-filter.js';
import { api, ApiError } from '../../services/api.js';
import { navigate } from '../../router.js';
import { gameState as _gameState } from '../../state/gameState.js';
import {
  renderImmobilienTab,
  mountImmobilienTab,
  cleanupImmobilienTab,
  refreshImmobilienTab,
} from '../../components/crime-ops/immobilien-tab.js';
import {
  renderViertelTab,
  mountViertelTab,
  cleanupViertelTab,
  refreshViertelTab,
} from '../../components/crime-ops/viertel-tab.js';
import {
  renderGewaechshausTab,
  mountGewaechshausTab,
  cleanupGewaechshausTab,
  refreshGewaechshausTab,
} from '../../components/crime-ops/gewaechshaus-tab.js';
import {
  renderRolltischTab,
  mountRolltischTab,
  cleanupRolltischTab,
  refreshRolltischTab,
} from '../../components/crime-ops/rolltisch-tab.js';
import {
  renderLaborTab,
  mountLaborTab,
  cleanupLaborTab,
  refreshLaborTab,
} from '../../components/crime-ops/labor-tab.js';
import {
  renderStreetMarketTab,
  mountStreetMarketTab,
  cleanupStreetMarketTab,
  refreshStreetMarketTab,
} from '../../components/crime-ops/streetmarket-tab.js';
import {
  renderDarknetTab,
  mountDarknetTab,
  cleanupDarknetTab,
  refreshDarknetTab,
} from '../../components/crime-ops/darknet-tab.js';
import {
  renderSchwarzmarktTab,
  mountSchwarzmarktTab,
  cleanupSchwarzmarktTab,
  refreshSchwarzmarktTab,
} from '../../components/crime-ops/schwarzmarkt-tab.js';
import { badgeHtml, getCleanItemName, getQualityBadge, QUALITY_BADGE_META, renderItemBadges } from '../../utils/item-badges.js';
import { clearRequiredBadges } from '../../utils/required-badge.js';
import { showToast } from '../../utils/toast.js';
import { refreshBalance } from '../../utils/refreshBalance.js';
import { tryOfferEmergencyGarage } from '../../components/EmergencyGarageOfferModal.js';
import { renderPropertyOptions, type PropertySelectItem } from '../../utils/property-select.js';
import { computeTransportPhaseUi } from '../../utils/transport-phases.js';
import { renderWhenIdle, cancelPendingRender } from '../../utils/background-render.js';
import { getCrimeOpsSectionForPath, getPathForCrimeOpsSection, isCrimeOpsPath } from '../../utils/dashboard-routes.js';
import { updateBottomNavTab } from '../../components/BottomNav.js';
import { showMissionEventModal, hideMissionEventModal } from '../../components/mission-event-modal.js';
import { DESKTOP_LAYOUT_QUERY, MOBILE_LAYOUT_QUERY } from '../../utils/mobile-layout.js';

// ─── Requirement-Typen (Aufgabe 6: Freischalt-Checkliste) ────────────────────

interface Requirement {
  label:       string;
  met:         boolean;
  hint:        string | null;
  action:      string | null;
  actionLabel: string | null;
}

// ─── Labor-Typen ──────────────────────────────────────────────────────────────

interface LaborPropertyInfo { id: string; label: string; subType: string; tier: string; batches: number; maxBatches: number; }
interface LaborBatchInfo    { id: string; recipeKey: string; recipeLabel: string; propertyId: string; propertyLabel: string; startedAt: string; completesAt: string; qualityCheckAt: string; qualityCheckDone: boolean; status: string; progressPercent: number; outputBonus: number; }
interface RecipeCatalogEntry { key: string; label: string; description: string; cookTimeMinutes: number; baseOutputQty: number; outputValueCents: string; outputCurrency: string; risk: string; requiredTier: string; lore: string; locked: boolean; lockReason: string | null; ingredients: Record<string, number>; permanentIngredients?: string[]; }
interface LaborStatusData    { labs: LaborPropertyInfo[]; activeBatches: LaborBatchInfo[]; completedBatches: LaborBatchInfo[]; recipes: RecipeCatalogEntry[]; }

// ─── Dealer-Typen ─────────────────────────────────────────────────────────────

type DealerTier = 'TIER_1' | 'TIER_2' | 'TIER_3';
type DealerStatus = 'ACTIVE' | 'PAUSED' | 'TRAINING' | 'ARRESTED' | 'FLEEING' | 'GONE';
interface DealerInventoryItemResponse {
  id: string;
  itemType: string;
  itemId: string;
  quantity: number;
}
interface DealerResponse {
  id: string;
  name: string;
  tier: DealerTier;
  status: DealerStatus;
  suspicion: number;
  suspicionZone: 'LOW' | 'MEDIUM' | 'HIGH';
  effectiveSlots: number;
  usedSlots: number;
  pendingEarnings: string;
  vehicleId: string | null;
  vehicleKey: string | null;
  vehicleLabel: string | null;
  trainingStartedAt: string | null;
  trainingCompletesAt: string | null;
  arrestedAt: string | null;
  arrestedUntil: string | null;
  arrestSuspicion: number | null;
  inventory: DealerInventoryItemResponse[];
  deployedAt: string;
  updatedAt: string;
  pendingSupplyDeliveries: DealerPendingSupplyDelivery[];
  // ─── Ecken (NT-VIERTEL-04-ECKEN) ─────────────────────────────────────────
  streetCornerId: string | null;
  cornerName: string | null;
  streetName: string | null;
  streetClass: 'RUHIG' | 'BELEBT' | 'HEISS' | null;
  relocateCompletesAt: string | null;
  // NT-VIERTEL-07-FEINSCHLIFF (B3) — server-berechnet, siehe presence.definitions.ts::
  // deriveAbsatzLabel(). null ohne Ecke ("OHNE POSTEN").
  absatzLabel: 'GEDROSSELT' | 'NORMAL' | 'STARK' | null;
  // NT-POLISH-01 (P5): Kosten/Dauer der nächsten Schulung — null auf Höchststufe.
  nextTraining: { costCents: string; hours: number } | null;
}
interface DealerPendingSupplyDelivery {
  id: string;
  vehicleKey: string;
  itemCount: number;
  startedAt: string;
  loadingEndsAt: string | null;
  unloadingStartsAt: string | null;
  arrivesAt: string;
}
interface DealerSupplyItem { itemKey: string; itemType: string; quantity: number; }
interface DealerSupplySource { sourceId: string; sourceType: 'LAGER' | 'VERSTECK'; sourceLabel: string; items: DealerSupplyItem[]; }
interface DealerStatusData { dealers: DealerResponse[]; supplySources: DealerSupplySource[]; maxDealers: number; canHireMore: boolean; }

// ─── Schmuggel-Typen ──────────────────────────────────────────────────────────

interface SchmuggelRoute  { key: string; label: string; vehicleTypes: string[]; durationMinutes: number; baseRisk: number; ertragsMulti: number; needsSchleuser: boolean; locked: boolean; lockReason: string | null; lore: string; }
interface ActiveOpInfo    { id: string; routeKey: string; routeLabel: string; vehicleKey: string; itemKey: string; quantity: number; successChance: number; startedAt: string; arrivesAt: string; remainingMs: number; }
interface CompletedOpInfo { id: string; routeKey: string; vehicleKey: string; itemKey: string; quantity: number; status: 'SUCCESS' | 'FAILED'; earnedCents: string | null; earnedCurrency: string | null; }
interface VehicleInfo     { propertyId: string; propertyLabel: string; vehicleKey: string; quantity: number; }
interface SchmuggelStatusData { routes: SchmuggelRoute[]; active: ActiveOpInfo[]; completed: CompletedOpInfo[]; vehicles: VehicleInfo[]; hasSchleuser: boolean; }

// Schmuggel-Run Checkpoint-System (Abschnitt A)
type SmuggleRisk = 'LOW' | 'MEDIUM' | 'HIGH';
type SmuggleRunStat = 'RUNNING' | 'CHECKPOINT' | 'COMPLETED' | 'FAILED';
interface SmuggleCheckpointOption { id: 'A' | 'B' | 'C'; label: string; description: string; riskReduction: number; costCents: string; timeAddedMinutes: number; safeOption: boolean; }
interface SmuggleActiveCheckpoint { checkpointId: string; number: number; type: string; typeLabel: string; lore: string; options: SmuggleCheckpointOption[]; finalFailChance: number; }
interface SmuggleRunView { id: string; vehicleKey: string; vehicleLabel: string; itemKey: string; itemLabel: string; quantity: number; riskLevel: SmuggleRisk; status: SmuggleRunStat; currentCheckpoint: number; totalCheckpoints: number; startedAt: string; nextEventAt: string | null; completedAt: string | null; activeCheckpoint: SmuggleActiveCheckpoint | null; flWarning: string | null; }
interface SmuggleDailyOfferItem { itemKey: string; label: string; riskLevel: SmuggleRisk; basePriceCents: string; }
interface SmuggleRunStatusData { dailyOffer: SmuggleDailyOfferItem[]; rotatesAt: string; activeRun: SmuggleRunView | null; hasSchleuser: boolean; schleuerStage: 1 | 2 | 3 | null; schleuerRuns: number; schleuerNextAt: number | null; flWarning: string | null; blackMoneyBalance: string; }
interface SmuggleHistoryEntry { id: string; itemKey: string; itemLabel: string; quantity: number; status: SmuggleRunStat; startedAt: string; completedAt: string | null; }

// ─── Typen ────────────────────────────────────────────────────────────────────

type Section = 'geldwaesche' | 'auftraege' | 'strassenhandel' | 'dealer' | 'labor' | 'schmuggel' | 'gewächshaus' | 'rolltisch' | 'schwarzmarkt' | 'immobilien' | 'kontakte' | 'darknet' | 'viertel';

interface NpcOrderItem {
  id: string;
  npcId: string;
  npcName: string;
  npcType: 'HEHLER' | 'SCHWARZMARKT' | 'SCHROTTHAENDLER';
  status: 'OPEN' | 'ACCEPTED' | 'PARTIAL' | 'DONE' | 'EXPIRED' | 'FAILED';
  itemType: string;
  itemLabel: string;
  inventoryKey: string | null;
  warehouseStock: number;
  totalQty: number;
  deliveredQty: number;
  remainingQty: number;
  expiresAt: string;
  completedAt: string | null;
  rewardNoxCents: string;
  rewardBlackMoneyCents: string;
  rewardPerUnitCents: string;
  alreadyEarnedCents: string;
  trustLevel: number;
  hungerFactor: number;
}

interface OrderNpcUnlockProgress {
  requiredLevel: number;
  levelMet: boolean;
  requiredJobLabels: string[];
  requiredCompletions: number;
  currentCompletions: number;
}

interface OrderNpcView {
  npcType: 'HEHLER' | 'SCHWARZMARKT' | 'SCHROTTHAENDLER';
  id: string | null;
  name: string;
  unlockedAt: string | null;
  returnsAt: string | null;
  requiredLevel: number;
  unlockProgress: OrderNpcUnlockProgress | null;
}

interface NpcMessageView {
  id: string;
  npcType: OrderNpcType;
  npcName: string;
  text: string;
  status: 'PENDING' | 'ACCEPTED' | 'IGNORED' | 'DISMISSED';
  sentAt: string;
  expiresAt: string;
}

interface VehicleSlotView {
  slotIndex: number;
  itemKey: string | null;
  quantity: number | null;
}

interface VehicleInventoryData {
  vehicleId: string;
  vehicleKey: string;
  label: string;
  capacity: number;
  occupiedSlots: number;
  freeSlots: number;
  speedFactor: number;
  slots: VehicleSlotView[];
}
interface TalentNode {
  id: string;
  investedPoints: number;
  isActive: boolean;
}

interface TalentTreeData {
  availableTalentPoints: number;
  branches: Record<string, TalentNode[]>;
}

interface VehicleSummary {
  id: string;
  vehicleKey: string;
  label: string;
  capacity: number;
  speedFactor: number;
  isAssignedToDealer: boolean;
  inTransport: boolean;
  // NT-HOTFIX-01 (H1): vom Backend berechnet (keine Rolle, nicht unterwegs, kein Lager-Fahrzeugslot) —
  // einzige Quelle für "auswählbar" in Dealer-Liefern- und Dealer-Fahrzeug-Auswahl.
  isAvailable: boolean;
  // NT-VIERTEL-04-ECKEN (E9) — gesetzt, während das Fahrzeug beschlagnahmt ist.
  seizedUntil: string | null;
}

interface DealerSupplyModalItem {
  itemKey:   string;
  itemType:  string;
  available: number;
  selected:  boolean;
  qty:       number;
}

interface DealerSupplyPropertySource {
  id:             string;
  label:          string;
  customName:     string | null;
  nickname:       string | null;
  isHideout:      boolean;
  inventoryItems: { itemType: string; itemKey: string; quantity: number; illegal: boolean; isDealerDrug: boolean }[];
}

interface DealerSupplyModal {
  dealerId:         string;
  dealerName:       string;
  freeSlots:        number;
  vehicleKey:       string;
  sourcePropertyId: string;
  items:            DealerSupplyModalItem[];
  errorMsg:         string | null;
}

interface OrdersSectionData {
  active: NpcOrderItem[];
  history: NpcOrderItem[];
  npcs: OrderNpcView[];
}

// ─── Auftragsbrett (NT-AUFTRAGSBRETT-01) ────────────────────────────────────────

type AuftraggeberTypView = 'SCHROTTHAENDLER' | 'HEHLER' | 'MITTELSMANN' | 'KNASTKONTAKT';

interface AuftragAngebotView {
  id: string;
  auftraggeberTyp: AuftraggeberTypView;
  auftraggeberName: string;
  auftragTyp: 'BUNKERN' | 'TRANSPORT';
  gebundeneSlots: number;
  stunden: number;
  rewardCents: string;
  rewardCurrency: 'NOX' | 'BLACK_MONEY' | 'SCHEINE';
  expiresAt: string;
  // TRANSPORT (NT-AUFTRAGSBRETT-02) — null für BUNKERN, Quelle/Ziel/Distanz bereits vom Backend
  // fixiert. Rohe IDs zusätzlich zu den Labels — das Annahme-Modal braucht sie für die
  // Dauer-Vorschau (POST /transport/preview), bevor der Auftrag existiert.
  quellePropertyId: string | null;
  zielPropertyId: string | null;
  quellePropertyLabel: string | null;
  zielPropertyLabel: string | null;
  steps: number | null;
}

interface AuftragTransportPhaseView {
  startedAt: string;
  loadingEndsAt: string | null;
  unloadingStartsAt: string | null;
  arrivesAt: string;
  status: string;
}

// NT-AUFTRAGSBRETT-05 — null solange kein Zwischenfall ausgelöst wurde. GERÄUSCHE ist bei
// Ankunft hier immer bereits aufgelöst (entscheidung bleibt null, ergebnis ist gesetzt) — die
// anderen drei sind "offen" solange entscheidung===null UND entscheidungBisAt noch in der Zukunft.
type AuftragEreignisTypView = 'FRUEHER_ABHOLEN' | 'KONTROLLE' | 'BESSERES_ANGEBOT' | 'GERAEUSCHE';
type AuftragEreignisEntscheidungView = 'AKTIV' | 'STANDARD';

interface AuftragEreignisView {
  typ: AuftragEreignisTypView;
  label: string;
  ausgeloestAm: string;
  entscheidungBisAt: string | null;
  entscheidung: AuftragEreignisEntscheidungView | null;
  ergebnis: string | null;
}

// NT-AUFTRAGSBRETT-05 — reine Anzeige-Duplikate der Backend-Button-Beschriftungen
// (EREIGNIS_ENTSCHEIDUNG_LABEL, auftragsbrett.definitions.ts) — kein Shared-Import für reine
// Anzeige-Werte, etabliertes Pattern in diesem Repo. GERAEUSCHE hat keine Wahl, kein Eintrag.
const EREIGNIS_ENTSCHEIDUNG_LABEL: Partial<Record<AuftragEreignisTypView, Record<AuftragEreignisEntscheidungView, string>>> = {
  FRUEHER_ABHOLEN: { AKTIV: 'ANNEHMEN (AUFSCHLAG)', STANDARD: 'ABLEHNEN' },
  KONTROLLE: { AKTIV: 'DURCHFAHREN', STANDARD: 'UMFAHREN' },
  BESSERES_ANGEBOT: { AKTIV: 'KISTE VERKAUFEN', STANDARD: 'ABLEHNEN' },
};

interface AuftragView {
  id: string;
  auftraggeberTyp: AuftraggeberTypView;
  auftraggeberName: string;
  auftragTyp: 'BUNKERN' | 'TRANSPORT';
  status: 'LAUFEND' | 'ABGESCHLOSSEN' | 'ABGEBROCHEN';
  itemLabel: string;
  gebundeneSlots: number;
  propertyId: string;
  propertyLabel: string;
  rewardCents: string;
  rewardCurrency: 'NOX' | 'BLACK_MONEY' | 'SCHEINE';
  stunden: number;
  acceptedAt: string;
  bunkerBisAt: string | null;
  completedAt: string | null;
  // TRANSPORT (NT-AUFTRAGSBRETT-02) — null für BUNKERN.
  zielPropertyLabel: string | null;
  transportPhase: AuftragTransportPhaseView | null;
  // NT-AUFTRAGSBRETT-05
  ereignis: AuftragEreignisView | null;
}

// NT-AUFTRAGSBRETT-01B (PR5) — reine Anzeige-Projektion von AUFTRAGGEBER_PROFILE[typ] aus dem
// Backend, keine zweite Werte-Quelle im Frontend. Wird sowohl für die Profilzeile auf der Karte
// als auch für die Objektwahl-/Verdachts-Filterung im Annahme-Modal genutzt (ersetzt die vorher
// hartcodierten KNASTKONTAKT-Sonderfälle).
interface AuftraggeberProfileView {
  objektwahl: 'LAGER' | 'VERSTECK';
  slotsMin: number;
  slotsMax: number;
  liegezeitStundenMin: number;
  liegezeitStundenMax: number;
  verdachtsaufschlag: number;
  waehrung: 'NOX' | 'BLACK_MONEY' | 'SCHEINE';
}

interface AuftraggeberStatusView {
  auftraggeberTyp: AuftraggeberTypView;
  name: string;
  unlockedAt: string | null;
  cooldownUntil: string | null;
  completedCount: number;
  rufStufe: number;
  rufFaktor: number;
  unlockProgress: {
    requiredLevel: number;
    levelMet: boolean;
    requiredJobLabels: string[];
    requiredCompletions: number;
    currentCompletions: number;
  } | null;
  // NT-AUFTRAGSBRETT-03 (K2) — nur KNASTKONTAKT, sonst immer null.
  bonusUntil: string | null;
  profile: AuftraggeberProfileView;
}

type AuftragsbrettBlockReason = 'KEIN_LAGER_MIT_PLATZ' | 'KEIN_VERSTECK_MIT_PLATZ' | 'ZU_WENIG_OBJEKTE' | 'GLEICHE_STRASSE' | 'KEIN_FREIES_FAHRZEUG';

interface AuftragsbrettBoardView {
  angebote: AuftragAngebotView[];
  aktive: AuftragView[];
  auftraggeber: AuftraggeberStatusView[];
  // NT-AUFTRAGSBRETT-01B (Q2): auftraggeberTyp ist nie mehr null — jeder Grund ist strikt einem
  // einzelnen Auftraggeber zugeordnet (auch die TRANSPORT-Gründe, vorher ein globaler Eintrag).
  blocked: { auftraggeberTyp: AuftraggeberTypView; auftragTyp: 'BUNKERN' | 'TRANSPORT'; reason: AuftragsbrettBlockReason; neededSlots: number | null }[];
}

interface AuftragHistoryItem {
  id: string;
  auftraggeberTyp: AuftraggeberTypView;
  auftraggeberName: string;
  status: 'ABGESCHLOSSEN' | 'ABGEBROCHEN';
  gebundeneSlots: number;
  // NT-AUFTRAG-PROPERTY-SETNULL-01 — "OBJEKT VERKAUFT", wenn das Objekt inzwischen gelöscht ist.
  propertyLabel: string;
  rewardCents: string;
  rewardCurrency: 'NOX' | 'BLACK_MONEY' | 'SCHEINE';
  acceptedAt: string;
  completedAt: string | null;
  // NT-AUFTRAGSBRETT-05
  ereignis: AuftragEreignisView | null;
}

interface FreePropertySlotView {
  id: string;
  type: 'WAREHOUSE' | 'HIDEOUT';
  subType: string;
  label: string;
  customName: string | null;
  nickname: string | null;
  freeSlots: number;
}

interface AuftragsbrettAcceptModal {
  angebotId: string;
  auftraggeberTyp: AuftraggeberTypView;
  auftraggeberName: string;
  auftragTyp: 'BUNKERN' | 'TRANSPORT';
  gebundeneSlots: number;
  propertyId: string;
  errorMsg: string | null;
  // TRANSPORT (NT-AUFTRAGSBRETT-02) — Quelle/Ziel sind vom Angebot fixiert (nur Anzeige), der
  // Spieler wählt aktiv nur das Fahrzeug. quelle/zielPropertyId werden für die Dauer-Vorschau
  // (POST /transport/preview) gebraucht.
  quellePropertyId: string | null;
  zielPropertyId: string | null;
  quellePropertyLabel: string | null;
  zielPropertyLabel: string | null;
  vehicleKey: string;
  durationPreviewText: string;
}

interface BreakInMissionContactPreview {
  id: string;
  missionType: string;
  status: 'AVAILABLE' | 'RUNNING' | 'COMPLETED' | 'ABORTED' | 'ARRESTED' | 'EXPIRED';
  riskBase: number;
  baseRewardBlackMoneyCents: string;
  label: string;
  description: string;
}

interface BreakInMissionContact {
  id: string;
  name: string;
  batch: string;
  releasedAt: string;
  availableUntil: string;
  mission: BreakInMissionContactPreview | null;
}

interface BreakInMissionEventOption {
  index: number;
  label: string;
  outcome: 'CONTINUE' | 'ABORT' | 'BONUS' | 'RISK';
  riskDelta: number;
  rewardMod: number;
  requires: { itemType: string; qty: number; available: boolean } | null;
}

interface BreakInMissionEvent {
  id: string;
  type: 'PASSIVE' | 'REACTION' | 'DECISION';
  label: string;
  description: string;
  timerSeconds: number;
  decisionExpiresAt: string | null;
  choices: BreakInMissionEventOption[];
}

interface BreakInMissionDecision {
  eventId: string;
  optionIndex: number | null;
  riskDelta?: number;
  rewardMod?: number;
  outcome?: 'CONTINUE' | 'ABORT' | 'BONUS' | 'RISK';
}

interface BreakInMissionAutoResolvedEvent {
  id: string;
  label: string;
  logMessage: string;
}

interface BreakInMissionState {
  id: string;
  contactId: string;
  missionType: string;
  status: 'AVAILABLE' | 'RUNNING' | 'COMPLETED' | 'ABORTED' | 'ARRESTED' | 'EXPIRED';
  startedAt: string | null;
  completedAt: string | null;
  riskLevel: number;
  rewardBlackMoneyCents: string;
  itemsFound: { itemType: string; qty: number }[];
  arrested: boolean;
  durationSeconds: number;
  remainingSeconds: number;
  nextEvent: BreakInMissionEvent | null;
  nextEventAvailableAt: string | null;
  decisions: BreakInMissionDecision[];
  autoResolvedEvents: BreakInMissionAutoResolvedEvent[];
}

interface MissionDropResult {
  dropped: boolean;
  itemType: string | null;
  itemLabel: string | null;
  quantity: number;
  overflowReason: 'NO_DROP' | 'NO_STORAGE' | 'STORAGE_FULL' | null;
}

type OrderNpcType = 'HEHLER' | 'SCHWARZMARKT' | 'SCHROTTHAENDLER';


interface LaundryRunCompact {
  id: string;
  blackMoneyAmount: string;
  noxOutput: string;
  lossPercent: number;
  progressPercent: number;
  startedAt: string;
  completesAt: string;
  status: 'RUNNING' | 'COMPLETED' | 'INTERRUPTED';
}

interface LaundryBusinessCompact {
  id: string;
  name: string;
  status: 'ACTIVE' | 'CREDIT' | 'FROZEN';
  upgradeLevel: number;
  maintenanceDebt: string;
  effective: {
    maxBatchCents: string;
    lossPercent: number;
    durationMinutes: number;
  };
  activeRun: LaundryRunCompact | null;
}

interface CrimeOpsSection {
  unlocked:      boolean;
  requiredTalent: string;
  requirements:  Requirement[];
}

interface PlayerContactInfo {
  contactKey:    string;
  label:         string;
  description:   string;
  acquiredAt:    string;
  trustLevel:    number;
  purchaseCount: number;
}

interface GaertnerSeedItem {
  strainKey:        string;
  label:            string;
  priceNox:         string;  // effektiver Preis (nach Rabatt)
  priceNoxOriginal: string;  // Originalpreis (vor Rabatt)
  minTrust:         number;
  available:        boolean;
}

interface GaertnerShopData {
  trustLevel:           number;
  trustLabel:           string;
  discountPct:          number;
  purchaseCount:        number;
  maxPerOrder:          number;
  weeklyLimit:          number;
  weeklyUsed:           number;
  weeklyRemaining:      number;
  nextWeeklyResetAt:    string;
  cooldownActive:       boolean;
  cooldownRemainingMs:  number;
  cooldownRemainingStr: string | null;
  nextOrderAt:          string | null;
  nextLevel:            { level: number; label: string; maxPerOrder: number; discountPct: number; purchasesLeft: number; totalNeeded: number } | null;
  catalog:              GaertnerSeedItem[];
}

interface AlchemistCatalogItem {
  itemKey:        string;
  label:          string;
  priceBm:        string;
  priceBmOriginal: string;
  minTrust:       number;
  available:      boolean;
}

interface AlchemistShopData {
  trustLevel:           number;
  trustLabel:           string;
  discountPct:          number;
  purchaseCount:        number;
  maxPerOrder:          number;
  weeklyLimit:          number;
  weeklyUsed:           number;
  weeklyRemaining:      number;
  nextWeeklyResetAt:    string;
  cooldownActive:       boolean;
  cooldownRemainingMs:  number;
  cooldownRemainingStr: string | null;
  nextOrderAt:          string | null;
  nextLevel:            { level: number; label: string; maxPerOrder: number; discountPct: number; purchasesLeft: number; totalNeeded: number } | null;
  catalog:              AlchemistCatalogItem[];
}

interface CrimeOpsStatus {
  geldwaesche:   { unlocked: boolean; businessCount: number; businesses: LaundryBusinessCompact[] };
  dealer:        CrimeOpsSection;
  labor:         CrimeOpsSection;
  schmuggel:     CrimeOpsSection;
  kartell:       CrimeOpsSection;
  gewächshaus:   CrimeOpsSection;
  schwarzmarkt:  { unlocked: boolean };
  kontakte:      { contacts: PlayerContactInfo[] };
}

interface CellmateSmuggleJobView {
  id: string;
  itemKey: string;
  itemLabel: string;
  quantity: number;
  riskLevel: number;
  flPenalty: number;
  rewardCents: string;
  isTrustTest: boolean;
  acceptedAt: string | null;
  completesAt: string | null;
  isCompleted: boolean;
  isFailed: boolean;
  expiresAt: string;
}

// ─── Modul-State ──────────────────────────────────────────────────────────────

let crimeOpsData: CrimeOpsStatus | null = null;
let selectedSection: Section = 'geldwaesche';
const expandedCategories = new Set<string>(); // Mobile-Accordion: offene Kategorien
let propsDataForGaertner = '';  // erste aktive Property-ID für Gärtner-Lieferungen
let expandedWashBid: string | null = null;
let refreshTimer: ReturnType<typeof setInterval> | null = null;
let progressTimer: ReturnType<typeof setInterval> | null = null;
let reloadStatusPromise: Promise<void> | null = null;
let isImmobilienTabMounted    = false;
let isViertelTabMounted       = false;
let isGewaechshausTabMounted  = false;
let isRolltischTabMounted     = false;
let isLaborTabMounted         = false;
let isStreetMarketTabMounted  = false;
let isDarknetTabMounted       = false;
let isSchwarzmarktTabMounted  = false;

// Sektion-spezifische Daten (nur geladen wenn freigeschaltet + aktiv)
let laborData:        LaborStatusData | null     = null;
let dealerData:       DealerStatusData | null    = null;
let schmuggelData:    SchmuggelStatusData | null  = null;
let smuggleRunStatus: SmuggleRunStatusData | null = null;
let smuggleRunHistory: SmuggleHistoryEntry[]      = [];
let smuggleRunSelectedItem   = '';
let smuggleRunSelectedVehicle = '';
let smuggleRunSelectedQty    = 1;
let ordersData: OrdersSectionData | null          = null;
let npcMessagesData: NpcMessageView[]             = [];
let missionContactsData: BreakInMissionContact[]  = [];
let missionHistoryData: BreakInMissionState[]     = [];
let missionActiveState: BreakInMissionState | null = null;
let missionModalMissionId: string | null          = null;
let cellmateJobData: CellmateSmuggleJobView | null = null;
let missionActionInFlight = false;
let vehicleList: VehicleSummary[]                 = [];
let selectedVehicleId = '';
let selectedVehicleSlotIndex = 0;
let selectedOrderNpcType: OrderNpcType = 'SCHROTTHAENDLER';
let tradeSellQty = 1;
let selectedVehicleInventory: VehicleInventoryData | null = null;
let orderDeliveryModalOrderId: string | null = null;
let orderDeliveryModalVehicleId = '';
let orderDeliveryModalQty = 1;
const orderVehicleInventoryById = new Map<string, VehicleInventoryData>();
// Auftragsbrett (NT-AUFTRAGSBRETT-01) — komplett eigene Datenquelle neben ordersData (Alt-System,
// Bestandsschutz F3). auftragsbrettFreeProperties wird lazy beim Öffnen des Annahme-Modals geladen.
let auftragsbrettData: AuftragsbrettBoardView | null = null;
let auftragsbrettHistoryData: AuftragHistoryItem[] = [];
let auftragsbrettFreeProperties: FreePropertySlotView[] = [];
let auftragsbrettAcceptModal: AuftragsbrettAcceptModal | null = null;
// TRANSPORT-Annahme-Modal: Dauer-Vorschau (POST /transport/preview), Muster wie
// supplyDurationPreviewText oben — Debounce bei Fahrzeugwechsel, in-place gepatcht.
let auftragsbrettPreviewDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let auftragsbrettPreviewRequestSeq = 0;

// NT-AUFTRAGSBRETT-01B (PR3) — ersetzt den vorherigen, hartcodierten KNASTKONTAKT-Sonderfall.
// Das Board (auftragsbrettData) ist an dieser Stelle bereits geladen und trägt das Profil jedes
// Auftraggebers mit — kein zweiter API-Call, keine frontend-eigene Konstanten-Tabelle nötig.
// Modulweit (nicht nur im Render-Closure), da sowohl handleOpenAuftragsbrettAccept() als auch
// renderAuftragsbrettAcceptModal() sie brauchen.
function getAuftraggeberProfile(typ: AuftraggeberTypView): AuftraggeberProfileView | null {
  return auftragsbrettData?.auftraggeber.find((a) => a.auftraggeberTyp === typ)?.profile ?? null;
}

/** NT-POLISH-01 (P1): Lieferziele nutzen die zentrale Immobilien-Auswahl. */
type ContactDeliveryTarget = PropertySelectItem;

let contactDeliveryTargets: ContactDeliveryTarget[] = [];
let dealerTalentTree: TalentTreeData | null = null;
let dealerVehicleOptions: VehicleSummary[] = [];
let dealerFleeingModalDealerId: string | null = null;
// NT-POLISH-01 (P5): Dealer, für den gerade der Schulungs-Bestätigungsdialog offen ist.
let dealerTrainingConfirmId: string | null = null;
const dealerVehiclePanelState = new Map<string, boolean>();
const dealerInventoryPanelState = new Set<string>();
let dealerSupplyModal: DealerSupplyModal | null = null;
let dealerSupplyProperties: DealerSupplyPropertySource[] = [];

// Dauer-Vorschau (NT-TRANSPORT-BELADUNG-01 Rev. 2) — reiner Anzeige-Text, per Debounce
// nachgeladen (Vehicle-/Item-/Mengenänderung) und in-place gepatcht, kein Full-Re-Render.
interface TransportPreview {
  loadingMinutes:   number;
  travelMinutes:    number;
  unloadingMinutes: number;
  totalMinutes:     number;
  steps:            number; // NT-VIERTEL-02: Dealer haben noch keine Straße — immer DEALER_DEFAULT_STEPS.
}
let supplyDurationPreviewText = '';
let supplyPreviewDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let supplyPreviewRequestSeq = 0;

const DEALER_PURCHASE_PRICE_CENTS = 20_000;

// Scheine-Skip fürs Warten auf Belieferung (Säule 3 QoL-Zeit-Skips,
// nighttrade_monetarisierung_v3.md) — Kosten sind serverseitig in
// DEALER_SUPPLY_SKIP_COST_SCHEINE fixiert, hier nur Anzeige.
const DEALER_SUPPLY_SKIP_COST_SCHEINE_DISPLAY = 20;
const DEALER_TIER_LABEL: Record<DealerTier, string> = {
  TIER_1: 'HANDLANGER',
  TIER_2: 'LAUFBURSCHE',
  TIER_3: 'VERTRAUENSMANN',
};
const DEALER_TIER_BAIL_CENTS: Record<DealerTier, number> = {
  TIER_1: 50_000,
  TIER_2: 150_000,
  TIER_3: 400_000,
};
// NT-VIERTEL-07-FEINSCHLIFF (B3): die frühere, rein aus streetClass abgeleitete CLASS_ABSATZ_LABEL
// (RUHIG→GEDROSSELT) ist entfernt — sie zeigte fälschlich GEDROSSELT auf jeder ruhigen, aber
// unbedrängten Straße. "ABSATZ …" kommt jetzt server-berechnet (DealerResponse.absatzLabel).
// E9 — Anzeige-Duplikat der Backend-Konstanten (dealer.constants.ts).
const VEHICLE_BAIL_COST_DISPLAY = 100_000;
const VEHICLE_SEIZURE_HOURS_DISPLAY = 72;
const DEALER_VEHICLE_EFFECTS: Record<string, { slotBonus: number; suspicionModifier: number }> = {
  fahrrad_oma: { slotBonus: 3, suspicionModifier: 2 },
  ebike: { slotBonus: 6, suspicionModifier: 1 },
  pkw_rostig_polo: { slotBonus: 8, suspicionModifier: 0 },
  pizza_roller: { slotBonus: 10, suspicionModifier: -1 },
  roller_pizza: { slotBonus: 10, suspicionModifier: -1 },
  golf_geliehen: { slotBonus: 12, suspicionModifier: 0 },
  taxe: { slotBonus: 15, suspicionModifier: -2 },
  taxi_ausser_dienst: { slotBonus: 15, suspicionModifier: -2 },
  kombi_tarnung: { slotBonus: 18, suspicionModifier: -3 },
  kombi_getarnt: { slotBonus: 8, suspicionModifier: -3 },
  eiswagen: { slotBonus: 30, suspicionModifier: -4 },
  wurstmobil: { slotBonus: 35, suspicionModifier: 4 },
  dhl_transporter: { slotBonus: 50, suspicionModifier: 6 },
  // NT-FAHRZEUGE-01 — Anzeige-Duplikat von dealer.constants.ts
  klapprad: { slotBonus: 2, suspicionModifier: 2 },
  mofa: { slotBonus: 5, suspicionModifier: 1 },
  bollerwagen: { slotBonus: 4, suspicionModifier: 2 },
  lastenrad: { slotBonus: 7, suspicionModifier: 1 },
  kurierroller: { slotBonus: 8, suspicionModifier: -1 },
  lastendreirad: { slotBonus: 14, suspicionModifier: 2 },
  kleinwagen: { slotBonus: 10, suspicionModifier: 0 },
  geschaeftslimousine: { slotBonus: 20, suspicionModifier: -2 },
  gelaendewagen: { slotBonus: 24, suspicionModifier: 1 },
  luxuslimousine: { slotBonus: 26, suspicionModifier: -3 },
  gepanzerte_limousine: { slotBonus: 30, suspicionModifier: -1 },
  lieferwagen: { slotBonus: 22, suspicionModifier: 2 },
  kleintransporter: { slotBonus: 28, suspicionModifier: 3 },
  kuehltransporter: { slotBonus: 42, suspicionModifier: 4 },
  moebeltransporter: { slotBonus: 55, suspicionModifier: 5 },
  gepanzerter_transporter: { slotBonus: 60, suspicionModifier: 3 },
};
const DEALER_BLOCKED_VEHICLE_KEYS = new Set([
  'polizei_bus', 'gekuehlter_lkw', 'lkw_kuehlung', 'lkw_7_5_tonner', 'schwerlast_lkw', 'sattelzug',
]);

// ─── Styles ───────────────────────────────────────────────────────────────────

function addStyles(): void {
  const existing = document.getElementById('crime-ops-styles');
  const s = existing instanceof HTMLStyleElement ? existing : document.createElement('style');
  s.id = 'crime-ops-styles';
  s.textContent = `
    /* ── Header ── */
    .co-header {
      padding: var(--space-3) var(--space-4) var(--space-2);
      border-bottom: 1px solid color-mix(in srgb, var(--accent-red) 30%, transparent);
    }
    .co-title {
      font-family: var(--font-mono);
      font-size: var(--font-size-base);
      letter-spacing: 0.14em;
      color: var(--accent-red);
      text-transform: uppercase;
      margin: 0 0 var(--space-1) 0;
    }
    @media(min-width: 1024px) {
      .co-title { font-size: var(--font-size-xl); }
    }
    .co-subtitle {
      font-family: var(--font-mono);
      font-size: var(--font-size-xs);
      color: var(--text-muted);
      margin: 0;
      letter-spacing: 0.03em;
      line-height: 1.4;
    }

    /* ── Sektion-Tabs (wie Job-Phasen) ── */
    .co-tabs {
      display: flex;
      gap: var(--space-2);
      flex-wrap: wrap;
      padding: var(--space-3) var(--space-4);
      border-bottom: 1px solid var(--border);
    }
    .co-tab-btn {
      font-family: var(--font-mono);
      font-size: var(--font-size-sm);
      letter-spacing: 0.06em;
      text-transform: uppercase;
      padding: var(--space-2) var(--space-4);
      background: transparent;
      border: 1px solid var(--border);
      border-radius: var(--radius-btn);
      color: var(--text-muted);
      cursor: pointer;
      min-height: 44px;
      transition: color var(--transition-fast), border-color var(--transition-fast);
    }
    .co-tab-btn:hover:not(:disabled) {
      color: var(--accent-red);
      border-color: color-mix(in srgb, var(--accent-red) 50%, transparent);
    }
    .co-tab-btn.is-active {
      color: var(--accent-red);
      border-color: var(--accent-red);
    }
    .co-tab-btn.is-locked {
      opacity: 0.38;
      cursor: pointer;
    }
    .co-tab-btn.is-locked:hover {
      opacity: 0.6;
      border-color: color-mix(in srgb, var(--accent-red) 40%, transparent);
      color: var(--text-muted);
    }

    /* ── Kategorie-Accordion (Mobile <768px) ── */
    @media ${DESKTOP_LAYOUT_QUERY} {
      .co-category        { display: contents; }
      .co-category-header { display: none; }
      .co-category-body   { display: contents; }
    }
    @media ${MOBILE_LAYOUT_QUERY} {
      .co-tabs { flex-direction: column; gap: 0; padding: 0; border-bottom: none; }
      .co-category { border-bottom: 1px solid var(--border); }
      .co-category-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        padding: var(--space-3) var(--space-4);
        background: transparent;
        border: none;
        border-radius: 0;
        font-family: var(--font-mono);
        font-size: var(--font-size-xs);
        letter-spacing: 0.10em;
        text-transform: uppercase;
        color: var(--text-muted);
        cursor: pointer;
        min-height: 44px;
      }
      .co-category--open .co-category-header { color: var(--accent-red); }
      .co-category-toggle { font-family: var(--font-mono); font-size: var(--font-size-xs); color: inherit; }
      .co-category-body {
        display: none;
        flex-direction: column;
        gap: var(--space-2);
        padding: 0 var(--space-4) var(--space-3);
      }
      .co-category--open .co-category-body { display: flex; }
      .co-tab-btn--standalone {
        margin: var(--space-2) var(--space-4);
        width: calc(100% - 2 * var(--space-4));
      }
    }

    /* ── Content ── */
    .co-content-area { padding: var(--space-4); }

    /* ── Business-Karte ── */
    .co-business {
      border: 1px solid var(--border);
      border-radius: var(--radius);
      padding: var(--space-4);
      margin-bottom: var(--space-3);
      background: var(--bg-surface);
    }
    .co-business-header {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      border-bottom: 1px solid var(--border);
      padding-bottom: var(--space-2);
      margin-bottom: var(--space-3);
      flex-wrap: wrap;
      gap: var(--space-2);
    }
    .co-business-name {
      font-family: var(--font-mono);
      font-size: var(--font-size-sm);
      color: var(--text-primary);
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }
    .co-status-badge {
      font-family: var(--font-mono);
      font-size: var(--font-size-xs);
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }
    .co-status-badge.aktiv      { color: var(--accent-green);  }
    .co-status-badge.kredit     { color: var(--accent-orange); }
    .co-status-badge.eingefroren { color: var(--accent-red);   }
    .co-progress-track {
      background: var(--bg-surface);
      border: 1px solid var(--border);
      border-radius: var(--radius-sm);
      height: 18px;
      position: relative;
      overflow: hidden;
      margin-bottom: 4px;
    }
    .co-progress-fill {
      position: absolute; left: 0; top: 0; height: 100%;
      background: var(--accent-green);
      border-radius: var(--radius-sm);
      width: var(--co-progress, 0%);
      transition: width 0.5s linear;
    }
    .co-progress-label {
      font-family: var(--font-mono);
      font-size: var(--font-size-xs);
      color: var(--text-primary);
      margin-bottom: 4px;
      white-space: nowrap;
    }
    .co-run-meta {
      display: flex;
      justify-content: space-between;
      font-family: var(--font-mono);
      font-size: var(--font-size-xs);
      color: var(--text-muted);
      margin-bottom: var(--space-2);
    }
    .co-debt-row {
      font-family: var(--font-mono);
      font-size: var(--font-size-xs);
      color: var(--accent-orange);
      margin-bottom: var(--space-2);
    }
    .co-actions {
      display: flex;
      gap: var(--space-2);
      flex-wrap: wrap;
      margin-top: var(--space-2);
    }
    .co-btn {
      font-family: var(--font-mono);
      font-size: var(--font-size-xs);
      letter-spacing: 0.06em;
      text-transform: uppercase;
      padding: var(--space-2) var(--space-3);
      background: transparent;
      border-radius: var(--radius-btn);
      cursor: pointer;
      min-height: 44px;
      border: 1px solid var(--border);
      color: var(--text-muted);
    }
    .co-btn-primary { border-color: var(--accent-green);  color: var(--accent-green);  }
    .co-btn-warn    { border-color: var(--accent-orange); color: var(--accent-orange); }
    .co-btn-info    { border-color: var(--accent-blue);   color: var(--accent-blue);   }
    .co-btn-accent  { border-color: var(--accent-gold);   color: var(--accent-gold);   }
    .co-btn-danger  { border-color: var(--accent-red);    color: var(--accent-red);    }
    .co-btn-link    {
      border-color: var(--border); color: var(--text-muted);
      text-decoration: none; display: inline-flex; align-items: center;
    }
    .co-btn:disabled  { opacity: 0.38; cursor: not-allowed; }
    .co-btn.is-active { border-color: var(--accent-green); color: var(--accent-green); }

    /* ── Gesperrte Sektion ── */
    .co-locked-box {
      border: 1px solid var(--border);
      border-radius: var(--radius);
      padding: var(--space-4);
      text-align: center;
    }
    .co-locked-icon {
      display: block;
      margin: 0 auto var(--space-2);
      color: var(--text-muted);
    }
    .co-locked-text {
      font-family: var(--font-mono);
      font-size: var(--font-size-sm);
      color: var(--text-muted);
      letter-spacing: 0.05em;
      margin: 0 0 var(--space-2) 0;
    }
    .co-locked-hint {
      font-family: var(--font-mono);
      font-size: var(--font-size-xs);
      color: var(--text-muted);
      opacity: 0.7;
      margin: 0;
      line-height: 1.4;
    }
    .co-empty {
      font-family: var(--font-mono);
      font-size: var(--font-size-xs);
      color: var(--text-muted);
      padding: var(--space-4) 0;
      line-height: 1.5;
    }
    @media ${MOBILE_LAYOUT_QUERY} {
      .co-btn { width: 100%; }
      .co-actions { flex-direction: column; }
    }

    /* ── Gemeinsame Sektion-Label ── */
    .co-section-label {
      font-family: var(--font-mono); font-size: var(--font-size-xs); letter-spacing: 0.10em;
      color: var(--text-muted); text-transform: uppercase;
      margin: var(--space-3) 0 var(--space-2) 0;
    }
    .co-link { color: var(--accent-gold); text-decoration: none; }

    /* ── Labor ── */
    .co-labs-row { display: flex; flex-wrap: wrap; gap: var(--space-2); margin-bottom: var(--space-3); }
    .co-lab-info {
      display: flex; align-items: center; gap: var(--space-2); flex-wrap: wrap;
      font-family: var(--font-mono); font-size: var(--font-size-xs);
      background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius); padding: var(--space-2) var(--space-3);
    }
    .co-lab-name { color: var(--text-primary); }
    .co-lab-tier { color: var(--accent-gold); }
    .co-lab-batches { color: var(--text-muted); }
    .co-batch {
      border: 1px solid var(--border); border-radius: var(--radius); padding: var(--space-2) var(--space-3);
      margin-bottom: var(--space-2); background: var(--bg-elevated);
    }
    .co-batch--done   { border-color: color-mix(in srgb, var(--accent-green)  30%, transparent); }
    .co-batch--failed { border-color: color-mix(in srgb, var(--accent-red)    30%, transparent); opacity: 0.7; }
    .co-batch-header  { display: flex; justify-content: space-between; align-items: baseline; flex-wrap: wrap; gap: var(--space-2); margin-bottom: 6px; }
    .co-batch-name    { font-family: var(--font-mono); font-size: var(--font-size-sm); color: var(--text-primary); }
    .co-badge-done    { font-family: var(--font-mono); font-size: var(--font-size-xs); color: var(--accent-green); }
    .co-qc-banner {
      display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: var(--space-2);
      margin-top: var(--space-2); padding: var(--space-1) var(--space-2);
      background: color-mix(in srgb, var(--accent-gold) 8%, var(--bg-surface));
      border: 1px solid color-mix(in srgb, var(--accent-gold) 30%, transparent); border-radius: 2px;
      font-family: var(--font-mono); font-size: 10px; color: var(--accent-gold);
    }
    .co-recipe {
      border: 1px solid var(--border); border-radius: var(--radius); padding: var(--space-2) var(--space-3);
      margin-bottom: var(--space-2); background: var(--bg-elevated);
    }
    .co-recipe--locked { opacity: 0.5; }
    .co-recipe-header  { display: flex; align-items: center; gap: var(--space-2); flex-wrap: wrap; margin-bottom: 4px; }
    .co-recipe-name    { font-family: var(--font-mono); font-size: var(--font-size-sm); color: var(--text-primary); flex: 1; }
    .co-recipe-time    { font-family: var(--font-mono); font-size: var(--font-size-xs); color: var(--text-muted); }
    .co-recipe-value   { font-family: var(--font-mono); font-size: var(--font-size-xs); color: var(--accent-gold); }
    .co-recipe-lore    { font-family: var(--font-mono); font-size: var(--font-size-xs); color: var(--text-italic); font-style: italic; margin: 0 0 var(--space-2) 0; line-height: 1.4; }
    .co-recipe-ingredients { display: flex; flex-wrap: wrap; gap: 4px; margin-bottom: var(--space-2); }
    .co-ing { font-family: var(--font-mono); font-size: 11px; color: var(--text-muted); background: var(--bg-surface); border: 1px solid var(--border); border-radius: 2px; padding: 2px 6px; }
    .co-btn-sm { padding: var(--space-1) var(--space-2); min-height: 44px; font-size: var(--font-size-xs); }

    /* ── Auftraggeber ── */
    .co-order-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 12px;
      margin-bottom: var(--space-3);
    }
    .co-order-card {
      border: 1px solid var(--border);
      border-left: 3px solid var(--accent-blue);
      border-radius: var(--radius-btn);
      background: var(--bg-surface);
      padding: var(--space-3);
      min-height: 250px;
      display: flex;
      flex-direction: column;
      gap: var(--space-2);
    }
    .co-order-card--schrotthaendler { border-left-color: var(--accent-blue); }
    .co-order-card--hehler { border-left-color: var(--accent-orange); }
    .co-order-card--schwarzmarkt { border-left-color: var(--accent-red); }
    .co-order-card--knastkontakt { border-left-color: var(--accent-gold); }
    .co-order-card--cellmate { border-left-color: var(--accent-purple); }
    .co-business--cellmate { border-left: 2px solid var(--accent-purple); padding-left: var(--space-3); }
    .co-order-note--ok { color: var(--accent-green); }
    .co-order-note--muted { color: var(--text-muted); }
    .co-order-batch {
      font-family: var(--font-mono);
      font-size: var(--font-size-xs);
      color: var(--text-muted);
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }
    .co-order-name {
      font-family: var(--font-mono);
      font-size: var(--font-size-sm);
      color: var(--text-primary);
      letter-spacing: 0.04em;
      margin: 0;
      text-transform: uppercase;
    }
    .co-order-divider {
      border-top: 1px solid var(--border);
      margin: 2px 0;
    }
    .co-order-row {
      display: flex;
      justify-content: space-between;
      gap: var(--space-2);
      font-family: var(--font-mono);
      font-size: var(--font-size-xs);
      color: var(--text-muted);
      letter-spacing: 0.04em;
      flex-wrap: wrap;
    }
    .co-order-track {
      height: 10px;
      border: 1px solid var(--border);
      border-radius: var(--radius-sm);
      overflow: hidden;
      background: var(--bg-base);
    }
    .co-order-fill {
      height: 100%;
      width: var(--co-progress, 0%);
      background: var(--accent-green);
    }
    .co-order-fill--hunger-low { background: var(--accent-orange); }
    .co-order-fill--trust { background: var(--accent-blue); }
    .co-order-label {
      font-family: var(--font-mono);
      font-size: var(--font-size-xs);
      color: var(--text-muted);
      letter-spacing: 0.08em;
      text-transform: uppercase;
      margin: 0;
    }
    .co-order-value {
      font-family: var(--font-mono);
      font-size: var(--font-size-sm);
      color: var(--text-primary);
      margin: 0;
      text-transform: uppercase;
    }
    .co-order-note {
      font-family: var(--font-mono);
      font-size: var(--font-size-xs);
      color: var(--text-muted);
      margin: 0;
      line-height: 1.4;
    }
    .co-order-note--warn { color: var(--accent-orange); }
    .co-order-note--danger { color: var(--accent-red); }
    .co-order-actions {
      margin-top: auto;
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: var(--space-2);
    }
    .co-order-lock {
      color: var(--text-muted);
      font-family: var(--font-mono);
      font-size: var(--font-size-xs);
      letter-spacing: 0.06em;
      text-transform: uppercase;
    }
    .co-order-active {
      border: 1px solid var(--border);
      border-radius: var(--radius-btn);
      padding: var(--space-3);
      margin-bottom: var(--space-2);
      background: var(--bg-elevated);
    }
    .co-order-progress {
      margin: var(--space-2) 0;
    }
    .co-order-history-item {
      display: flex;
      justify-content: space-between;
      gap: var(--space-2);
      border-bottom: 1px dashed var(--border);
      padding: var(--space-2) 0;
      font-family: var(--font-mono);
      font-size: var(--font-size-xs);
      color: var(--text-primary);
      text-transform: uppercase;
    }
    .co-order-history-item--muted { color: var(--text-muted); }
    .co-order-hint {
      font-family: var(--font-mono);
      font-size: var(--font-size-xs);
      color: var(--text-muted);
      margin-top: 4px;
      text-transform: uppercase;
    }
    @media ${MOBILE_LAYOUT_QUERY} {
      .co-order-grid { grid-template-columns: 1fr; }
      .co-order-actions { grid-template-columns: 1fr; }
    }

    /* ── Freischalt-Checkliste (Aufgabe 6) ── */
    .co-req-list {
      text-align: left; margin: var(--space-3) 0 var(--space-2) 0;
      width: 100%; max-width: 400px;
    }
    .co-req-row {
      display: flex; align-items: flex-start; gap: var(--space-2);
      margin-bottom: var(--space-2); padding-bottom: var(--space-2);
      border-bottom: 1px solid var(--border);
    }
    .co-req-row:last-child { border-bottom: none; margin-bottom: 0; }
    .co-req-icon {
      font-family: var(--font-mono); font-size: var(--font-size-sm);
      flex-shrink: 0; width: 20px; text-align: center; line-height: 1.3;
    }
    .co-req-icon--ok  { color: var(--accent-green); }
    .co-req-icon--no  { color: var(--accent-red); }
    .co-req-info { flex: 1; min-width: 0; }
    .co-req-label {
      font-family: var(--font-mono); font-size: var(--font-size-xs);
      color: var(--text-muted); display: block; line-height: 1.4;
    }
    .co-req-label--met { color: var(--text-primary); }
    .co-req-label--unmet { color: var(--accent-red); }
    .co-req-hint {
      font-family: var(--font-mono); font-size: 11px;
      color: var(--text-muted); display: block; margin-top: 2px; font-style: italic;
    }
    .co-req-action {
      font-family: var(--font-mono); font-size: 11px;
      color: var(--accent-gold); display: inline-block; margin-top: 4px;
      text-decoration: none; letter-spacing: 0.04em;
    }
    .co-req-action:hover { text-decoration: underline; }
    .co-req-goto-talent {
      font-family: var(--font-mono); font-size: var(--font-size-xs);
      color: var(--accent-gold); display: inline-block;
      margin-top: var(--space-2); text-decoration: none;
      letter-spacing: 0.06em; text-transform: uppercase;
      border: 1px solid color-mix(in srgb, var(--accent-gold) 40%, transparent);
      padding: var(--space-1) var(--space-3); border-radius: var(--radius-btn);
    }
    .co-req-goto-talent:hover { background: color-mix(in srgb, var(--accent-gold) 8%, transparent); }

    /* ── Dealer ── */
    .co-dealer-card        { border: 1px solid var(--border); border-radius: var(--radius); padding: var(--space-3); margin-bottom: var(--space-2); background: var(--bg-elevated); }
    .co-dealer-card--inactive { opacity: 0.55; }
    .co-dealer-head { display: flex; justify-content: space-between; gap: var(--space-3); align-items: flex-start; margin-bottom: var(--space-2); }
    .co-dealer-tier { font-family: var(--font-mono); font-size: var(--font-size-xs); color: var(--text-muted); letter-spacing: 0.06em; text-transform: uppercase; margin-top: 2px; }
    .co-dealer-row { display: flex; justify-content: space-between; gap: var(--space-3); font-family: var(--font-mono); font-size: var(--font-size-xs); color: var(--text-muted); margin-top: var(--space-1); flex-wrap: wrap; }
    .co-dealer-row strong { color: var(--text-primary); font-weight: 400; }
    .co-dealer-actions { flex-wrap: wrap; gap: var(--space-2); margin-top: var(--space-3); }
    .co-dealer-inventory-panel { border: 1px solid var(--border); border-radius: var(--radius); padding: var(--space-2) var(--space-3); margin-top: var(--space-2); background: var(--bg-surface); }
    .co-dealer-inv-line { display: flex; justify-content: space-between; gap: var(--space-2); align-items: center; font-family: var(--font-mono); font-size: var(--font-size-xs); padding: 2px 0; }
    .co-dealer-inv-qty { color: var(--text-muted); white-space: nowrap; }
    .co-dealer-inv-name { text-align: right; }
    .co-progress-track { width: 100%; height: 6px; border: 1px solid var(--border); border-radius: var(--radius-sm); background: var(--bg-base); margin-top: var(--space-1); overflow: hidden; }
    .co-progress-fill { display: block; height: 100%; width: var(--co-progress, 0%); background: var(--accent-green); }
    /* Phasen-Balken (NT-TRANSPORT-BELADUNG-01): Verladung/Fahrt/Abladung als 3 proportionale
       Segmente, Text-Label immer sichtbar (Barrierefreiheit, nicht nur Farbe). */
    .co-phase-wrap { display: flex; flex-direction: column; gap: 2px; margin-top: var(--space-1); }
    .co-phase-label { font-family: var(--font-mono); font-size: var(--font-size-xs); letter-spacing: 0.08em; text-transform: uppercase; }
    .co-phase-bar { display: flex; gap: 2px; height: 6px; }
    .co-phase-seg { flex-grow: 1; flex-shrink: 1; flex-basis: 0; min-width: 8px; height: 100%; background: var(--bg-base); border: 1px solid var(--border); border-radius: var(--radius-sm); overflow: hidden; }
    .co-phase-seg-fill { height: 100%; border-radius: var(--radius-sm); transition: width 0.3s; }
    .co-status-badge--warn { color: var(--accent-orange); border-color: color-mix(in srgb, var(--accent-orange) 40%, transparent); }
    .co-status-badge--info { color: var(--accent-blue); border-color: color-mix(in srgb, var(--accent-blue) 40%, transparent); }
    .co-status-badge--danger { color: var(--accent-red); border-color: color-mix(in srgb, var(--accent-red) 40%, transparent); }
    .co-status-badge--muted { color: var(--text-muted); border-color: var(--border); }
    .co-dealer-vehicle-panel { border-top: 1px solid var(--border); margin-top: var(--space-3); padding-top: var(--space-3); }
    .co-dealer-vehicle-panel.is-empty { opacity: 0.8; }
    .co-dealer-vehicle-panel-head { display: flex; justify-content: space-between; gap: var(--space-2); align-items: center; font-family: var(--font-mono); font-size: var(--font-size-xs); color: var(--text-muted); margin-bottom: var(--space-2); }
    .co-dealer-vehicle-list { display: grid; gap: var(--space-2); }
    .co-dealer-vehicle-row { width: 100%; text-align: left; border: 1px solid var(--border); border-radius: var(--radius-btn); padding: var(--space-2); background: var(--bg-base); color: var(--text-primary); display: grid; gap: 4px; }
    .co-dealer-vehicle-row.is-selected { border-color: var(--accent-gold); }
    .co-dealer-vehicle-row.is-disabled { opacity: 0.5; }
    .co-dealer-vehicle-name { font-family: var(--font-mono); font-size: var(--font-size-xs); text-transform: uppercase; letter-spacing: 0.04em; }
    .co-dealer-vehicle-meta, .co-dealer-vehicle-hint { font-family: var(--font-mono); font-size: 11px; color: var(--text-muted); }
    /* ── Modal-Content (Shell = .modal-backdrop/.modal aus dashboard.css,
       siehe components/BottomSheet.ts) ── */
    .co-modal-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--space-3); margin-top: var(--space-3); }
    .co-modal-panel { border: 1px solid var(--border); border-radius: var(--radius); padding: var(--space-3); background: var(--bg-base); }
    .co-modal-row {
      display: flex;
      justify-content: space-between;
      gap: var(--space-3);
      margin-bottom: var(--space-2);
      font-family: var(--font-mono);
      font-size: var(--font-size-xs);
      color: var(--text-muted);
      flex-wrap: wrap;
    }
    .co-modal-row strong { color: var(--text-primary); font-weight: 400; }
    .co-modal-qty {
      display: grid;
      grid-template-columns: 36px 1fr 36px;
      gap: var(--space-2);
      align-items: center;
      margin-bottom: var(--space-3);
    }
    .co-modal-qty input {
      width: 100%;
      min-height: 36px;
      border: 1px solid var(--border);
      border-radius: var(--radius-sm);
      background: var(--bg-base);
      color: var(--text-primary);
      font-family: var(--font-mono);
      font-size: var(--font-size-sm);
      padding: 0 var(--space-2);
      text-align: center;
    }

    /* ── Dealer Supply Modal (Content) ── */
    .co-supply-section { margin-bottom: var(--space-3); }
    .co-supply-label { font-family: var(--font-mono); font-size: var(--font-size-xs); color: var(--text-italic); text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: var(--space-2); display: block; }
    .co-supply-select { width: 100%; font-family: var(--font-mono); font-size: var(--font-size-xs); background: var(--bg-base); color: var(--text-primary); border: 1px solid var(--border); border-radius: var(--radius); padding: 6px 8px; }
    .co-supply-items { display: grid; grid-template-columns: 14px 1fr auto; column-gap: 8px; row-gap: 4px; align-items: center; overflow-x: hidden; padding-right: 4px; font-family: var(--font-mono); font-size: var(--font-size-xs); }
    .co-supply-item { display: contents; }
    /* Checkbox: alle Box-Model-Werte explizit zurückgesetzt (nicht nur width/height/margin) —
       die globale input/textarea/select-Basisregel (base.css) setzt padding/border/background
       auf JEDEN input, auch type=checkbox. Manche Mobile-Browser respektieren das bei nativen
       Checkboxen (anders als Desktop-Chrome), wodurch die Box breiter als 14px wird und dadurch
       nicht mehr linksbündig mit dem Grid-Slot sitzt (NT-UI-DEALER-LIEFERN-01, #8b). */
    .co-supply-item input[type="checkbox"] { accent-color: var(--accent-purple); width: 14px; height: 14px; margin: 0; padding: 0; border: none; background: transparent; appearance: checkbox; -webkit-appearance: checkbox; cursor: pointer; }
    .co-supply-item-name { color: var(--text-primary); text-transform: uppercase; white-space: normal; overflow-wrap: break-word; min-width: 0; line-height: 1.4; }
    .co-supply-q-badge { font-size: 10px; letter-spacing: 0.05em; margin-left: 4px; text-transform: uppercase; }
    .co-supply-item-avail { color: var(--text-italic); font-size: 11px; white-space: nowrap; }
    .co-supply-qty-ctrl { display: flex; align-items: center; gap: 4px; }
    .co-supply-qty-btn { height: 44px; min-width: 44px; padding: 0 6px; font-size: var(--font-size-xs); font-family: var(--font-mono); background: var(--bg-base); border: 1px solid var(--border); border-radius: var(--radius-sm); cursor: pointer; display: flex; align-items: center; justify-content: center; }
    .co-supply-qty-btn:disabled { opacity: 0.35; cursor: default; }
    .co-supply-qty-btn--dec { color: var(--accent-red); border-color: var(--accent-red); }
    .co-supply-qty-btn--inc { color: var(--accent-green); border-color: var(--accent-green); }
    .co-supply-qty-btn--max { color: var(--accent-gold); border-color: var(--accent-gold); font-size: 10px; letter-spacing: 0.04em; }
    .co-supply-qty-val { min-width: 26px; text-align: center; font-family: var(--font-mono); font-size: var(--font-size-xs); color: var(--text-primary); }
    .co-supply-qty-input { width: 40px; height: 32px; text-align: center; font-family: var(--font-mono); font-size: var(--font-size-xs); color: var(--text-primary); background: var(--bg-base); border: 1px solid var(--border); border-radius: var(--radius-sm); padding: 0 4px; line-height: 1; -moz-appearance: textfield; }
    .co-supply-qty-input:disabled { opacity: 0.35; }
    .co-supply-qty-input::-webkit-inner-spin-button, .co-supply-qty-input::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
    .co-supply-empty { font-family: var(--font-mono); font-size: var(--font-size-xs); color: var(--text-italic); padding: var(--space-2) 0; grid-column: 1 / -1; }
    .co-supply-warn { font-family: var(--font-mono); font-size: var(--font-size-xs); color: var(--accent-red); margin-top: var(--space-2); }
    .co-supply-hint { font-family: var(--font-mono); font-size: var(--font-size-xs); color: var(--text-muted); margin-top: var(--space-2); }
    .co-supply-slots-info { font-family: var(--font-mono); font-size: 11px; color: var(--text-italic); margin-top: var(--space-1); }
    .co-supply-vehicle-unavail { font-size: 11px; color: var(--text-italic); margin-left: 4px; }
    @media ${MOBILE_LAYOUT_QUERY} {
      .co-supply-items { display: flex; flex-direction: column; gap: 4px; }
      .co-supply-item { display: grid; grid-template-columns: 14px 1fr; grid-template-areas: 'check name' '. qty'; column-gap: 8px; row-gap: 4px; align-items: start; }
      .co-supply-item input[type="checkbox"] { grid-area: check; margin-top: 2px; }
      .co-supply-item-name { grid-area: name; }
      .co-supply-qty-ctrl { grid-area: qty; width: 100%; }
    }
  `;
  if (!(existing instanceof HTMLStyleElement)) {
    document.head.appendChild(s);
  }
}

// ─── Helfer ───────────────────────────────────────────────────────────────────

function fmtSeur(cents: string): string {
  return (parseInt(cents, 10) / 100).toLocaleString('de-DE', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }) + ' S-₦';
}

function fmtEur(cents: string): string {
  return (parseInt(cents, 10) / 100).toLocaleString('de-DE', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }) + ' ₦';
}

function fmtDurationMins(minutes: number): string {
  if (minutes < 60) return `${String(minutes)} Min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${String(h)}h ${String(m)}m` : `${String(h)}h`;
}

function timeLeft(completesAt: string): string {
  const ms = new Date(completesAt).getTime() - Date.now();
  if (ms <= 0) return 'FERTIG';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${String(h)}h ${String(m)}m`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function esc(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function getMissionDismissStorageKey(contactId: string): string {
  return `mission_contact_dismissed_${contactId}`;
}

function setMissionDismissed(contactId: string, availableUntil: string): void {
  const until = new Date(availableUntil).getTime();
  localStorage.setItem(getMissionDismissStorageKey(contactId), String(until));
}

function isMissionDismissed(contactId: string): boolean {
  const raw = localStorage.getItem(getMissionDismissStorageKey(contactId));
  if (!raw) return false;
  const until = Number.parseInt(raw, 10);
  if (!Number.isFinite(until) || until <= Date.now()) {
    localStorage.removeItem(getMissionDismissStorageKey(contactId));
    return false;
  }
  return true;
}

function getMissionRiskLabel(riskBase: number): 'NIEDRIG' | 'MITTEL' | 'HOCH' | 'SEHR HOCH' {
  if (riskBase < 0.25) return 'NIEDRIG';
  if (riskBase < 0.45) return 'MITTEL';
  if (riskBase < 0.7) return 'HOCH';
  return 'SEHR HOCH';
}

function getOrderVehicleStock(inventory: VehicleInventoryData, order: { itemType: string; inventoryKey: string | null }): number {
  const key = order.inventoryKey ?? order.itemType;
  return inventory.slots.reduce((sum, slot) => {
    if (slot.itemKey !== key) return sum;
    return sum + (slot.quantity ?? 0);
  }, 0);
}

function getOrderById(orderId: string): NpcOrderItem | null {
  return (ordersData?.active ?? []).find((order) => order.id === orderId) ?? null;
}

async function ensureOrderInventoriesLoaded(): Promise<void> {
  const missing = vehicleList
    .map((vehicle) => vehicle.id)
    .filter((vehicleId) => !orderVehicleInventoryById.has(vehicleId));

  if (missing.length === 0) return;

  const inventories = await Promise.all(
    missing.map((vehicleId) => api.get<VehicleInventoryData>(`/vehicle/${encodeURIComponent(vehicleId)}/inventory`)),
  );

  inventories.forEach((inventory) => {
    orderVehicleInventoryById.set(inventory.vehicleId, inventory);
  });
}

function closeOrderDeliveryModal(): void {
  orderDeliveryModalOrderId = null;
  orderDeliveryModalVehicleId = '';
  orderDeliveryModalQty = 1;
  renderCurrentSection();
}

function renderOrderDeliveryModalOverlay(): void {
  const root = document.getElementById('co-root');
  if (!root) return;

  root.querySelector('#co-order-delivery-modal')?.remove();

  if (selectedSection !== 'auftraege' || !orderDeliveryModalOrderId) return;

  const order = getOrderById(orderDeliveryModalOrderId);
  if (!order) {
    orderDeliveryModalOrderId = null;
    return;
  }

  const vehicleOptions = vehicleList
    .map((vehicle) => {
      const inventory = orderVehicleInventoryById.get(vehicle.id);
      if (!inventory) return null;
      const stock = getOrderVehicleStock(inventory, order);
      if (stock <= 0) return null;
      return { vehicle, stock };
    })
    .filter((option): option is { vehicle: VehicleSummary; stock: number } => option !== null);

  const warehouseAvail = order.warehouseStock > 0;

  if (vehicleOptions.length === 0 && !warehouseAvail) {
    orderDeliveryModalOrderId = null;
    showToast('Kein passendes Inventar verfügbar (Fahrzeug oder Lager).', 'error');
    return;
  }

  // Lager als zusätzliche Option anbieten wenn Ware vorhanden
  const isWarehouse = orderDeliveryModalVehicleId === 'warehouse'
    || (vehicleOptions.length === 0 && warehouseAvail);

  if (isWarehouse || orderDeliveryModalVehicleId === 'warehouse') {
    orderDeliveryModalVehicleId = 'warehouse';
  } else if (!vehicleOptions.some((o) => o.vehicle.id === orderDeliveryModalVehicleId)) {
    orderDeliveryModalVehicleId = vehicleOptions[0]?.vehicle.id ?? 'warehouse';
  }

  const usingWarehouse = orderDeliveryModalVehicleId === 'warehouse';
  const selectedOption = usingWarehouse
    ? null
    : (vehicleOptions.find((o) => o.vehicle.id === orderDeliveryModalVehicleId) ?? vehicleOptions[0] ?? null);

  const maxQty = usingWarehouse
    ? Math.max(1, Math.min(order.remainingQty, order.warehouseStock))
    : Math.max(1, Math.min(order.remainingQty, selectedOption?.stock ?? 0));

  if (!Number.isFinite(orderDeliveryModalQty) || orderDeliveryModalQty < 1 || orderDeliveryModalQty > maxQty) {
    orderDeliveryModalQty = maxQty;
  }

  const baseSeconds = order.npcType === 'SCHWARZMARKT' ? 600 : order.npcType === 'HEHLER' ? 300 : 120;
  const cooldownSeconds = usingWarehouse
    ? baseSeconds
    : Math.max(30, Math.round(baseSeconds * (selectedOption?.vehicle.speedFactor ?? 1)));
  const rewardThisDelivery = (BigInt(order.rewardPerUnitCents) * BigInt(orderDeliveryModalQty)).toString();

  // Quelle-Select: Fahrzeuge + ggf. Lager-Option
  const sourceOptions = [
    ...vehicleOptions.map((o) =>
      `<option value="${esc(o.vehicle.id)}" ${!usingWarehouse && o.vehicle.id === orderDeliveryModalVehicleId ? 'selected' : ''}>${esc(o.vehicle.label.toUpperCase())} · ${String(o.stock)}x</option>`
    ),
    ...(warehouseAvail ? [`<option value="warehouse" ${usingWarehouse ? 'selected' : ''}>LAGER · ${String(order.warehouseStock)}x</option>`] : []),
  ].join('');

  const stockLabel = usingWarehouse
    ? `IM LAGER`
    : 'IM FAHRZEUG';
  const stockValue = usingWarehouse
    ? String(order.warehouseStock)
    : String(selectedOption?.stock ?? 0);

  const modal = document.createElement('div');
  modal.id = 'co-order-delivery-modal';
  modal.className = 'modal-backdrop';
  modal.innerHTML = `
    <div class="modal" role="dialog" aria-modal="true" aria-label="Lieferung bestätigen">
      <div class="modal-header"><h2>LIEFERUNG</h2></div>
      <div class="modal-body">
        <div class="co-modal-row"><span>AUFTRAG</span><strong>${String(order.totalQty)}x ${esc(order.itemLabel.toUpperCase())} an ${esc(order.npcName.toUpperCase())}</strong></div>
        <p class="co-section-label">QUELLE</p>
        <select class="markt-select" data-order-modal-vehicle>
          ${sourceOptions}
        </select>
        <div class="co-modal-row" style="margin-top:var(--space-2);"><span>${stockLabel}</span><strong>${stockValue} EINHEITEN</strong></div>
        <div class="co-modal-row"><span>MAX LIEFERBAR</span><strong>${String(maxQty)} EINHEITEN</strong></div>
        <p class="co-section-label">MENGE</p>
        <div class="co-modal-qty">
          <button class="co-btn" data-co-action="order-modal-qty-down">-</button>
          <input type="number" min="1" max="${String(maxQty)}" step="1" value="${String(orderDeliveryModalQty)}" data-order-modal-qty>
          <button class="co-btn" data-co-action="order-modal-qty-up">+</button>
        </div>
        <div class="co-modal-row"><span>LIEFERZEIT</span><strong>~ ${fmtDurationMins(Math.max(1, Math.round(cooldownSeconds / 60)))}</strong></div>
        <div class="co-modal-row"><span>DIESE LIEFERUNG</span><strong>${fmtEur(rewardThisDelivery)}</strong></div>
      </div>
      <div class="modal-actions">
        <button class="co-btn" data-co-action="close-order-delivery-modal">ABBRECHEN</button>
        <button class="co-btn co-btn-primary" data-co-action="confirm-order-delivery" data-orderid="${esc(order.id)}">BESTÄTIGEN</button>
      </div>
    </div>
  `;

  root.appendChild(modal);
}

async function openOrderDeliveryModal(orderId: string): Promise<void> {
  const order = getOrderById(orderId);
  if (!order) {
    showToast('Auftrag nicht gefunden.', 'error');
    return;
  }

  try {
    await ensureOrderInventoriesLoaded();
  } catch {
    showToast('Fahrzeug-Inventare konnten nicht geladen werden.', 'error');
    return;
  }

  const eligible = vehicleList
    .map((vehicle) => {
      const inventory = orderVehicleInventoryById.get(vehicle.id);
      if (!inventory) return null;
      const stock = getOrderVehicleStock(inventory, order);
      return stock > 0 ? { vehicleId: vehicle.id, stock } : null;
    })
    .filter((entry): entry is { vehicleId: string; stock: number } => entry !== null);

  const warehouseStock = order.warehouseStock;
  if (eligible.length === 0 && warehouseStock <= 0) {
    showToast(`Kein ${order.itemLabel.toUpperCase()} im Fahrzeug oder Lager.`, 'error');
    return;
  }

  const selected = eligible.find((entry) => entry.vehicleId === selectedVehicleId) ?? eligible[0];
  orderDeliveryModalOrderId = order.id;
  orderDeliveryModalVehicleId = selected?.vehicleId ?? 'warehouse';
  orderDeliveryModalQty = selected
    ? Math.max(1, Math.min(order.remainingQty, selected.stock))
    : Math.max(1, Math.min(order.remainingQty, warehouseStock));
  renderCurrentSection();
}

function renderNpcMessageModalOverlay(): void {
  const root = document.getElementById('co-root');
  if (!root) return;

  root.querySelector('#co-npc-message-modal')?.remove();
  if (document.getElementById('global-npc-message-modal')) return;
  const message = npcMessagesData[0] ?? null;
  if (!message) return;

  const modal = document.createElement('div');
  modal.id = 'co-npc-message-modal';
  modal.className = 'modal-backdrop';
  modal.innerHTML = `
    <div class="modal" role="dialog" aria-modal="true" aria-label="Neue Nachricht">
      <div class="modal-header"><h2>NEUE NACHRICHT</h2></div>
      <div class="modal-body">
        <div class="co-modal-row"><span>ABSENDER</span><strong>${esc(message.npcName.toUpperCase())}</strong></div>
        <p class="co-order-note" style="margin-top:var(--space-2);">${esc(message.text)}</p>
      </div>
      <div class="modal-actions">
        <button class="co-btn co-btn-primary" data-co-action="npc-message-accept" data-messageid="${esc(message.id)}">ANNEHMEN</button>
        <button class="co-btn" data-co-action="npc-message-ignore" data-messageid="${esc(message.id)}">IGNORIEREN</button>
      </div>
    </div>
  `;

  root.appendChild(modal);
}

async function handleStartMission(contactId: string): Promise<void> {
  if (!contactId || missionActionInFlight) return;
  missionActionInFlight = true;
  try {
    const data = await api.post<{ mission: BreakInMissionState }>(`/missions/${encodeURIComponent(contactId)}/start`, {});
    missionActiveState = data.mission;
    missionModalMissionId = data.mission.id;
    showToast('Mission gestartet.', 'info');
    showAutoResolvedMissionToast(data.mission.autoResolvedEvents);
    await reloadSectionData();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Mission konnte nicht gestartet werden.', 'error');
  } finally {
    missionActionInFlight = false;
    renderCurrentSection();
  }
}

async function handleOpenMission(missionId: string): Promise<void> {
  if (!missionId) return;
  try {
    const data = await api.get<{ mission: BreakInMissionState }>(`/missions/${encodeURIComponent(missionId)}`);
    missionActiveState = data.mission;
    missionModalMissionId = missionId;
    showAutoResolvedMissionToast(data.mission.autoResolvedEvents);
    checkAndShowMissionEventModal();
    renderCurrentSection();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Mission konnte nicht geladen werden.', 'error');
  }
}

async function handleMissionDecision(missionId: string, eventId: string, optionIndex: number): Promise<void> {
  if (!missionId || !eventId || missionActionInFlight) return;
  missionActionInFlight = true;
  try {
    const data = await api.post<{ mission: BreakInMissionState; dropResults: MissionDropResult[] }>(
      `/missions/${encodeURIComponent(missionId)}/decide`,
      { eventId, optionIndex },
    );
    missionActiveState = data.mission;
    missionModalMissionId = data.mission.id;

    const autoResolvedSuffix = data.mission.autoResolvedEvents.length > 0
      ? ` (+${String(data.mission.autoResolvedEvents.length)} automatisch aufgelöst)`
      : '';
    if (data.mission.status === 'COMPLETED') {
      showToast(`Mission abgeschlossen.${autoResolvedSuffix}`, 'info');
    } else if (data.mission.status === 'ABORTED') {
      showToast(`Mission abgebrochen.${autoResolvedSuffix}`, 'info');
    } else if (data.mission.status === 'ARRESTED') {
      showToast(`Mission gescheitert: Verhaftet.${autoResolvedSuffix}`, 'error');
    } else if (data.mission.status === 'EXPIRED') {
      showToast(`Mission ist abgelaufen.${autoResolvedSuffix}`, 'error');
    } else {
      showAutoResolvedMissionToast(data.mission.autoResolvedEvents);
    }

    const droppedOnGround = data.dropResults.filter(
      (drop) => !drop.dropped && (drop.overflowReason === 'NO_STORAGE' || drop.overflowReason === 'STORAGE_FULL'),
    );
    if (droppedOnGround.length > 0) {
      const qty = droppedOnGround.reduce((sum, drop) => sum + Math.max(0, drop.quantity), 0);
      showToast(`LIEGENGELASSEN: ${String(qty)}x Beute konnte nicht eingelagert werden.`, 'error');
    }

    await reloadSectionData();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Entscheidung fehlgeschlagen.', 'error');
  } finally {
    missionActionInFlight = false;
    renderCurrentSection();
  }
}

function handleMissionDismiss(contactId: string, availableUntil: string): void {
  if (!contactId) return;
  setMissionDismissed(contactId, availableUntil);
  showToast('Kontakt abgelehnt.', 'info');
  renderCurrentSection();
}

async function handleCellmateAccept(jobId: string): Promise<void> {
  if (!jobId) return;
  try {
    const res = await api.post<{ data: CellmateSmuggleJobView }>(`/cellmate/jobs/${encodeURIComponent(jobId)}/accept`, {});
    // Doppel-Cast bewusst: der api-Client entpackt eine {data,meta}-Hülle nur, wenn der Backend-
    // Response tatsächlich so geformt ist (hasDataEnvelope() in services/api.ts) - res kann zur
    // Laufzeit bereits das flache Payload sein, auch wenn der generische Typ hier {data:...}
    // behauptet.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion, @typescript-eslint/no-unnecessary-condition -- s.o.
    cellmateJobData = (res as unknown as { data: CellmateSmuggleJobView }).data ?? cellmateJobData;
    showToast('Auftrag angenommen. Lieferung läuft.', 'info');
    renderCurrentSection();
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'Fehler beim Annehmen.';
    showToast(msg, 'error');
  }
}

async function handleCellmateComplete(jobId: string): Promise<void> {
  if (!jobId) return;
  try {
    const res = await api.post<{ data: { job: CellmateSmuggleJobView; rewardCents: string } }>(
      `/cellmate/jobs/${encodeURIComponent(jobId)}/complete`, {},
    );
    // Doppel-Cast bewusst, s.o. (handleCellmateAccept): api-Client entpackt {data,meta} nur bei
    // tatsächlich so geformtem Response.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- s.o.
    const data = (res as unknown as { data: { job: CellmateSmuggleJobView; rewardCents: string } }).data;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- s.o.
    cellmateJobData = data?.job ?? cellmateJobData;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- s.o.
    const reward = data?.rewardCents ? `+${(Number(data.rewardCents) / 100).toFixed(0)} S-₦` : '';
    void refreshBalance();
    showToast(`Lieferung abgeschlossen. ${reward}`, 'info');
    renderCurrentSection();
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'Fehler beim Abschließen.';
    showToast(msg, 'error');
  }
}

async function handleCellmateReject(jobId: string): Promise<void> {
  if (!jobId) return;
  try {
    const res = await api.post<{ data: CellmateSmuggleJobView }>(`/cellmate/jobs/${encodeURIComponent(jobId)}/reject`, {});
    // Doppel-Cast bewusst, s.o. (handleCellmateAccept): api-Client entpackt {data,meta} nur bei
    // tatsächlich so geformtem Response.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion, @typescript-eslint/no-unnecessary-condition -- s.o.
    cellmateJobData = (res as unknown as { data: CellmateSmuggleJobView }).data ?? null;
    showToast('Auftrag abgelehnt.', 'info');
    renderCurrentSection();
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'Fehler beim Ablehnen.';
    showToast(msg, 'error');
  }
}

const LOCK_SVG = `<svg class="co-locked-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>`;

// ─── Render: Sektion-Tabs ─────────────────────────────────────────────────────

function renderTabs(status: CrimeOpsStatus): string {
  const allTabs: { id: Section; label: string; enabled: boolean }[] = [
    { id: 'geldwaesche',    label: 'GELDWÄSCHE',    enabled: true },
    { id: 'auftraege',      label: 'AUFTRAGGEBER',  enabled: true },
    { id: 'strassenhandel', label: 'STRASSENHANDEL', enabled: true },
    { id: 'schwarzmarkt',   label: 'SCHWARZMARKT',  enabled: status.schwarzmarkt.unlocked },
    { id: 'dealer',         label: 'DEALER',        enabled: status.dealer.unlocked },
    { id: 'labor',          label: 'LABOR',         enabled: status.labor.unlocked },
    { id: 'schmuggel',      label: 'SCHMUGGEL',     enabled: status.schmuggel.unlocked },
    { id: 'gewächshaus',    label: 'GEWÄCHSHAUS',   enabled: true },
    { id: 'rolltisch',      label: 'ROLLTISCH',     enabled: true },
    { id: 'immobilien',     label: 'IMMOBILIEN',    enabled: true },
    { id: 'viertel',        label: 'VIERTEL',       enabled: true },
    { id: 'kontakte',       label: 'KONTAKTE',      enabled: true },
    { id: 'darknet',        label: 'DARKNET',       enabled: true },
  ];
  const tabMap = new Map(allTabs.map((t) => [t.id as string, t]));

  function btnHtml(tabId: string): string {
    const t = tabMap.get(tabId);
    if (!t) return '';
    const cls = [
      'co-tab-btn',
      selectedSection === t.id ? 'is-active' : '',
      !t.enabled ? 'is-locked' : '',
    ].filter(Boolean).join(' ');
    return `<button class="${cls}" data-co-section="${t.id}">${t.label}</button>`;
  }

  const categoriesHtml = CRIME_OPS_CATEGORIES.map((cat) => {
    const isOpen = expandedCategories.has(cat.id);
    return `<div class="co-category${isOpen ? ' co-category--open' : ''}" data-category="${cat.id}">
      <button class="co-category-header" data-co-toggle-category="${cat.id}" type="button">
        <span>${cat.label}</span><span class="co-category-toggle">${isOpen ? '[-]' : '[+]'}</span>
      </button>
      <div class="co-category-body">${cat.tabs.map(btnHtml).join('')}</div>
    </div>`;
  }).join('');

  return `<div class="co-tabs">${categoriesHtml}</div>`;
}

// ─── Render: Geldwäsche-Inhalt ────────────────────────────────────────────────

function renderWashPreviewHtml(amount: number, lossPercent: number, maxBatchCents: number, durationMinutes: number): string {
  const loss           = Math.floor((amount * lossPercent) / 100);
  const output         = amount - loss;
  const fillRatio      = maxBatchCents > 0 ? amount / maxBatchCents : 1;
  const scaledDuration = Math.max(1, Math.ceil(durationMinutes * fillRatio));
  return `
    <span style="font-size:11px;color:var(--accent-red);">Verlust: -${fmtSeur(String(loss))}</span>
    <span style="font-size:11px;color:var(--accent-green);">Output: +${fmtEur(String(output))}</span>
    <span style="font-size:11px;color:var(--text-muted);">Dauer: ${fmtDurationMins(scaledDuration)}</span>
  `;
}

function renderInlineWashForm(b: LaundryBusinessCompact): string {
  const playerBm  = parseInt(_gameState.getPlayer()?.blackMoney ?? '0', 10);
  const maxBatch  = parseInt(b.effective.maxBatchCents, 10);
  const usableMax = Math.min(playerBm, maxBatch);
  const step      = 100;
  const initVal   = Math.max(step, Math.floor(usableMax / 2 / step) * step);

  return `
    <div style="border-top:1px solid var(--border);padding-top:var(--space-3);margin-top:var(--space-2);">
      <div style="display:flex;justify-content:space-between;font-size:11px;color:var(--text-muted);margin-bottom:var(--space-2);">
        <span>Verfügbar: <span style="color:var(--accent-bm);">${fmtSeur(String(playerBm))}</span></span>
        <span>Max: ${fmtSeur(String(maxBatch))}</span>
      </div>
      <div style="display:flex;align-items:center;gap:var(--space-2);margin-bottom:4px;">
        <span style="font-size:10px;color:var(--text-muted);">MIN</span>
        <input type="range"
          data-co-wash-slider="${esc(b.id)}"
          min="${String(step)}" max="${String(usableMax)}" step="${String(step)}" value="${String(initVal)}"
          style="flex:1;accent-color:var(--accent-orange);">
        <span style="font-size:10px;color:var(--text-muted);">MAX</span>
      </div>
      <p id="co-wash-amount-${esc(b.id)}" style="text-align:center;font-size:13px;color:var(--accent-bm);margin:0 0 var(--space-2) 0;font-family:var(--font-mono);">${fmtSeur(String(initVal))}</p>
      <div id="co-wash-preview-${esc(b.id)}" style="background:var(--bg-surface);border:1px solid var(--border);padding:var(--space-2);margin-bottom:var(--space-3);display:flex;gap:var(--space-4);flex-wrap:wrap;font-family:var(--font-mono);">
        ${renderWashPreviewHtml(initVal, b.effective.lossPercent, parseInt(b.effective.maxBatchCents, 10), b.effective.durationMinutes)}
      </div>
      <div class="co-actions">
        <button class="co-btn co-btn-primary" data-co-action="submit-wash" data-bid="${esc(b.id)}" data-amount="${String(initVal)}">STARTEN</button>
        <button class="co-btn" data-co-action="cancel-wash" data-bid="${esc(b.id)}">ABBRECHEN</button>
      </div>
    </div>
  `;
}

function renderBusinessCard(b: LaundryBusinessCompact): string {
  const statusClass = b.status === 'ACTIVE' ? 'aktiv' : b.status === 'CREDIT' ? 'kredit' : 'eingefroren';
  const statusLabel = b.status === 'ACTIVE' ? 'AKTIV' : b.status === 'CREDIT' ? 'KREDIT' : 'EINGEFROREN';

  const run        = b.activeRun;
  const hasDebt    = parseInt(b.maintenanceDebt, 10) > 0;
  const isRunning  = run?.status === 'RUNNING';
  const isComplete = run?.status === 'COMPLETED' || (run !== null && run.progressPercent >= 100);

  let runHtml = '';
  if (run && isRunning) {
    const pct = run.progressPercent;
    runHtml = `
      <div class="co-progress-label">${fmtSeur(run.blackMoneyAmount)} &#8594; ${fmtEur(run.noxOutput)} (-${String(run.lossPercent)}%)</div>
      <div class="co-progress-track">
        <div class="co-progress-fill" id="co-prog-${esc(b.id)}" style="--co-progress:${String(pct)}%"></div>
      </div>
      <div class="co-run-meta">
        <span id="co-pct-${esc(b.id)}">${String(pct)}%</span>
        <span id="co-time-${esc(b.id)}">${timeLeft(run.completesAt)}</span>
      </div>
    `;
  }

  const debtHtml = hasDebt
    ? `<div class="co-debt-row">Schulden: ${fmtSeur(b.maintenanceDebt)}</div>`
    : '';

  let bottomHtml = '';
  if (isComplete) {
    bottomHtml = `<div class="co-actions">
      <button class="co-btn co-btn-primary" data-co-action="collect" data-runid="${esc(run.id)}" data-bid="${esc(b.id)}">EINSAMMELN</button>
      ${hasDebt ? `<button class="co-btn co-btn-warn" data-co-action="go-geschaefte">SCHULDEN ZAHLEN</button>` : ''}
      <a class="co-btn co-btn-link" href="/immobilien/geschaefte">ZU DEN GESCHÄFTEN</a>
    </div>`;
  } else if (!run || run.status === 'INTERRUPTED') {
    if (expandedWashBid === b.id) {
      bottomHtml = renderInlineWashForm(b);
    } else {
      bottomHtml = `<div class="co-actions">
        <button class="co-btn co-btn-primary" data-co-action="start-wash" data-bid="${esc(b.id)}" ${b.status === 'FROZEN' ? 'disabled' : ''}>WÄSCHE STARTEN</button>
        ${hasDebt ? `<button class="co-btn co-btn-warn" data-co-action="go-geschaefte">SCHULDEN ZAHLEN</button>` : ''}
        <a class="co-btn co-btn-link" href="/immobilien/geschaefte">ZU DEN GESCHÄFTEN</a>
      </div>`;
    }
  } else {
    bottomHtml = `<div class="co-actions">
      <button class="co-btn" disabled>LÄUFT...</button>
      ${hasDebt ? `<button class="co-btn co-btn-warn" data-co-action="go-geschaefte">SCHULDEN ZAHLEN</button>` : ''}
      <a class="co-btn co-btn-link" href="/immobilien/geschaefte">ZU DEN GESCHÄFTEN</a>
    </div>`;
  }

  return `
    <div class="co-business">
      <div class="co-business-header">
        <span class="co-business-name">${esc(b.name)}</span>
        <span class="co-status-badge ${statusClass}">${statusLabel}</span>
      </div>
      ${runHtml}${debtHtml}${bottomHtml}
    </div>
  `;
}

function renderGeldwaescheContent(gw: CrimeOpsStatus['geldwaesche']): string {
  if (gw.businesses.length === 0) {
    return `
      <p class="co-empty">Noch kein Geldwäsche-Betrieb vorhanden.</p>
      <div class="co-actions">
        <a class="co-btn co-btn-primary" href="/immobilien/geschaefte">BETRIEB KAUFEN</a>
      </div>
    `;
  }
  return `
    <p style="font-family:var(--font-mono);font-size:11px;letter-spacing:0.1em;color:var(--text-muted);text-transform:uppercase;margin:0 0 var(--space-3) 0;">
      MEINE BETRIEBE (${String(gw.businesses.length)}/2)
    </p>
    ${gw.businesses.map(renderBusinessCard).join('')}
  `;
}

// ─── Format-Helfer (global, mehrfach genutzt) ────────────────────────────────

function fmtMins(m: number): string {
  if (m < 60) return `${String(m)} Min`;
  const h = Math.floor(m / 60); const min = m % 60;
  return min > 0 ? `${String(h)}h ${String(min)}m` : `${String(h)}h`;
}
function fmtCountdown(ms: number): string {
  if (ms <= 0) return 'FERTIG';
  const s = Math.floor(ms / 1000);
  if (s >= 3600) return `${String(Math.floor(s / 3600))}h ${String(Math.floor((s % 3600) / 60))}m`;
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

// NT-TRANSPORT-BELADUNG-01: Phasen-Label+Balken (Verladung/Fahrt/Abladung) für die Dealer-
// Belieferung — dieselbe geteilte Mathematik wie lagerhaus.ts::renderTransportPhaseBar(), aber
// eigene co-*-Klassenkonvention. Alt-Lieferungen ohne Phasen-Zeitpunkte fallen auf die bisherige
// durchgehende "LIEFERUNG"-Anzeige (gelb) zurück.
function renderDealerDeliveryPhaseHtml(d: DealerPendingSupplyDelivery, now: number): string {
  const phaseUi = computeTransportPhaseUi(d, now);
  if (!phaseUi) {
    const total   = new Date(d.arrivesAt).getTime() - new Date(d.startedAt).getTime();
    const elapsed = now - new Date(d.startedAt).getTime();
    const pct     = total <= 0 ? 100 : Math.max(0, Math.min(100, Math.round((elapsed / total) * 100)));
    return `
      <div class="co-phase-wrap">
        <span class="co-phase-label" style="color:var(--accent-gold);">LIEFERUNG</span>
        <div class="co-progress-track"><div class="co-progress-fill" data-dealer-delivery-bar="${esc(d.id)}" style="--co-progress:${String(pct)}%;"></div></div>
      </div>`;
  }

  const activeColorVar = phaseUi.segments.find((s) => s.isActive)?.colorVar ?? '--accent-gold';
  const segmentsHtml = phaseUi.segments.map((s) => `
    <div class="co-phase-seg" style="flex-basis:${String(s.widthPct)}%;">
      <div class="co-phase-seg-fill" data-dealer-delivery-seg="${esc(d.id)}:${s.key}" style="width:${String(s.fillPct)}%;background:var(${s.colorVar});"></div>
    </div>`).join('');

  return `
    <div class="co-phase-wrap" data-dealer-delivery-phase-label="${esc(d.id)}">
      <span class="co-phase-label" style="color:var(${activeColorVar});">${phaseUi.activeLabel}</span>
      <div class="co-phase-bar">${segmentsHtml}</div>
    </div>`;
}

// NT-AUFTRAGSBRETT-02 (Abschnitt 5: "bestehender Phasenbalken, kein zweiter Fortschrittsbalken") —
// exakt derselbe Balken wie bei der Dealer-Belieferung, nur mit eigenen data-*-Attributen für den
// separaten In-Place-Tick-Patch (unterschiedliche Datenquelle: auftragsbrettData statt dealerStatus).
function renderAuftragTransportPhaseHtml(phase: AuftragTransportPhaseView, now: number, auftragId: string): string {
  const phaseUi = computeTransportPhaseUi(phase, now);
  if (!phaseUi) {
    const total   = new Date(phase.arrivesAt).getTime() - new Date(phase.startedAt).getTime();
    const elapsed = now - new Date(phase.startedAt).getTime();
    const pct     = total <= 0 ? 100 : Math.max(0, Math.min(100, Math.round((elapsed / total) * 100)));
    return `
      <div class="co-phase-wrap">
        <span class="co-phase-label" style="color:var(--accent-gold);">LIEFERUNG</span>
        <div class="co-progress-track"><div class="co-progress-fill" data-auftrag-transport-bar="${esc(auftragId)}" style="--co-progress:${String(pct)}%;"></div></div>
      </div>`;
  }

  const activeColorVar = phaseUi.segments.find((s) => s.isActive)?.colorVar ?? '--accent-gold';
  const segmentsHtml = phaseUi.segments.map((s) => `
    <div class="co-phase-seg" style="flex-basis:${String(s.widthPct)}%;">
      <div class="co-phase-seg-fill" data-auftrag-transport-seg="${esc(auftragId)}:${s.key}" style="width:${String(s.fillPct)}%;background:var(${s.colorVar});"></div>
    </div>`).join('');

  return `
    <div class="co-phase-wrap" data-auftrag-transport-phase-label="${esc(auftragId)}">
      <span class="co-phase-label" style="color:var(${activeColorVar});">${phaseUi.activeLabel}</span>
      <div class="co-phase-bar">${segmentsHtml}</div>
    </div>`;
}

// NT-AUFTRAGSBRETT-05 — eingebettet in beide Branches von auftragsbrettAktiveHtml (TRANSPORT UND
// BUNKERN, s. Schritt-0-Bericht: Scope-Entscheidung "nur Liege-Phase" für FRUEHER_ABHOLEN/
// BESSERES_ANGEBOT ändert an der Renderstelle selbst nichts — die Phase ist bereits serverseitig
// entschieden, das Frontend zeigt nur, was `a.ereignis` liefert). '' ohne Ereignis.
function renderAuftragEreignisHtml(ereignis: AuftragEreignisView | null, auftragId: string): string {
  if (!ereignis) return '';

  // GERÄUSCHE (und jedes bereits beantwortete Ereignis) — nur noch das Ergebnis anzeigen, keine Buttons.
  if (ereignis.entscheidung !== null || ereignis.typ === 'GERAEUSCHE') {
    return `
      <p class="co-order-note co-order-note--muted">${esc(ereignis.label.toUpperCase())}: ${esc(ereignis.ergebnis ?? '')}</p>
    `;
  }

  // Offene Entscheidung — Countdown statisch bis zum nächsten vollen Reload (Muster: Angebots-/
  // Auftraggeber-Ablauf an derselben Stelle, s. updateProgressInPlace()-Kommentar weiter unten).
  const remainingMs = ereignis.entscheidungBisAt ? Math.max(0, new Date(ereignis.entscheidungBisAt).getTime() - Date.now()) : 0;
  const labels = EREIGNIS_ENTSCHEIDUNG_LABEL[ereignis.typ];
  return `
    <div class="co-order-note co-order-note--danger">
      <p>${esc(ereignis.label.toUpperCase())} — NOCH ${fmtCountdown(remainingMs)}</p>
      <div class="co-actions">
        ${labels ? `
          <button class="co-btn co-btn-primary" data-co-action="auftragsbrett-ereignis-entscheiden" data-auftragid="${esc(auftragId)}" data-entscheidung="AKTIV">${esc(labels.AKTIV)}</button>
          <button class="co-btn" data-co-action="auftragsbrett-ereignis-entscheiden" data-auftragid="${esc(auftragId)}" data-entscheidung="STANDARD">${esc(labels.STANDARD)}</button>
        ` : ''}
      </div>
    </div>
  `;
}

// ─── Render: Dealer-Sektion ───────────────────────────────────────────────────

function getDealerTierLabel(tier: DealerTier): string {
  return DEALER_TIER_LABEL[tier];
}

function getDealerStatusLabel(status: DealerStatus): string {
  return ({
    ACTIVE: 'AKTIV',
    PAUSED: 'PAUSIERT',
    TRAINING: 'IN SCHULUNG',
    ARRESTED: 'VERHAFTET',
    FLEEING: 'ABGEHAUEN',
    GONE: 'ABGEHAUEN',
  })[status];
}

function getDealerStatusClass(status: DealerStatus): string {
  return ({
    ACTIVE: 'aktiv',
    PAUSED: 'co-status-badge--warn',
    TRAINING: 'co-status-badge--info',
    ARRESTED: 'co-status-badge--danger',
    FLEEING: 'co-status-badge--muted',
    GONE: 'co-status-badge--muted',
  })[status];
}

function getDealerLimit(): number {
  const activeNodes = Object.values(dealerTalentTree?.branches ?? {}).flat().filter((node) => node.isActive);
  if (activeNodes.some((node) => node.id === 'menschenfuehrer_der_apparat')) return 12;
  if (activeNodes.some((node) => node.id === 'menschenfuehrer_die_crew')) return 10;
  if (activeNodes.some((node) => node.id === 'menschenfuehrer_zweiter_mann')) return 6;
  if (activeNodes.some((node) => node.id === 'menschenfuehrer_erster_handlanger')) return 3;
  return 0;
}

function isDealerVehicleCompatible(vehicleKey: string): boolean {
  return !DEALER_BLOCKED_VEHICLE_KEYS.has(vehicleKey) && vehicleKey in DEALER_VEHICLE_EFFECTS;
}

function renderDealerVehicleAssignment(dealer: DealerResponse): string {
  if (dealerVehicleOptions.length === 0) {
    return '<p class="co-empty">KEINE FAHRZEUGE VERFÜGBAR.</p>';
  }

  const selectedVehicleId = dealer.vehicleId ?? '';
  const rows = dealerVehicleOptions.map((vehicle) => {
    const compatible = isDealerVehicleCompatible(vehicle.vehicleKey);
    const effects = DEALER_VEHICLE_EFFECTS[vehicle.vehicleKey] ?? { slotBonus: 0, suspicionModifier: 0 };
    const isSelected = selectedVehicleId === vehicle.id;
    const label = `${esc(vehicle.label)}${isSelected ? ' (AKTUELL)' : ''}`;
    const suspicionText = `${effects.suspicionModifier >= 0 ? '+' : ''}${String(effects.suspicionModifier)}`;
    return `
      <button class="co-dealer-vehicle-row${isSelected ? ' is-selected' : ''}${compatible ? '' : ' is-disabled'}" data-co-dealer-vehicle="${esc(dealer.id)}" data-vehicle-id="${esc(vehicle.id)}" ${compatible ? '' : 'disabled'}>
        <span class="co-dealer-vehicle-name">${label}</span>
        <span class="co-dealer-vehicle-meta">+${String(effects.slotBonus)} SLOTS · VERDACHT ${suspicionText} / ZYKLUS</span>
        ${compatible ? '' : '<span class="co-dealer-vehicle-hint">NICHT FÜR DEALER GEEIGNET</span>'}
      </button>`;
  }).join('');

  return `<div class="co-dealer-vehicle-list">${rows}</div>`;
}

function renderDealerFleeingModal(): string {
  if (!dealerData || !dealerFleeingModalDealerId) return '';
  const dealer = dealerData.dealers.find((entry) => entry.id === dealerFleeingModalDealerId && entry.status === 'FLEEING');
  if (!dealer) return '';

  const inventoryReturnText = `${Math.floor(dealer.usedSlots / 2)} INVENTAR ZURÜCK`;
  const earningsReturnText = `${fmtSeur(String(Math.floor(Number.parseInt(dealer.pendingEarnings, 10) / 2)))} EINNAHMEN ZURÜCK`;

  return `
    <div class="modal-backdrop" id="co-dealer-fleeing-modal">
      <div class="modal modal--wide" role="dialog" aria-modal="true" aria-label="Dealer abgehauen">
        <div class="modal-header"><h2>DEALER ${esc(dealer.name.toUpperCase())} IST ABGEHAUEN</h2></div>
        <div class="modal-body">
          <p class="co-order-note">ES WURDE IHM ZU HEISS.</p>
          <div class="co-modal-grid">
            <div class="co-modal-panel">
              <p class="co-section-label">LAUFENLASSEN</p>
              <p class="co-order-note">INVENTAR VERLOREN</p>
              <p class="co-order-note">EINNAHMEN VERLOREN</p>
              <p class="co-order-note">FAHRZEUG: FREIGEGEBEN</p>
              <button class="co-btn co-btn-primary" data-co-action="resolve-fleeing" data-dealerid="${esc(dealer.id)}" data-flee-action="ABANDON">LAUFENLASSEN</button>
            </div>
            <div class="co-modal-panel">
              <p class="co-section-label">RECHENSCHAFT FORDERN</p>
              <p class="co-order-note">${esc(inventoryReturnText)}</p>
              <p class="co-order-note">${esc(earningsReturnText)}</p>
              <p class="co-order-note">FAHRZEUG: FREIGEGEBEN</p>
              <button class="co-btn co-btn-primary" data-co-action="resolve-fleeing" data-dealerid="${esc(dealer.id)}" data-flee-action="COLLECT">RECHENSCHAFT FORDERN</button>
            </div>
          </div>
        </div>
      </div>
    </div>`;
}

function isDealerVehiclePanelOpen(dealer: DealerResponse): boolean {
  // NT-POLISH-01 (P3): standardmäßig zugeklappt — auch bei zugewiesenem Fahrzeug. Nur ein
  // expliziter Klick (oder der BELIEFERN/FAHRZEUG-Sprung aus dem Viertel) klappt auf; der
  // Zustand wird beim Tab-Wechsel verworfen (dealerVehiclePanelState.clear()).
  return dealerVehiclePanelState.get(dealer.id) ?? false;
}

function isDealerInventoryPanelOpen(dealer: DealerResponse): boolean {
  return dealerInventoryPanelState.has(dealer.id);
}

function renderDealerInventoryPanel(dealer: DealerResponse): string {
  if (dealer.inventory.length === 0) {
    return '<p class="co-empty">Kein Bestand.</p>';
  }
  const rows = dealer.inventory
    .filter((item) => item.quantity > 0)
    .map((item) => `
      <div class="co-dealer-inv-line">
        <span class="co-dealer-inv-qty">${String(item.quantity)}&times;</span>
        <span class="co-dealer-inv-name">${esc(getCleanItemName(item.itemId))} ${renderItemBadges(item.itemId, 'co-supply-q-badge')}</span>
      </div>`)
    .join('');
  return rows || '<p class="co-empty">Kein Bestand.</p>';
}

// Geteilte Ableitungslogik für das Supply-Modal (Voll-Render UND chirurgische DOM-Patches nach
// Klicks nutzen exakt dieselbe Formel — verhindert Abweichungen zwischen Erst-Render und Update).
interface SupplyDisplayState {
  availableVehicles: VehicleSummary[];
  vehicleCapacity:   number;
  selectedTotal:     number;
  hasSelection:      boolean;
  overCapacity:      boolean;
  overSlots:         boolean;
  confirmDisabled:   boolean;
  slotsInfo:         string;
}

function computeSupplyDisplayState(sm: DealerSupplyModal): SupplyDisplayState {
  const availableVehicles = dealerVehicleOptions.filter((v) => v.isAvailable);
  const selectedVehicle   = availableVehicles.find((v) => v.vehicleKey === sm.vehicleKey);
  const vehicleCapacity   = selectedVehicle?.capacity ?? 0;
  const selectedTotal     = sm.items.filter((i) => i.selected).reduce((s, i) => s + i.qty, 0);
  const hasSelection      = sm.items.some((i) => i.selected && i.qty > 0);
  const overCapacity      = vehicleCapacity > 0 && selectedTotal > vehicleCapacity;
  const overSlots         = selectedTotal > sm.freeSlots;
  const confirmDisabled   = !hasSelection || availableVehicles.length === 0 || !sm.vehicleKey || overCapacity || overSlots;
  const slotsInfo         = hasSelection
    ? `${String(selectedTotal)} / ${String(sm.freeSlots)} DEALER-SLOTS BELEGT${vehicleCapacity > 0 ? ` · FAHRZEUG: ${String(selectedTotal)}/${String(vehicleCapacity)} KAPAZITÄT` : ''}`
    : `DEALER HAT ${String(sm.freeSlots)} FREIE SLOTS`;
  return { availableVehicles, vehicleCapacity, selectedTotal, hasSelection, overCapacity, overSlots, confirmDisabled, slotsInfo };
}

function computeSupplyItemState(
  sm: DealerSupplyModal, item: DealerSupplyModalItem, selectedTotal: number, vehicleCapacity: number,
): { itemMax: number; atMax: boolean } {
  const otherQty       = selectedTotal - (item.selected ? item.qty : 0);
  const maxFromVehicle = vehicleCapacity > 0 ? vehicleCapacity - otherQty : item.available;
  const maxFromSlots    = sm.freeSlots - otherQty;
  const itemMax         = Math.max(0, Math.min(item.available, maxFromVehicle, maxFromSlots));
  const atMax            = item.selected && item.qty >= itemMax;
  return { itemMax, atMax };
}

function buildSupplyItemsHtml(sm: DealerSupplyModal): string {
  if (sm.items.length === 0) {
    return `<p class="co-supply-empty">Keine lieferbaren Waren im Lager.<br>Produziere Gras, Joints oder Blunts um sie hier zu sehen.</p>`;
  }
  const { selectedTotal, vehicleCapacity } = computeSupplyDisplayState(sm);
  return sm.items.map((item) => {
    const { itemMax, atMax } = computeSupplyItemState(sm, item, selectedTotal, vehicleCapacity);
    return `
        <div class="co-supply-item">
          <input type="checkbox" id="supply-item-${esc(item.itemKey)}"
            data-supply-item-check="${esc(item.itemKey)}"
            ${item.selected ? 'checked' : ''} />
          <label class="co-supply-item-name" for="supply-item-${esc(item.itemKey)}">${parseSupplyItemLabel(item.itemKey, item.itemType)} <span class="co-supply-item-avail">(${String(item.available)})</span></label>
          <div class="co-supply-qty-ctrl">
            <button class="co-supply-qty-btn co-supply-qty-btn--dec" data-supply-qty-dec="${esc(item.itemKey)}" ${!item.selected || item.qty <= 1 ? 'disabled' : ''}>−</button>
            <input type="number" class="co-supply-qty-input" data-supply-qty-input="${esc(item.itemKey)}"
              value="${String(item.qty)}" min="0" max="${String(item.available)}" ${!item.selected ? 'disabled' : ''} />
            <button class="co-supply-qty-btn co-supply-qty-btn--inc" data-supply-qty-inc="${esc(item.itemKey)}" ${!item.selected || item.qty >= item.available || atMax ? 'disabled' : ''}>+</button>
            <button class="co-supply-qty-btn co-supply-qty-btn--max" data-supply-qty-max="${esc(item.itemKey)}" ${itemMax <= 0 ? 'disabled' : ''}>MAX</button>
          </div>
        </div>`;
  }).join('');
}

function renderDealerSupplyModal(): string {
  if (!dealerSupplyModal) return '';
  const sm = dealerSupplyModal;

  // Zählt nur tatsächlich an den Dealer lieferbare Ware (isDealerDrug) — deckt sich damit
  // mit der Liste in Abschnitt 2 (buildSupplyItems() filtert identisch). Vorher wurden hier
  // ALLE Nicht-Fahrzeug-Items gezählt (Material, Ausrüstung, Saatgut etc.), was die Zahl
  // gegenüber der echten Warenliste künstlich aufblähte (NT-UI-DEALER-LIEFERN-01, #8a).
  // NT-POLISH-01 (P1): Name/Gruppierung zentral über renderPropertyOptions().
  const itemCountById = new Map(dealerSupplyProperties.map((p) => [
    p.id,
    p.inventoryItems.filter((i) => i.isDealerDrug && i.quantity > 0).reduce((s, i) => s + i.quantity, 0),
  ]));
  const sourceOptions = renderPropertyOptions(
    dealerSupplyProperties.map((p) => ({ id: p.id, label: p.label, customName: p.customName, nickname: p.nickname, isHideout: p.isHideout })),
    { selectedId: sm.sourcePropertyId, suffix: (p) => `(${String(itemCountById.get(p.id) ?? 0)} ITEMS)` },
  );

  const state = computeSupplyDisplayState(sm);
  const vehicleOptions = state.availableVehicles.length === 0
    ? '<option value="">— KEIN FAHRZEUG VERFÜGBAR —</option>'
    : state.availableVehicles.map((v) => {
        const cap = v.capacity > 0 ? ` · ${String(v.capacity)} SLOTS` : '';
        return `<option value="${esc(v.vehicleKey)}" ${v.vehicleKey === sm.vehicleKey ? 'selected' : ''}>${esc(v.label.toUpperCase())}${cap}</option>`;
      }).join('');

  const itemsHtml = buildSupplyItemsHtml(sm);

  return `
    <div class="modal-backdrop" id="co-dealer-supply-modal">
      <div class="modal" role="dialog" aria-modal="true" aria-label="Ware liefern">
        <div class="modal-header"><h2>WARE LIEFERN — ${esc(sm.dealerName.toUpperCase())}</h2></div>
        <div class="modal-body">
          <div class="co-supply-section">
            <label class="co-supply-label">1. QUELLLAGER</label>
            ${dealerSupplyProperties.length === 0
              ? '<p class="co-supply-empty">KEIN LAGER VORHANDEN.</p>'
              : `<select class="co-supply-select" id="co-supply-source">${sourceOptions}</select>`}
          </div>
          <div class="co-supply-section">
            <label class="co-supply-label">2. WAREN AUSWÄHLEN</label>
            <div class="co-supply-items" id="co-supply-items">${itemsHtml}</div>
            <p class="co-supply-slots-info">${esc(state.slotsInfo)}</p>
          </div>
          <div class="co-supply-section">
            <label class="co-supply-label">3. LIEFERFAHRZEUG</label>
            <select class="co-supply-select" id="co-supply-vehicle" ${state.availableVehicles.length === 0 ? 'disabled' : ''}>${vehicleOptions}</select>
            ${state.availableVehicles.length === 0 ? '<p class="co-supply-empty">Alle Fahrzeuge sind im Dealer-Einsatz oder auf Transport.</p>' : ''}
          </div>
          <p class="co-supply-hint" id="co-supply-duration-preview">${esc(supplyDurationPreviewText || supplyDurationDefaultHint(state.hasSelection))}</p>
          <p class="co-supply-warn" id="co-supply-warn-capacity" ${state.overCapacity ? '' : 'hidden'}>ZU VIELE ITEMS FÜR DIESES FAHRZEUG (MAX. ${String(state.vehicleCapacity)} SLOTS).</p>
          <p class="co-supply-warn" id="co-supply-warn-slots" ${state.overSlots ? '' : 'hidden'}>DEALER HAT NICHT GENUG FREIE SLOTS (NUR ${String(sm.freeSlots)} FREI).</p>
          <p class="co-supply-warn" id="co-supply-warn-error" ${sm.errorMsg ? '' : 'hidden'}>${esc(sm.errorMsg ?? '')}</p>
        </div>
        <div class="modal-actions">
          <button class="co-btn co-btn-primary" data-co-action="confirm-dealer-supply" ${state.confirmDisabled ? 'disabled' : ''}>LIEFERN</button>
          <button class="co-btn" data-co-action="close-dealer-supply">ABBRECHEN</button>
        </div>
      </div>
    </div>`;
}

// ─── Supply-Modal: chirurgische DOM-Updates (kein Voll-Re-Render bei jedem Klick) ─────────────
// Vorher rief jede Interaktion (Checkbox/-/+/MAX/Qty-Eingabe) renderCurrentSection() auf, was das
// komplette Section-innerHTML ersetzte — Flackern + Scroll-/Fokus-Verlust bei jedem Klick
// (NT-UI-LAYOUT-RERENDER-FIX-01, Abschnitt C, H-A). Diese Funktionen aktualisieren stattdessen nur
// die tatsächlich betroffenen DOM-Knoten. Formeln sind 1:1 aus computeSupplyDisplayState()/
// computeSupplyItemState() übernommen — keine neue Berechnungslogik, nur ein neuer Ausführungsort.

function updateSupplyModalDerivedDom(): void {
  if (!dealerSupplyModal) return;
  const sm = dealerSupplyModal;
  const state = computeSupplyDisplayState(sm);

  for (const item of sm.items) {
    const { itemMax, atMax } = computeSupplyItemState(sm, item, state.selectedTotal, state.vehicleCapacity);
    const key = CSS.escape(item.itemKey);
    const decBtn   = document.querySelector<HTMLButtonElement>(`[data-supply-qty-dec="${key}"]`);
    const incBtn   = document.querySelector<HTMLButtonElement>(`[data-supply-qty-inc="${key}"]`);
    const maxBtn   = document.querySelector<HTMLButtonElement>(`[data-supply-qty-max="${key}"]`);
    const qtyInput = document.querySelector<HTMLInputElement>(`[data-supply-qty-input="${key}"]`);
    const checkbox = document.querySelector<HTMLInputElement>(`[data-supply-item-check="${key}"]`);

    if (decBtn) decBtn.disabled = !item.selected || item.qty <= 1;
    if (incBtn) incBtn.disabled = !item.selected || item.qty >= item.available || atMax;
    if (maxBtn) maxBtn.disabled = itemMax <= 0;
    if (qtyInput) {
      qtyInput.disabled = !item.selected;
      // Fokussiertes Feld nie überschreiben — sonst würde eine Direkteingabe mitten im Tippen
      // zurückgesetzt (die Formel greift hier trotzdem schon vorher via Klemmung im Handler).
      if (document.activeElement !== qtyInput) qtyInput.value = String(item.qty);
    }
    if (checkbox) checkbox.checked = item.selected;
  }

  const slotsInfoEl = document.querySelector<HTMLElement>('.co-supply-slots-info');
  if (slotsInfoEl) slotsInfoEl.textContent = state.slotsInfo;

  const confirmBtn = document.querySelector<HTMLButtonElement>('[data-co-action="confirm-dealer-supply"]');
  if (confirmBtn) confirmBtn.disabled = state.confirmDisabled;

  const capWarnEl = document.getElementById('co-supply-warn-capacity');
  if (capWarnEl) {
    capWarnEl.textContent = `ZU VIELE ITEMS FÜR DIESES FAHRZEUG (MAX. ${String(state.vehicleCapacity)} SLOTS).`;
    capWarnEl.hidden = !state.overCapacity;
  }
  const slotsWarnEl = document.getElementById('co-supply-warn-slots');
  if (slotsWarnEl) {
    slotsWarnEl.textContent = `DEALER HAT NICHT GENUG FREIE SLOTS (NUR ${String(sm.freeSlots)} FREI).`;
    slotsWarnEl.hidden = !state.overSlots;
  }
  const errWarnEl = document.getElementById('co-supply-warn-error');
  if (errWarnEl) {
    errWarnEl.textContent = sm.errorMsg ?? '';
    errWarnEl.hidden = !sm.errorMsg;
  }

  // NT-TRANSPORT-BELADUNG-01 Rev. 2: Dauer-Vorschau nach jeder relevanten Änderung neu anfragen —
  // updateSupplyModalDerivedDom() ist bereits der einzige Aufrufpunkt für Checkbox/-/+/MAX/
  // Vehicle-/Source-Wechsel, ein Hook hier deckt alle Call-Sites ab.
  scheduleSupplyDurationPreview();
}

// NT-TRANSPORT-BELADUNG-01 Rev. 2: Dauer-Vorschau vor dem Absenden. Reiner Anzeige-Text-Zustand
// (supplyDurationPreviewText), per Debounce nachgeladen und in-place gepatcht — kein Full-Re-Render.
function supplyDurationDefaultHint(hasSelection: boolean): string {
  return hasSelection
    ? 'Dauer wird berechnet...'
    : 'Wähle mindestens eine Ware für eine Zeitschätzung.';
}

function buildSupplyPreviewItems(sm: DealerSupplyModal): { itemKey: string; itemType: string; quantity: number }[] {
  return sm.items
    .filter((i) => i.selected && i.qty > 0)
    .map((i) => ({ itemKey: i.itemKey, itemType: i.itemType, quantity: i.qty }));
}

function patchSupplyDurationPreviewDom(): void {
  if (!dealerSupplyModal) return;
  const el = document.getElementById('co-supply-duration-preview');
  if (!el) return;
  const hasSelection = dealerSupplyModal.items.some((i) => i.selected && i.qty > 0);
  el.textContent = supplyDurationPreviewText || supplyDurationDefaultHint(hasSelection);
}

function scheduleSupplyDurationPreview(): void {
  supplyDurationPreviewText = '';
  patchSupplyDurationPreviewDom();

  if (supplyPreviewDebounceTimer !== null) clearTimeout(supplyPreviewDebounceTimer);
  supplyPreviewDebounceTimer = setTimeout(() => { void refreshSupplyDurationPreview(); }, 300);
}

async function refreshSupplyDurationPreview(): Promise<void> {
  // Referenz auf DIESES Modal-Objekt einfangen — schützt nicht nur gegen "Modal inzwischen
  // geschlossen" (dealerSupplyModal === null), sondern auch gegen "Modal geschlossen UND ein
  // neues geöffnet, bevor diese Antwort da ist" (dealerSupplyModal zeigt dann auf ein anderes
  // Objekt). Ein reiner !dealerSupplyModal-Check nach dem await würde TS/ESLint zudem als
  // unreachable markieren, da die Narrowing aus dem Funktionsstart über den await hinweg
  // erhalten bleibt.
  const modalRef = dealerSupplyModal;
  if (!modalRef) return;
  const items = buildSupplyPreviewItems(modalRef);
  if (items.length === 0) return; // Hinweistext ("wähle mind. eine Ware") bleibt stehen

  const vehicleKey = modalRef.vehicleKey;
  const seq = ++supplyPreviewRequestSeq;
  try {
    const preview = await api.post<TransportPreview>('/dealer/supply-preview', { vehicleKey, items });
    if (seq !== supplyPreviewRequestSeq || dealerSupplyModal !== modalRef) return;
    const entfernungLabel = ` · Entfernung ${String(preview.steps)} Straße${preview.steps === 1 ? '' : 'n'}`;
    supplyDurationPreviewText = `Dauer ca. ${String(preview.totalMinutes)} Min. (Verladung ${String(preview.loadingMinutes)} / Fahrt ${String(preview.travelMinutes)} / Abladung ${String(preview.unloadingMinutes)})${entfernungLabel}`;
  } catch {
    if (seq !== supplyPreviewRequestSeq || dealerSupplyModal !== modalRef) return;
    supplyDurationPreviewText = 'Dauer konnte nicht berechnet werden.';
  }
  patchSupplyDurationPreviewDom();
}

// NT-AUFTRAGSBRETT-02 — Dauer-Vorschau im TRANSPORT-Annahme-Modal, exaktes Muster wie oben
// (Dealer-Belieferung): Quelle/Ziel stehen bereits fest (vom Angebot fixiert), nur der Fahrzeug-
// wechsel löst eine neue Berechnung aus.
function patchAuftragsbrettDurationPreviewDom(): void {
  if (!auftragsbrettAcceptModal) return;
  const el = document.getElementById('co-auftragsbrett-duration-preview');
  if (!el) return;
  el.textContent = auftragsbrettAcceptModal.durationPreviewText || 'Fahrzeug wählen für eine Zeitschätzung.';
}

function scheduleAuftragsbrettDurationPreview(): void {
  if (!auftragsbrettAcceptModal) return;
  auftragsbrettAcceptModal = { ...auftragsbrettAcceptModal, durationPreviewText: '' };
  patchAuftragsbrettDurationPreviewDom();

  if (auftragsbrettPreviewDebounceTimer !== null) clearTimeout(auftragsbrettPreviewDebounceTimer);
  auftragsbrettPreviewDebounceTimer = setTimeout(() => { void refreshAuftragsbrettDurationPreview(); }, 300);
}

async function refreshAuftragsbrettDurationPreview(): Promise<void> {
  const modalRef = auftragsbrettAcceptModal;
  if (!modalRef?.vehicleKey || !modalRef.quellePropertyId || !modalRef.zielPropertyId) return;

  const seq = ++auftragsbrettPreviewRequestSeq;
  try {
    const preview = await api.post<TransportPreview>('/transport/preview', {
      vehicleKey: modalRef.vehicleKey,
      items: [{ itemKey: 'auftrag_paket_transport', itemType: 'MATERIAL', quantity: modalRef.gebundeneSlots }],
      fromPropertyId: modalRef.quellePropertyId,
      toPropertyId: modalRef.zielPropertyId,
    });
    if (seq !== auftragsbrettPreviewRequestSeq || auftragsbrettAcceptModal !== modalRef) return;
    const entfernungLabel = ` · Entfernung ${String(preview.steps)} Straße${preview.steps === 1 ? '' : 'n'}`;
    auftragsbrettAcceptModal = {
      ...modalRef,
      durationPreviewText: `Dauer ca. ${String(preview.totalMinutes)} Min. (Verladung ${String(preview.loadingMinutes)} / Fahrt ${String(preview.travelMinutes)} / Abladung ${String(preview.unloadingMinutes)})${entfernungLabel}`,
    };
  } catch {
    if (seq !== auftragsbrettPreviewRequestSeq || auftragsbrettAcceptModal !== modalRef) return;
    auftragsbrettAcceptModal = { ...modalRef, durationPreviewText: 'Dauer konnte nicht berechnet werden.' };
  }
  patchAuftragsbrettDurationPreviewDom();
}

// Quelllager-Wechsel ändert den Item-Bestand legitim — hier wird bewusst nur der Item-Listen-
// Container neu aufgebaut (nicht das Modal-Chrome: Titel, Schritt-Labels, Footer bleiben stehen),
// Scrollposition des Modal-Bodys wird gesichert/wiederhergestellt.
function refreshSupplyItemsList(): void {
  if (!dealerSupplyModal) return;
  const container = document.getElementById('co-supply-items');
  if (!container) return;
  const modalBody = document.querySelector<HTMLElement>('#co-dealer-supply-modal .modal-body');
  const savedScrollTop = modalBody?.scrollTop ?? 0;

  container.innerHTML = buildSupplyItemsHtml(dealerSupplyModal);
  updateSupplyModalDerivedDom();

  if (modalBody) modalBody.scrollTop = savedScrollTop;
}

/** NT-VIERTEL-04-ECKEN (E9) — Fahrzeuge, die bei einer Dealer-Verhaftung beschlagnahmt wurden
 * (role=SEIZED). Global im Dealer-Tab, da ein beschlagnahmtes Fahrzeug keinem Dealer mehr
 * zugeordnet ist (dealer.vehicleId wurde bereits bei der Verhaftung genullt). */
function renderSeizedVehiclesSection(): string {
  const seized = dealerVehicleOptions.filter((v) => v.seizedUntil !== null);
  if (seized.length === 0) return '';

  const rows = seized.map((v) => {
    const remaining = Math.max(0, new Date(v.seizedUntil ?? '').getTime() - Date.now());
    return `
      <div class="co-dealer-row">
        <span>${esc(v.label.toUpperCase())}</span>
        <strong>FREI IN ${fmtCountdown(remaining)}</strong>
      </div>
      <div class="co-actions" style="margin-bottom:var(--space-2);">
        <button class="co-btn co-btn-primary" data-co-action="bail-vehicle" data-vehicle-id="${esc(v.id)}">KAUTION ZAHLEN — ${fmtEur(String(VEHICLE_BAIL_COST_DISPLAY))}</button>
      </div>`;
  }).join('');

  return `
    <p class="co-section-label" style="margin-top:var(--space-3);">BESCHLAGNAHMTE FAHRZEUGE</p>
    <p class="co-empty" style="padding:0 0 var(--space-2);">Automatische Rückgabe nach ${String(VEHICLE_SEIZURE_HOURS_DISPLAY)} Std., oder sofort gegen Kaution.</p>
    ${rows}`;
}

function renderDealerContent(): string {
  if (!dealerData) return '<p class="co-empty">LADE...</p>';

  const limit = getDealerLimit();
  const canHireMore = dealerData.canHireMore && limit > 0;
  const dealers = dealerData.dealers;

  if (dealerFleeingModalDealerId === null) {
    const fleeingDealer = dealers.find((dealer) => dealer.status === 'FLEEING');
    if (fleeingDealer) dealerFleeingModalDealerId = fleeingDealer.id;
  }

  const dealerCards = dealers.length === 0
    ? '<p class="co-empty">NOCH KEIN DEALER ANGEHEUERT.</p>'
    : dealers.map((dealer) => {
        const isTraining = dealer.status === 'TRAINING';
        const isArrested = dealer.status === 'ARRESTED';
        const suspicionPct = Math.max(0, Math.min(100, Math.round(dealer.suspicion)));
        const inventoryPct = dealer.effectiveSlots > 0 ? Math.max(0, Math.min(100, Math.round((dealer.usedSlots / dealer.effectiveSlots) * 100))) : 0;
        const inventoryFree = Math.max(0, dealer.effectiveSlots - dealer.usedSlots);
        const pending = Number.parseInt(dealer.pendingEarnings, 10);
        const bailCents = DEALER_TIER_BAIL_CENTS[dealer.tier];
        const trainingRemaining = dealer.trainingCompletesAt ? Math.max(0, new Date(dealer.trainingCompletesAt).getTime() - Date.now()) : 0;
        const arrestRemaining = dealer.arrestedUntil ? Math.max(0, new Date(dealer.arrestedUntil).getTime() - Date.now()) : 0;
        const vehicleLabel = dealer.vehicleLabel ?? 'KEIN FAHRZEUG';
        const vehicleText = dealer.vehicleKey ? esc(vehicleLabel.toUpperCase()) : 'KEIN FAHRZEUG';
        const statusClass = getDealerStatusClass(dealer.status);
        const statusLabel = getDealerStatusLabel(dealer.status);
        const suspicionColor = dealer.suspicionZone === 'HIGH'
          ? 'var(--accent-red)'
          : dealer.suspicionZone === 'MEDIUM'
            ? 'var(--accent-orange)'
            : 'var(--accent-green)';

        const actions: string[] = [];
        if (dealer.status === 'ACTIVE' || dealer.status === 'PAUSED') {
          actions.push(`<button class="co-btn ${dealer.status === 'ACTIVE' ? 'co-btn-warn' : 'co-btn-primary'}" data-co-action="pause-dealer" data-dealerid="${esc(dealer.id)}">${dealer.status === 'ACTIVE' ? 'PAUSIEREN' : 'FORTSETZEN'}</button>`);
          actions.push(`<button class="co-btn co-btn-primary" data-co-action="open-dealer-supply" data-dealerid="${esc(dealer.id)}">WARE LIEFERN</button>`);
        }
        actions.push(`<button class="co-btn co-btn-accent" data-co-action="toggle-dealer-inventory-panel" data-dealerid="${esc(dealer.id)}">${isDealerInventoryPanelOpen(dealer) ? 'INVENTAR SCHLIESSEN' : 'INVENTAR ZEIGEN'}</button>`);
        if (pending > 0) {
          actions.push(`<button class="co-btn co-btn-primary" data-co-action="collect-dealer" data-dealerid="${esc(dealer.id)}">EINSAMMELN</button>`);
        }
        if (dealer.status === 'ACTIVE' && dealer.trainingCompletesAt === null) {
          actions.push(`<button class="co-btn co-btn-info" data-co-action="train-dealer" data-dealerid="${esc(dealer.id)}">SCHULEN</button>`);
        }
        if (isArrested) {
          actions.push(`<button class="co-btn co-btn-primary" data-co-action="bail-dealer" data-dealerid="${esc(dealer.id)}">KAUTION ZAHLEN — ${fmtEur(String(bailCents))}</button>`);
        }

        return `
          <div class="co-dealer-card${dealer.status !== 'ACTIVE' && dealer.status !== 'PAUSED' ? ' co-dealer-card--inactive' : ''}" data-dealer-id="${esc(dealer.id)}">
            <div class="co-dealer-head">
              <div>
                <div class="co-business-name">${esc(dealer.name)}</div>
                <div class="co-dealer-tier">${esc(getDealerTierLabel(dealer.tier))}</div>
              </div>
              <span class="co-status-badge ${statusClass}">${statusLabel}</span>
            </div>
            <div class="co-dealer-row">
              <span>VERDACHT</span>
              <strong data-dealer-susp-val="${esc(dealer.id)}" style="color:${suspicionColor}">${String(suspicionPct)}%</strong>
            </div>
            <div class="co-progress-track"><div class="co-progress-fill" data-dealer-susp-bar="${esc(dealer.id)}" style="--co-progress:${String(suspicionPct)}%;background:${suspicionColor};"></div></div>
            <div class="co-dealer-row">
              <span>INVENTAR</span>
              <strong data-dealer-inv-display="${esc(dealer.id)}">${String(dealer.usedSlots)} / ${String(dealer.effectiveSlots)} SLOTS</strong>
            </div>
            <div class="co-progress-track"><div class="co-progress-fill" data-dealer-inv-bar="${esc(dealer.id)}" style="--co-progress:${String(inventoryPct)}%;"></div></div>
            <div class="co-dealer-row">
              <span>EINNAHMEN</span>
              <strong data-dealer-earnings="${esc(dealer.id)}" class="${pending > 0 ? 'val-green' : 'val-muted'}">${fmtSeur(dealer.pendingEarnings)}</strong>
            </div>
            <div class="co-dealer-row">
              <span>FAHRZEUG</span>
              <strong>${esc(vehicleText)}</strong>
            </div>
            <div class="co-dealer-row">
              <span>SLOTS FREI</span>
              <strong data-dealer-slots-free="${esc(dealer.id)}">${String(inventoryFree)}</strong>
            </div>
            <div class="co-dealer-row">
              <span>STANDORT</span>
              <strong class="${dealer.streetCornerId ? '' : 'val-muted'}">${
                dealer.streetCornerId
                  ? `${esc(dealer.streetName ?? '')} · ${esc(dealer.cornerName ?? '')}${dealer.absatzLabel ? ` · ABSATZ ${esc(dealer.absatzLabel)}` : ''}`
                  : 'OHNE POSTEN'
              }</strong>
            </div>
            ${dealer.relocateCompletesAt ? `<p class="co-order-note">SIEDELT UM — noch ${fmtCountdown(Math.max(0, new Date(dealer.relocateCompletesAt).getTime() - Date.now()))}</p>` : ''}
            ${dealer.pendingSupplyDeliveries.map((d) => {
              const remaining = Math.max(0, new Date(d.arrivesAt).getTime() - Date.now());
              return `
                <div class="co-dealer-row">
                  <span>LIEFERUNG UNTERWEGS</span>
                  <strong data-dealer-delivery-time="${esc(d.id)}">${String(d.itemCount)}&times; &mdash; ${fmtCountdown(remaining)}</strong>
                </div>
                ${renderDealerDeliveryPhaseHtml(d, Date.now())}
                <div class="co-actions" style="margin-top:var(--space-1);">
                  <button class="co-btn co-btn-accent" data-co-action="skip-dealer-supply" data-dealerid="${esc(dealer.id)}" data-delivery-id="${esc(d.id)}">${String(DEALER_SUPPLY_SKIP_COST_SCHEINE_DISPLAY)} SCHEINE - SOFORT</button>
                </div>`;
            }).join('')}
            ${isTraining ? `<p class="co-order-note" data-dealer-training="${esc(dealer.id)}">AUSLERNEN IN ${fmtCountdown(trainingRemaining)}</p>` : ''}
            ${isArrested ? `<p class="co-order-note" data-dealer-arrest="${esc(dealer.id)}">FREILASSUNG IN ${fmtCountdown(arrestRemaining)}</p>` : ''}
            <div class="co-actions co-dealer-actions">
              ${actions.join('')}
            </div>
            ${isDealerInventoryPanelOpen(dealer) ? `
            <div class="co-dealer-inventory-panel" data-dealer-inventory-panel="${esc(dealer.id)}">
              ${renderDealerInventoryPanel(dealer)}
            </div>` : ''}
            <div class="co-dealer-vehicle-panel${dealerVehicleOptions.length > 0 ? '' : ' is-empty'}" data-dealer-vehicle-panel="${esc(dealer.id)}">
              <div class="co-dealer-vehicle-panel-head">
                <span>FAHRZEUG-ZUWEISUNG</span>
                <button class="co-btn co-btn-accent" data-co-action="toggle-dealer-vehicle-panel" data-dealerid="${esc(dealer.id)}">${isDealerVehiclePanelOpen(dealer) ? 'SCHLIESSEN' : 'ÖFFNEN'}</button>
              </div>
              ${isDealerVehiclePanelOpen(dealer) && dealerFleeingModalDealerId !== dealer.id ? renderDealerVehicleAssignment(dealer) : ''}
              ${dealer.vehicleId ? `<div class="co-actions" style="margin-top:var(--space-2);"><button class="co-btn co-btn-danger" data-co-action="assign-dealer-vehicle" data-dealerid="${esc(dealer.id)}" data-vehicle-id="__release__">FAHRZEUG FREIGEBEN</button></div>` : ''}
            </div>
          </div>`;
      }).join('');

  return `
    <p class="co-section-label">MEINE DEALER (${String(dealers.length)}/${String(limit)})</p>
    <div class="co-run-meta"><span>LIMIT: ${String(dealers.length)} / ${String(limit)} DEALER AKTIV</span><span>VERFÜGBAR: ${String(Math.max(0, limit - dealers.length))}</span></div>
    ${dealerCards}
    ${canHireMore ? `
      <p class="co-section-label" style="margin-top:var(--space-3);">DEALER REKRUTIEREN</p>
      <div class="co-dealer-recruit">
        <button class="co-btn co-btn-primary" data-co-action="hire-dealer" ${limit <= dealers.length ? 'disabled' : ''}>DEALER REKRUTIEREN — ${fmtEur(String(DEALER_PURCHASE_PRICE_CENTS))}</button>
      </div>
    ` : '<p class="co-empty">MAXIMALE ANZAHL DEALER ERREICHT.</p>'}
    ${renderSeizedVehiclesSection()}
    ${renderDealerFleeingModal()}
    ${renderDealerSupplyModal()}
    ${renderDealerTrainingConfirmModal(dealers)}
  `;
}

/** NT-POLISH-01 (P5): "4 STD 00 MIN" — Dauer im Bestätigungsdialog. */
function fmtTrainingDuration(hours: number): string {
  const totalMin = Math.round(hours * 60);
  return `${String(Math.floor(totalMin / 60))} STD ${String(totalMin % 60).padStart(2, '0')} MIN`;
}

/** NT-POLISH-01 (P5): Bestätigungsdialog vor dem Schulungsstart (gemeinsame Modal-Hülle). */
function renderDealerTrainingConfirmModal(dealers: DealerResponse[]): string {
  if (!dealerTrainingConfirmId) return '';
  const dealer = dealers.find((d) => d.id === dealerTrainingConfirmId);
  const training = dealer?.nextTraining ?? null;
  if (!dealer || !training) return '';
  const balance = BigInt(_gameState.getPlayer()?.noxBalance ?? '0');
  const cost    = BigInt(training.costCents);
  const canPay  = balance >= cost;
  return `
    <div class="modal-backdrop" id="co-train-dealer-modal">
      <div class="modal modal--narrow" role="dialog" aria-modal="true" aria-label="Schulung starten">
        <div class="modal-header"><h2>SCHULUNG STARTEN?</h2><p class="modal-subtitle">${esc(dealer.name)}</p></div>
        <div class="modal-body">
          <div class="co-dealer-row"><span>KOSTEN</span><strong>${fmtEur(training.costCents)}</strong></div>
          <div class="co-dealer-row"><span>DAUER</span><strong>${fmtTrainingDuration(training.hours)}</strong></div>
          <p class="co-order-note">Der Dealer verkauft während der Schulung nichts.</p>
          ${canPay ? '' : '<p class="co-order-note co-order-note--warn">ZU WENIG GELD FÜR DIE SCHULUNG.</p>'}
        </div>
        <div class="modal-actions">
          <button class="co-btn" data-co-action="close-train-dealer">ABBRECHEN</button>
          <button class="co-btn co-btn-primary" data-co-action="confirm-train-dealer" ${canPay ? '' : 'disabled'}>STARTEN</button>
        </div>
      </div>
    </div>`;
}

// ─── Render: Schmuggel-Sektion ────────────────────────────────────────────────

function renderSchmuggelContent(): string {
  if (!schmuggelData) return '<p class="co-empty">LADE...</p>';
  const { routes, active, completed, vehicles, hasSchleuser } = schmuggelData;
  const now = Date.now();

  const activeHtml = active.length === 0
    ? '<p class="co-empty">Keine aktiven Operationen.</p>'
    : active.map((op) => {
        const remaining = Math.max(0, new Date(op.arrivesAt).getTime() - now);
        return `
          <div class="co-batch">
            <div class="co-batch-header">
              <span class="co-batch-name">${esc(op.routeLabel.toUpperCase())} · ${esc(op.vehicleKey.toUpperCase())} · ${String(op.quantity)}× ${esc(op.itemKey)}</span>
              <span data-schm-time="${esc(op.id)}">${fmtCountdown(remaining)}</span>
            </div>
            <p style="font-family:var(--font-mono);font-size:10px;color:var(--text-muted);">Chance: ${String(Math.round(op.successChance * 100))}%</p>
          </div>`;
      }).join('');

  const completedHtml = completed.slice(0, 3).map((op) => `
    <div class="co-batch co-batch--${op.status === 'SUCCESS' ? 'done' : 'failed'}">
      <div class="co-batch-header">
        <span class="co-batch-name">${esc(op.routeKey.toUpperCase())}</span>
        <span class="${op.status === 'SUCCESS' ? 'val-green' : 'val-red'}">${op.status === 'SUCCESS' ? `+${op.earnedCents ? (parseInt(op.earnedCents, 10) / 100).toFixed(0) : '?'} ${op.earnedCurrency === 'BLACK_MONEY' ? 'S-₦' : '₦'}` : 'ERWISCHT'}</span>
      </div>
    </div>`).join('');

  const routesHtml = routes.map((r) => {
    const hasVehicle = vehicles.some((v) => r.vehicleTypes.includes(v.vehicleKey));
    const blocked    = r.locked || !hasVehicle || (r.needsSchleuser && !hasSchleuser);
    const blockReason = r.locked ? r.lockReason : !hasVehicle ? 'Fahrzeug benötigt' : r.needsSchleuser && !hasSchleuser ? 'Schleuser-Kontakt fehlt' : null;
    return `
      <div class="co-recipe${blocked ? ' co-recipe--locked' : ''}">
        <div class="co-recipe-header">
          <span class="co-recipe-name">${esc(r.label.toUpperCase())}</span>
          <span class="val-gold">×${String(r.ertragsMulti)}</span>
          <span class="val-orange">${String(Math.round(r.baseRisk * 100))}% RISIKO</span>
          <span class="co-recipe-time">${fmtMins(r.durationMinutes)}</span>
        </div>
        <p class="co-recipe-lore">${esc(r.lore)}</p>
        ${blocked
          ? `<p class="co-locked-hint">${esc(blockReason ?? 'GESPERRT')}</p>`
          : `<button class="co-btn co-btn-primary co-btn-sm" data-co-action="start-schmuggel" data-route="${esc(r.key)}">STARTEN</button>`
        }
      </div>`;
  }).join('');

  return `
    ${renderSmuggleCheckpointModal()}
    ${renderSmuggleRunSection()}
    <p class="co-section-label" style="margin-top:var(--space-3);">TRANSPORT-ROUTEN</p>
    <p class="co-section-label">AKTIVE OPERATIONEN</p>
    ${activeHtml}
    ${completed.length > 0 ? `<p class="co-section-label">LETZTE ERGEBNISSE</p>${completedHtml}` : ''}
    <p class="co-section-label" style="margin-top:var(--space-3);">ROUTEN</p>
    ${vehicles.length === 0 ? `<p class="co-empty">Kein Fahrzeug im Lager. <a href="/business/markt" class="co-link">Fahrzeug kaufen</a>.</p>` : ''}
    ${routesHtml}
  `;
}

// ─── Schmuggel-Run UI (Checkpoint-System) ────────────────────────────────────

function riskColor(r: SmuggleRisk): string {
  if (r === 'HIGH')   return 'var(--accent-red)';
  if (r === 'MEDIUM') return 'var(--accent-orange)';
  return 'var(--accent-green)';
}

function riskLabel(r: SmuggleRisk): string {
  if (r === 'HIGH')   return 'HOCH';
  if (r === 'MEDIUM') return 'MITTEL';
  return 'NIEDRIG';
}

function renderSmuggleCheckpointModal(): string {
  const run = smuggleRunStatus?.activeRun;
  if (run?.status !== 'CHECKPOINT' || !run.activeCheckpoint) return '';
  const cp             = run.activeCheckpoint;
  const blackMoney     = BigInt(smuggleRunStatus?.blackMoneyBalance ?? '0');

  const optionsHtml = cp.options.map((opt) => {
    const costCents   = BigInt(opt.costCents);
    const cantAfford  = costCents > 0n && blackMoney < costCents;
    const costHtml    = costCents > 0n
      ? ` <span style="color:${cantAfford ? 'var(--accent-red)' : 'var(--accent-orange)'};">${(Number(costCents) / 100).toFixed(0)} S-₦${cantAfford ? ' – NICHT GENUG' : ''}</span>` : '';
    return `<div class="co-modal-panel">
      <p class="co-section-label">${esc(opt.label)}</p>
      <p class="co-order-note">${esc(opt.description)}${costHtml}</p>
      <button class="co-btn co-btn-primary"
        data-co-action="decide-checkpoint"
        data-run-id="${esc(run.id)}"
        data-checkpoint-id="${esc(cp.checkpointId)}"
        data-decision="${esc(opt.id)}"
        ${cantAfford ? 'disabled title="Nicht genug S-₦"' : ''}
      >${esc(opt.label)}</button>
    </div>`;
  }).join('');

  return `<div class="modal-backdrop" id="co-smuggle-checkpoint-modal">
    <div class="modal modal--wide" role="dialog" aria-modal="true" aria-label="Checkpoint">
      <div class="modal-header"><h2>CHECKPOINT ${String(cp.number)} / ${String(run.totalCheckpoints)} — ${esc(cp.typeLabel.toUpperCase())}</h2></div>
      <div class="modal-body">
        <p class="co-order-note" style="margin-bottom:var(--space-3);">${esc(cp.lore)}</p>
        <div class="co-modal-grid">${optionsHtml}</div>
      </div>
    </div>
  </div>`;
}

function renderSmuggleRunSection(): string {
  if (!smuggleRunStatus) return '';
  const s = smuggleRunStatus;

  // Kein Schleuser-Kontakt → Hinweis
  if (!s.hasSchleuser) {
    return `<div class="co-section">
      <p class="co-section-label">SCHMUGGEL-RUN</p>
      <p class="co-empty">Schleuser-Kontakt benötigt. Unter KONTAKTE freischalten.</p>
    </div>`;
  }

  // Schleuser-Info
  const stageLabel = s.schleuerStage === 3 ? 'PARTNER' : s.schleuerStage === 2 ? 'VERTRAUT' : 'BEKANNT';
  const nextRunsText = s.schleuerNextAt !== null ? ` (nächste Stufe in ${String(s.schleuerNextAt - s.schleuerRuns)} Runs)` : '';
  const schleuerInfo = `<p class="co-order-note" style="margin-bottom:var(--space-2);">
    SCHLEUSER: <span style="color:var(--accent-gold);">${stageLabel}</span> · ${String(s.schleuerRuns)} Runs${esc(nextRunsText)}
  </p>`;

  // FL-Warnung
  const flWarnHtml = s.flWarning
    ? `<p class="co-order-note" style="color:var(--accent-red);">${esc(s.flWarning)}</p>` : '';

  // Aktiver Run
  const activeRun = s.activeRun;
  let activeHtml = '';
  if (activeRun && (activeRun.status === 'RUNNING' || activeRun.status === 'CHECKPOINT')) {
    const prog = activeRun.totalCheckpoints > 0
      ? Math.round((activeRun.currentCheckpoint / activeRun.totalCheckpoints) * 100) : 0;
    const nextEta = activeRun.nextEventAt
      ? `<span data-schm-run-timer="${esc(activeRun.id)}">${fmtCountdown(Math.max(0, new Date(activeRun.nextEventAt).getTime() - Date.now()))}</span>`
      : '—';
    activeHtml = `<div class="co-batch co-batch--active">
      <div class="co-batch-header">
        <span class="co-batch-name">${esc(activeRun.itemLabel.toUpperCase())} · ${String(activeRun.quantity)}× · ${esc(activeRun.vehicleLabel.toUpperCase())}</span>
        <span style="color:${riskColor(activeRun.riskLevel)};">${riskLabel(activeRun.riskLevel)}</span>
      </div>
      <div style="display:flex;gap:var(--space-2);align-items:center;margin-top:var(--space-2);font-size:var(--font-size-xs);color:var(--text-muted);">
        <div style="flex:1;height:4px;background:var(--bg-elevated);border-radius:2px;">
          <div style="width:${String(prog)}%;height:100%;background:var(--accent-gold);border-radius:2px;"></div>
        </div>
        <span>CHECKPOINT ${String(activeRun.currentCheckpoint)}/${String(activeRun.totalCheckpoints)}</span>
        ${activeRun.status === 'RUNNING' ? `· NÄCHSTER IN: ${nextEta}` : ''}
        ${activeRun.status === 'CHECKPOINT' ? `<span style="color:var(--accent-orange);">ENTSCHEIDUNG AUSSTEHEND</span>` : ''}
      </div>
    </div>`;
  }

  // Tagesangebot (nur wenn kein aktiver Run)
  let offerHtml = '';
  if (!activeRun || activeRun.status === 'COMPLETED' || activeRun.status === 'FAILED') {
    const offerItems = s.dailyOffer.map((item) => {
      const isSelected = smuggleRunSelectedItem === item.itemKey;
      const priceStr = (Number(item.basePriceCents) / 100).toFixed(0);
      return `<div class="co-recipe${isSelected ? ' co-recipe--selected' : ''}"
        style="cursor:pointer;"
        data-co-action="select-smuggle-item"
        data-item-key="${esc(item.itemKey)}"
      >
        <div class="co-recipe-header">
          <span class="co-recipe-name">${esc(item.label.toUpperCase())}</span>
          <span style="color:${riskColor(item.riskLevel)};">${riskLabel(item.riskLevel)}</span>
          <span class="val-gold">~${esc(priceStr)} S-₦/Stk.</span>
        </div>
      </div>`;
    }).join('');
    offerHtml = `<p class="co-section-label">TAGESANGEBOT</p>${offerItems}`;

    // Run-Startformular (wenn Item gewählt + kein aktiver Run)
    if (smuggleRunSelectedItem && schmuggelData) {
      const availableVehicles = schmuggelData.vehicles;
      const vehicleOpts = availableVehicles.length === 0
        ? '<option value="">— KEIN FAHRZEUG —</option>'
        : availableVehicles.map((v) => {
            const sel = v.vehicleKey === smuggleRunSelectedVehicle ? ' selected' : '';
            return `<option value="${esc(v.vehicleKey)}"${sel}>${esc(v.vehicleKey.toUpperCase())}</option>`;
          }).join('');
      const selVehicle = availableVehicles.find((v) => v.vehicleKey === smuggleRunSelectedVehicle) ?? availableVehicles[0];
      const maxQty  = selVehicle ? Math.min(selVehicle.quantity * 2, 50) : 1;
      const safeQty = Math.max(1, Math.min(smuggleRunSelectedQty, maxQty));

      offerHtml += `<div class="co-recipe" style="margin-top:var(--space-2);">
        <p class="co-section-label">RUN STARTEN</p>
        <div class="co-modal-row">
          <span>WARE</span>
          <strong>${esc(smuggleRunSelectedItem.toUpperCase())}</strong>
        </div>
        <div class="co-modal-row">
          <span>FAHRZEUG</span>
          <select class="co-select" data-co-action="select-smuggle-vehicle">${vehicleOpts}</select>
        </div>
        <div class="co-modal-row">
          <span>MENGE</span>
          <input type="number" class="co-input" id="smuggle-run-qty"
            min="1" max="${String(maxQty)}" value="${String(safeQty)}"
            style="width:80px;"
            data-co-action="set-smuggle-qty"
          />
        </div>
        ${flWarnHtml}
        <button class="co-btn co-btn-primary co-btn-sm" data-co-action="start-smuggle-run"
          ${s.flWarning || availableVehicles.length === 0 ? 'disabled' : ''}
        >SCHMUGGEL-RUN STARTEN</button>
      </div>`;
    }
  }

  // Historie (letzte 5 abgeschlossene)
  let historyHtml = '';
  if (smuggleRunHistory.length > 0) {
    const rows = smuggleRunHistory.slice(0, 5).map((r) => {
      const isOk = r.status === 'COMPLETED';
      return `<div class="co-batch co-batch--${isOk ? 'done' : 'failed'}">
        <div class="co-batch-header">
          <span class="co-batch-name">${esc(r.itemLabel.toUpperCase())} ×${String(r.quantity)}</span>
          <span class="${isOk ? 'val-green' : 'val-red'}">${isOk ? 'ERFOLGREICH' : 'GESCHEITERT'}</span>
        </div>
      </div>`;
    }).join('');
    historyHtml = `<p class="co-section-label" style="margin-top:var(--space-3);">LETZTE RUNS</p>${rows}`;
  }

  return `<div class="co-section">
    <p class="co-section-label">SCHMUGGEL-RUN</p>
    ${schleuerInfo}
    ${activeHtml}
    ${offerHtml}
    ${historyHtml}
  </div>`;
}


// ─── Render: Gewächshaus-Sektion ─────────────────────────────────────────────

// ─── Render: gesperrte Sektion ────────────────────────────────────────────────

/**
 * NT-LAYOUT-01 (L2): URL an die aktive Sektion angleichen, damit die Bottom-Nav den richtigen
 * Button hervorhebt (/labor, /viertel …) — per replaceState, kein Router-Lauf, kein neuer
 * History-Eintrag. Nur wenn wir uns überhaupt auf einem Crime-Ops-Pfad befinden.
 */
function syncCrimeOpsUrl(section: Section): void {
  const current = window.location.pathname;
  if (!isCrimeOpsPath(current)) return;
  const target = getPathForCrimeOpsSection(section);
  if (target === current) return;
  history.replaceState(history.state, '', target);
  updateBottomNavTab(target);
}

/** Für das Dashboard: nach Navigation auf /business/crime-ops die URL wieder an die bereits aktive Sektion angleichen. */
export function syncCrimeOpsUrlToSelection(): void {
  syncCrimeOpsUrl(selectedSection);
}

// NT-LAYOUT-01 (L2): Labor und Gewächshaus sind direkt über die Bottom-Nav erreichbar — auch
// gesperrt. Die Sperr-Ansicht erklärt deshalb zusätzlich in einem Satz, was der Bereich ist.
const LOCKED_SECTION_INTRO: Partial<Record<Section, string>> = {
  labor:         'Im Labor stellst du aus Zutaten Ware her — legale Produkte für den Straßenhandel, illegale für Dealer und Schwarzmarkt.',
  'gewächshaus': 'Im Gewächshaus baust du Hanf und Tabak an. Die Ernte verkaufst du direkt oder verarbeitest sie am Rolltisch weiter.',
};

function renderLockedContent(sectionName: string, section: CrimeOpsSection, intro?: string): string {
  const reqs = section.requirements;
  const allMet = reqs.every((r) => r.met);

  const checklistHtml = reqs.map((r) => `
    <div class="co-req-row">
      <span class="co-req-icon ${r.met ? 'co-req-icon--ok' : 'co-req-icon--no'}">${r.met ? '&#10003;' : '&#10007;'}</span>
      <div class="co-req-info">
        <span class="co-req-label ${r.met ? 'co-req-label--met' : 'co-req-label--unmet'}">${esc(r.label)}</span>
        ${r.hint ? `<span class="co-req-hint">${esc(r.hint)}</span>` : ''}
        ${!r.met && r.action && r.actionLabel
          ? `<a class="co-req-action" href="${esc(r.action)}" data-route="${esc(r.action)}">&#8594; ${esc(r.actionLabel)}</a>`
          : ''}
      </div>
    </div>
  `).join('');

  return `
    <div class="co-locked-box">
      ${LOCK_SVG}
      <p class="co-locked-text">${esc(sectionName)} — GESPERRT</p>
      ${intro ? `<p class="co-locked-hint">${esc(intro)}</p>` : ''}
      ${reqs.length > 0
        ? `<div class="co-req-list">${checklistHtml}</div>`
        : `<p class="co-locked-hint">Benötigt: ${esc(section.requiredTalent)}</p>`
      }
      ${!allMet && reqs.some((r) => !r.met && r.action === '/talentbaum')
        ? `<a class="co-req-goto-talent" href="/talentbaum" data-route="/talentbaum">&#8594; TALENTBAUM ÖFFNEN</a>`
        : ''}
    </div>
  `;
}

// ─── Render: aktiver Sektions-Inhalt ─────────────────────────────────────────

// ─── Gärtner-Shop State ───────────────────────────────────────────────────────

let gaertnerShopData:    GaertnerShopData | null = null;
let gaertnerShopLoading                 = false;
// Warenkorb: strainKey → Menge
const gaertnerCart          = new Map<string, number>();

// ─── Alchemist-Shop State ─────────────────────────────────────────────────────

let alchemistShopData:    AlchemistShopData | null = null;
let alchemistShopLoading                  = false;
// Warenkorb: itemKey → Menge
const alchemistCart           = new Map<string, number>();

function renderContactDeliveryTargetPicker(): string {
  if (contactDeliveryTargets.length === 0) {
    return '<p class="co-empty" style="margin:0 0 var(--space-2) 0;">Kein aktives Lager/Versteck als Lieferziel verfügbar.</p>';
  }

  const hasSelected = contactDeliveryTargets.some((t) => t.id === propsDataForGaertner);
  const firstTarget = contactDeliveryTargets[0];
  if (!firstTarget) return '';
  const effectiveTargetId = hasSelected ? propsDataForGaertner : firstTarget.id;
  if (propsDataForGaertner !== effectiveTargetId) {
    propsDataForGaertner = effectiveTargetId;
  }

  const options = renderPropertyOptions(contactDeliveryTargets, { selectedId: effectiveTargetId });

  return `
    <p class="co-section-label" style="margin-top:0;">LIEFERZIEL</p>
    <select class="markt-select" data-contact-target style="margin-bottom:var(--space-2);">${options}</select>
  `;
}

// ─── Render: KONTAKTE Tab ─────────────────────────────────────────────────────

function renderKontakteContent(kontakte: CrimeOpsStatus['kontakte']): string {
  const CONTACT_EFFECTS: Record<string, { icon: string; color: string }> = {
    kontakt_gruener_guenther: { icon: '→ EXKLUSIVER SAMEN-HÄNDLER',    color: 'var(--accent-green)'  },
    kontakt_alchemist:        { icon: '→ ILLEGALE LABOR-ZUTATEN',      color: 'var(--accent-purple)' },
    kontakt_schleuser:        { icon: '→ SCHMUGGEL-ROUTEN',            color: 'var(--accent-blue)'   },
    kontakt_informant:        { icon: '→ POLIZEI-VORWARNUNG',          color: 'var(--accent-orange)' },
    kontakt_lieferant:        { icon: '→ SCHWARZMARKT-ZUGANG',         color: 'var(--accent-purple)' },
    kontakt_fixer:            { icon: '→ PROBLEME LÖSEN',              color: 'var(--accent-red)'    },
  };

  if (kontakte.contacts.length === 0) {
    return `
      <p class="co-empty">Keine Kontakte vorhanden.</p>
      <p class="co-empty" style="margin-top:0;">Im Markt unter KONTAKTE kaufen — belegen keine Lager-Slots.</p>
    `;
  }

  const cardsHtml = kontakte.contacts.map((c) => {
    const fx   = CONTACT_EFFECTS[c.contactKey];
    const date = new Date(c.acquiredAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const isGaertner   = c.contactKey === 'kontakt_gruener_guenther';
    const isAlchemist  = c.contactKey === 'kontakt_alchemist';
    const hasShopSection = isGaertner || isAlchemist;

    let extraHtml = '';
    if (isGaertner)  extraHtml = renderGaertnerSection(c);
    if (isAlchemist) extraHtml = renderAlchemistSection(c);

    return `
      <div class="co-business">
        <div class="co-business-header">
          <span class="co-business-name">${esc(c.label)}</span>
          <span class="co-status-badge aktiv">AKTIV</span>
        </div>
        <p style="font-family:var(--font-mono);font-size:var(--font-size-xs);color:var(--text-muted);margin:0 0 var(--space-2) 0;line-height:1.5;">${esc(c.description)}</p>
        ${fx ? `<p style="font-family:var(--font-mono);font-size:11px;color:${fx.color};margin:0 0 var(--space-1) 0;letter-spacing:0.06em;">${esc(fx.icon)}</p>` : ''}
        <p style="font-family:var(--font-mono);font-size:10px;color:var(--text-muted);margin:0 0 ${hasShopSection ? 'var(--space-3)' : '0'} 0;">Erworben: ${esc(date)}</p>
        ${extraHtml}
      </div>
    `;
  }).join('');

  return `
    <p style="font-family:var(--font-mono);font-size:11px;letter-spacing:0.1em;color:var(--text-muted);text-transform:uppercase;margin:0 0 var(--space-3) 0;">
      AKTIVE KONTAKTE (${String(kontakte.contacts.length)})
    </p>
    ${cardsHtml}
    <p style="font-family:var(--font-mono);font-size:11px;color:var(--text-muted);margin-top:var(--space-3);line-height:1.5;opacity:0.7;">
      Kontakte im Markt kaufen · belegen keine Lager-Slots · jeweils einmalig
    </p>
  `;
}

function renderGaertnerSection(c: PlayerContactInfo): string {
  // Trust-Info aus bekannten Stufen (spiegelt Backend GAERTNER_TRUST_LEVELS)
  const TRUST_CFG = [
    { needed:   0, label: 'FREMDER',          max:  5, discount:  0 },
    { needed:  15, label: 'BEKANNTER',         max: 10, discount:  0 },
    { needed:  40, label: 'STAMMKUNDE',        max: 20, discount:  0 },
    { needed:  90, label: 'VERTRAUT',          max: 35, discount:  0 },
    { needed: 175, label: 'PARTNER',           max: 50, discount:  0 },
    { needed: 300, label: 'GESCHÄFTSFREUND',  max: 75, discount: 10 },
    { needed: 500, label: 'KARTELL-INSIDER',   max: 99, discount: 20 },
  ];
  const firstTrustCfg = TRUST_CFG[0];
  if (!firstTrustCfg) throw new Error('unreachable');
  const cur  = TRUST_CFG[c.trustLevel] ?? firstTrustCfg;
  const next = TRUST_CFG[c.trustLevel + 1];

  let progressHtml = '';
  if (next) {
    const pct = Math.min(100, Math.round((c.purchaseCount / next.needed) * 100));
    const discountBadge = cur.discount > 0
      ? `<span style="color:var(--accent-green);margin-left:8px;">-${String(cur.discount)}% RABATT</span>` : '';
    progressHtml = `
      <div style="margin-bottom:var(--space-2);">
        <div style="display:flex;justify-content:space-between;font-family:var(--font-mono);font-size:10px;color:var(--text-muted);margin-bottom:3px;">
          <span>VERTRAUEN: ${esc(cur.label)}${discountBadge}</span>
          <span>${String(c.purchaseCount)} / ${String(next.needed)} → ${esc(next.label)}</span>
        </div>
        <div style="height:5px;background:var(--bg-base);border:1px solid var(--border);border-radius:2px;overflow:hidden;">
          <div style="height:100%;background:var(--accent-green);width:${String(pct)}%;transition:width .3s;"></div>
        </div>
        <p style="font-family:var(--font-mono);font-size:10px;color:var(--text-muted);margin:3px 0 0;">
          Max pro Bestellung: ${String(cur.max)} Samen/Sorte${cur.discount > 0 ? ` · ${String(cur.discount)}% Rabatt aktiv` : ''}
        </p>
      </div>`;
  } else {
    progressHtml = `
      <p style="font-family:var(--font-mono);font-size:10px;color:var(--accent-green);margin:0 0 var(--space-2) 0;">
        ${esc(cur.label)} — Max ${String(cur.max)} / Sorte · -${String(cur.discount)}% RABATT
      </p>`;
  }

  if (gaertnerShopLoading) {
    return `${progressHtml}<p class="co-empty" style="margin:0;">Gärtner lädt...</p>`;
  }

  if (!gaertnerShopData) {
    return `${progressHtml}
      <button class="co-btn co-btn-primary" data-co-action="load-gaertner-shop">BEIM GÄRTNER KAUFEN</button>`;
  }

  const shop = gaertnerShopData;

  // Wöchentliches Limit
  const weeklyPct      = Math.min(100, Math.round((shop.weeklyUsed / shop.weeklyLimit) * 100));
  const weeklyColor    = shop.weeklyRemaining === 0 ? 'var(--accent-red)' : shop.weeklyRemaining <= 5 ? 'var(--accent-orange)' : 'var(--accent-green)';
  const nextWeeklyDate = new Date(shop.nextWeeklyResetAt);
  const nextWeeklyTz   = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin', timeZoneName: 'short',
  }).formatToParts(nextWeeklyDate).find((p) => p.type === 'timeZoneName')?.value ?? 'MEZ';
  const nextWeeklyStr  = nextWeeklyDate.toLocaleDateString('de-DE', {
    timeZone: 'Europe/Berlin', weekday: 'short', day: '2-digit', month: '2-digit',
  }) + ' 06:00 ' + nextWeeklyTz;
  const weeklyHtml = `
    <div style="margin-bottom:var(--space-2);">
      <div style="display:flex;justify-content:space-between;font-family:var(--font-mono);font-size:10px;color:var(--text-muted);margin-bottom:3px;">
        <span>WÖCHENTLICH ${String(shop.weeklyUsed)}/${String(shop.weeklyLimit)}</span>
        <span style="color:${weeklyColor};">${String(shop.weeklyRemaining)} übrig · Reset ${esc(nextWeeklyStr)}</span>
      </div>
      <div style="height:4px;background:var(--bg-base);border:1px solid var(--border);border-radius:2px;overflow:hidden;">
        <div style="height:100%;background:${weeklyColor};width:${String(weeklyPct)}%;transition:width .3s;"></div>
      </div>
    </div>`;

  // Cooldown-Anzeige
  let cooldownHtml = '';
  if (shop.cooldownActive && shop.cooldownRemainingStr) {
    cooldownHtml = `
      <div style="border:1px solid var(--accent-orange);border-radius:var(--radius);padding:8px 12px;margin-bottom:var(--space-2);font-family:var(--font-mono);font-size:11px;">
        <span style="color:var(--accent-orange);">GÜNTHER BESCHÄFTIGT</span>
        <span style="color:var(--text-muted);margin-left:8px;">Nächste Bestellung in ${esc(shop.cooldownRemainingStr)}</span>
      </div>`;
  }

  // Warenkorb berechnen
  let cartTotal = 0n;
  let cartQty   = 0;
  for (const [sk, qty] of gaertnerCart) {
    const def = shop.catalog.find((i) => i.strainKey === sk);
    if (def) {
      cartTotal += BigInt(parseInt(def.priceNox, 10)) * BigInt(qty);
      cartQty   += qty;
    }
  }

  // Katalog-Tabelle
  const rows = shop.catalog.map((item) => {
    const locked   = !item.available;
    const inCart   = gaertnerCart.get(item.strainKey) ?? 0;
    const hasDiscount = item.priceNox !== item.priceNoxOriginal;
    const priceDisplay = hasDiscount
      ? `<span style="color:var(--accent-gold);">${(parseInt(item.priceNox, 10) / 100).toFixed(0)} ₦</span> <span style="color:var(--text-muted);text-decoration:line-through;font-size:10px;">${(parseInt(item.priceNoxOriginal, 10) / 100).toFixed(0)}</span>`
      : `<span style="color:var(--accent-gold);">${(parseInt(item.priceNox, 10) / 100).toFixed(0)} ₦</span>`;

    const addBtn = locked
      ? `<button class="co-btn" disabled style="font-size:10px;padding:3px 8px;">ab Trust ${String(item.minTrust)}</button>`
      : `<button class="co-btn co-btn-primary" style="font-size:10px;padding:3px 8px;"
           data-co-action="gaertner-cart-add" data-strain="${esc(item.strainKey)}"
           ${shop.cooldownActive ? 'disabled' : ''}>
           + WARENKORB${inCart > 0 ? ` (${String(inCart)})` : ''}
         </button>`;

    return `<tr style="${locked ? 'opacity:.4;' : ''}">
      <td style="padding:5px 8px;font-family:var(--font-mono);font-size:var(--font-size-xs);color:${inCart > 0 ? 'var(--accent-green)' : 'var(--text-primary)'};">${esc(item.label)}</td>
      <td style="padding:5px 8px;font-family:var(--font-mono);font-size:11px;">${priceDisplay}<span style="font-family:var(--font-mono);font-size:10px;color:var(--text-muted);">/Stk</span></td>
      <td style="padding:5px 8px;">${addBtn}</td>
    </tr>`;
  }).join('');

  // Warenkorb-Einträge
  let cartHtml = '';
  if (gaertnerCart.size > 0) {
    const cartItems = Array.from(gaertnerCart.entries()).map(([sk, qty]) => {
      const def = shop.catalog.find((i) => i.strainKey === sk);
      if (!def) return '';
      const lineTotal = (parseInt(def.priceNox, 10) * qty / 100).toFixed(0);
      return `
        <div style="display:flex;align-items:center;gap:8px;font-family:var(--font-mono);font-size:11px;margin-bottom:4px;">
          <button class="markt-qty-btn" style="flex-shrink:0;" data-co-action="gaertner-cart-qty" data-strain="${esc(sk)}" data-delta="-1">-</button>
          <span style="min-width:20px;text-align:center;color:var(--text-primary);">${String(qty)}</span>
          <button class="markt-qty-btn" style="flex-shrink:0;" data-co-action="gaertner-cart-qty" data-strain="${esc(sk)}" data-delta="+1">+</button>
          <span style="flex:1;color:var(--text-primary);">${esc(def.label)}</span>
          <span style="color:var(--accent-gold);min-width:60px;text-align:right;">${lineTotal} ₦</span>
          <button class="co-btn" style="font-size:10px;padding:2px 8px;color:var(--accent-red);border-color:var(--accent-red);"
            data-co-action="gaertner-cart-remove" data-strain="${esc(sk)}">X</button>
        </div>`;
    }).join('');

    const canOrder = !shop.cooldownActive && gaertnerCart.size > 0;
    cartHtml = `
      <div style="border-top:1px solid var(--border);margin-top:var(--space-2);padding-top:var(--space-2);">
        <p style="font-family:var(--font-mono);font-size:10px;letter-spacing:.08em;color:var(--text-muted);margin:0 0 var(--space-1) 0;">WARENKORB (${String(cartQty)} Samen)</p>
        ${cartItems}
        <div style="border-top:1px solid var(--border);margin-top:8px;padding-top:8px;display:flex;justify-content:space-between;align-items:center;">
          <span style="font-family:var(--font-mono);font-size:12px;color:var(--text-primary);">GESAMT</span>
          <span style="font-family:var(--font-mono);font-size:13px;color:var(--accent-gold);font-weight:600;">${(Number(cartTotal) / 100).toFixed(0)} ₦</span>
        </div>
        <div style="margin-top:var(--space-2);">
          <button class="co-btn co-btn-primary" data-co-action="gaertner-buy" ${!canOrder ? 'disabled' : ''} style="width:100%;">
            JETZT BESTELLEN (${String(cartQty)} Samen · ${(Number(cartTotal) / 100).toFixed(0)} ₦)
          </button>
          ${shop.cooldownActive ? `<p style="font-family:var(--font-mono);font-size:10px;color:var(--accent-orange);margin:4px 0 0;text-align:center;">Cooldown aktiv — ${esc(shop.cooldownRemainingStr ?? '')}</p>` : ''}
        </div>
      </div>`;
  }

  return `${progressHtml}
    ${weeklyHtml}
    ${cooldownHtml}
    ${renderContactDeliveryTargetPicker()}
    <div style="border-top:1px solid var(--border);padding-top:var(--space-2);">
      <p style="font-family:var(--font-mono);font-size:10px;letter-spacing:.08em;color:var(--text-muted);margin:0 0 var(--space-2) 0;">
        GÜNTHERS SORTIMENT · Max ${String(shop.maxPerOrder)} pro Sorte/Bestellung
      </p>
      <div style="overflow-x:auto;margin-bottom:var(--space-2);">
        <table style="width:100%;border-collapse:collapse;">
          <thead><tr>
            <th style="font-family:var(--font-mono);font-size:10px;color:var(--text-muted);text-align:left;padding:0 8px 4px;">SORTE</th>
            <th style="font-family:var(--font-mono);font-size:10px;color:var(--text-muted);text-align:left;padding:0 8px 4px;">PREIS/STK</th>
            <th style="font-family:var(--font-mono);font-size:10px;color:var(--text-muted);text-align:left;padding:0 8px 4px;"></th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
      ${cartHtml}
    </div>`;
}

// ─── Render: Alchemist-Sektion ────────────────────────────────────────────────

function renderAlchemistSection(c: PlayerContactInfo): string {
  const TRUST_CFG = [
    { needed:   0, label: 'FREMDER',          max:  5, discount:  0 },
    { needed:  15, label: 'BEKANNTER',         max: 10, discount:  0 },
    { needed:  40, label: 'STAMMKUNDE',        max: 20, discount:  0 },
    { needed:  90, label: 'VERTRAUT',          max: 35, discount:  0 },
    { needed: 175, label: 'PARTNER',           max: 50, discount:  0 },
    { needed: 300, label: 'GESCHÄFTSFREUND',  max: 75, discount: 10 },
    { needed: 500, label: 'KARTELL-INSIDER',   max: 99, discount: 20 },
  ];
  const firstTrustCfg = TRUST_CFG[0];
  if (!firstTrustCfg) throw new Error('unreachable');
  const cur  = TRUST_CFG[c.trustLevel] ?? firstTrustCfg;
  const next = TRUST_CFG[c.trustLevel + 1];

  let progressHtml = '';
  if (next) {
    const pct = Math.min(100, Math.round((c.purchaseCount / next.needed) * 100));
    const discountBadge = cur.discount > 0
      ? `<span style="color:var(--accent-green);margin-left:8px;">-${String(cur.discount)}% RABATT</span>` : '';
    progressHtml = `
      <div style="margin-bottom:var(--space-2);">
        <div style="display:flex;justify-content:space-between;font-family:var(--font-mono);font-size:10px;color:var(--text-muted);margin-bottom:3px;">
          <span>VERTRAUEN: ${esc(cur.label)}${discountBadge}</span>
          <span>${String(c.purchaseCount)} / ${String(next.needed)} → ${esc(next.label)}</span>
        </div>
        <div style="height:5px;background:var(--bg-base);border:1px solid var(--border);border-radius:2px;overflow:hidden;">
          <div style="height:100%;background:var(--accent-purple);width:${String(pct)}%;transition:width .3s;"></div>
        </div>
        <p style="font-family:var(--font-mono);font-size:10px;color:var(--text-muted);margin:3px 0 0;">
          Max pro Bestellung: ${String(cur.max)} Einheiten/Artikel${cur.discount > 0 ? ` · ${String(cur.discount)}% Rabatt aktiv` : ''}
        </p>
      </div>`;
  } else {
    progressHtml = `
      <p style="font-family:var(--font-mono);font-size:10px;color:var(--accent-purple);margin:0 0 var(--space-2) 0;">
        ${esc(cur.label)} — Max ${String(cur.max)} / Artikel · -${String(cur.discount)}% RABATT
      </p>`;
  }

  if (alchemistShopLoading) {
    return `${progressHtml}<p class="co-empty" style="margin:0;">Alchemist lädt...</p>`;
  }

  if (!alchemistShopData) {
    return `${progressHtml}
      <button class="co-btn co-btn-primary" data-co-action="load-alchemist-shop">BEIM ALCHEMISTEN KAUFEN</button>`;
  }

  const shop = alchemistShopData;

  // Wöchentliches Limit
  const alchWeeklyPct   = Math.min(100, Math.round((shop.weeklyUsed / shop.weeklyLimit) * 100));
  const alchWeeklyColor = shop.weeklyRemaining === 0 ? 'var(--accent-red)' : shop.weeklyRemaining <= 5 ? 'var(--accent-orange)' : 'var(--accent-purple)';
  const alchNextDate    = new Date(shop.nextWeeklyResetAt);
  const alchNextTz      = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin', timeZoneName: 'short',
  }).formatToParts(alchNextDate).find((p) => p.type === 'timeZoneName')?.value ?? 'MEZ';
  const alchNextStr     = alchNextDate.toLocaleDateString('de-DE', {
    timeZone: 'Europe/Berlin', weekday: 'short', day: '2-digit', month: '2-digit',
  }) + ' 06:00 ' + alchNextTz;
  const alchWeeklyHtml = `
    <div style="margin-bottom:var(--space-2);">
      <div style="display:flex;justify-content:space-between;font-family:var(--font-mono);font-size:10px;color:var(--text-muted);margin-bottom:3px;">
        <span>WÖCHENTLICH ${String(shop.weeklyUsed)}/${String(shop.weeklyLimit)}</span>
        <span style="color:${alchWeeklyColor};">${String(shop.weeklyRemaining)} übrig · Reset ${esc(alchNextStr)}</span>
      </div>
      <div style="height:4px;background:var(--bg-base);border:1px solid var(--border);border-radius:2px;overflow:hidden;">
        <div style="height:100%;background:${alchWeeklyColor};width:${String(alchWeeklyPct)}%;transition:width .3s;"></div>
      </div>
    </div>`;

  let cooldownHtml = '';
  if (shop.cooldownActive && shop.cooldownRemainingStr) {
    cooldownHtml = `
      <div style="border:1px solid var(--accent-orange);border-radius:var(--radius);padding:8px 12px;margin-bottom:var(--space-2);font-family:var(--font-mono);font-size:11px;">
        <span style="color:var(--accent-orange);">ALCHEMIST BESCHÄFTIGT</span>
        <span style="color:var(--text-muted);margin-left:8px;">Nächste Bestellung in ${esc(shop.cooldownRemainingStr)}</span>
      </div>`;
  }

  let cartTotal = 0n;
  let cartQty   = 0;
  for (const [ik, qty] of alchemistCart) {
    const def = shop.catalog.find((i) => i.itemKey === ik);
    if (def) {
      cartTotal += BigInt(parseInt(def.priceBm, 10)) * BigInt(qty);
      cartQty   += qty;
    }
  }

  const rows = shop.catalog.map((item) => {
    const locked   = !item.available;
    const inCart   = alchemistCart.get(item.itemKey) ?? 0;
    const hasDiscount = item.priceBm !== item.priceBmOriginal;
    const priceDisplay = hasDiscount
      ? `<span style="color:var(--accent-bm);">${(parseInt(item.priceBm, 10) / 100).toFixed(0)} S-₦</span> <span style="color:var(--text-muted);text-decoration:line-through;font-size:10px;">${(parseInt(item.priceBmOriginal, 10) / 100).toFixed(0)}</span>`
      : `<span style="color:var(--accent-bm);">${(parseInt(item.priceBm, 10) / 100).toFixed(0)} S-₦</span>`;

    const addBtn = locked
      ? `<button class="co-btn" disabled style="font-size:10px;padding:3px 8px;">ab Trust ${String(item.minTrust)}</button>`
      : `<button class="co-btn co-btn-primary" style="font-size:10px;padding:3px 8px;"
           data-co-action="alchemist-cart-add" data-item="${esc(item.itemKey)}"
           ${shop.cooldownActive ? 'disabled' : ''}>
           + WARENKORB${inCart > 0 ? ` (${String(inCart)})` : ''}
         </button>`;

    return `<tr style="${locked ? 'opacity:.4;' : ''}">
      <td style="padding:5px 8px;font-family:var(--font-mono);font-size:var(--font-size-xs);color:${inCart > 0 ? 'var(--accent-purple)' : 'var(--text-primary)'};">${esc(item.label)}</td>
      <td style="padding:5px 8px;font-family:var(--font-mono);font-size:11px;">${priceDisplay}<span style="font-family:var(--font-mono);font-size:10px;color:var(--text-muted);">/Stk</span></td>
      <td style="padding:5px 8px;">${addBtn}</td>
    </tr>`;
  }).join('');

  let cartHtml = '';
  if (alchemistCart.size > 0) {
    const cartItems = Array.from(alchemistCart.entries()).map(([ik, qty]) => {
      const def = shop.catalog.find((i) => i.itemKey === ik);
      if (!def) return '';
      const lineTotal = (parseInt(def.priceBm, 10) * qty / 100).toFixed(0);
      return `
        <div style="display:flex;align-items:center;gap:8px;font-family:var(--font-mono);font-size:11px;margin-bottom:4px;">
          <button class="markt-qty-btn" style="flex-shrink:0;" data-co-action="alchemist-cart-qty" data-item="${esc(ik)}" data-delta="-1">-</button>
          <span style="min-width:20px;text-align:center;color:var(--text-primary);">${String(qty)}</span>
          <button class="markt-qty-btn" style="flex-shrink:0;" data-co-action="alchemist-cart-qty" data-item="${esc(ik)}" data-delta="+1">+</button>
          <span style="flex:1;color:var(--text-primary);">${esc(def.label)}</span>
          <span style="color:var(--accent-bm);min-width:70px;text-align:right;">${lineTotal} S-₦</span>
          <button class="co-btn" style="font-size:10px;padding:2px 8px;color:var(--accent-red);border-color:var(--accent-red);"
            data-co-action="alchemist-cart-remove" data-item="${esc(ik)}">X</button>
        </div>`;
    }).join('');

    const canOrder = !shop.cooldownActive && alchemistCart.size > 0;
    cartHtml = `
      <div style="border-top:1px solid var(--border);margin-top:var(--space-2);padding-top:var(--space-2);">
        <p style="font-family:var(--font-mono);font-size:10px;letter-spacing:.08em;color:var(--text-muted);margin:0 0 var(--space-1) 0;">WARENKORB (${String(cartQty)} Einheiten)</p>
        ${cartItems}
        <div style="border-top:1px solid var(--border);margin-top:8px;padding-top:8px;display:flex;justify-content:space-between;align-items:center;">
          <span style="font-family:var(--font-mono);font-size:12px;color:var(--text-primary);">GESAMT</span>
          <span style="font-family:var(--font-mono);font-size:13px;color:var(--accent-bm);font-weight:600;">${(Number(cartTotal) / 100).toFixed(0)} S-₦</span>
        </div>
        <div style="margin-top:var(--space-2);">
          <button class="co-btn co-btn-primary" data-co-action="alchemist-buy" ${!canOrder ? 'disabled' : ''} style="width:100%;">
            JETZT BESTELLEN (${String(cartQty)} Einh. · ${(Number(cartTotal) / 100).toFixed(0)} S-₦)
          </button>
          ${shop.cooldownActive ? `<p style="font-family:var(--font-mono);font-size:10px;color:var(--accent-orange);margin:4px 0 0;text-align:center;">Cooldown aktiv — ${esc(shop.cooldownRemainingStr ?? '')}</p>` : ''}
        </div>
      </div>`;
  }

  return `${progressHtml}
    ${alchWeeklyHtml}
    ${cooldownHtml}
    ${renderContactDeliveryTargetPicker()}
    <div style="border-top:1px solid var(--border);padding-top:var(--space-2);">
      <p style="font-family:var(--font-mono);font-size:10px;letter-spacing:.08em;color:var(--text-muted);margin:0 0 var(--space-2) 0;">
        DES ALCHEMISTEN SORTIMENT · Max ${String(shop.maxPerOrder)} pro Artikel/Bestellung
      </p>
      <div style="overflow-x:auto;margin-bottom:var(--space-2);">
        <table style="width:100%;border-collapse:collapse;">
          <thead><tr>
            <th style="font-family:var(--font-mono);font-size:10px;color:var(--text-muted);text-align:left;padding:0 8px 4px;">ARTIKEL</th>
            <th style="font-family:var(--font-mono);font-size:10px;color:var(--text-muted);text-align:left;padding:0 8px 4px;">PREIS/STK</th>
            <th style="font-family:var(--font-mono);font-size:10px;color:var(--text-muted);text-align:left;padding:0 8px 4px;"></th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
      ${cartHtml}
    </div>`;
}

function renderSectionContent(status: CrimeOpsStatus): string {
  function renderVehicleControls(includeSlotSelector = true): string {
    const vehicleOptions = vehicleList.map((v) =>
      `<option value="${esc(v.id)}" ${v.id === selectedVehicleId ? 'selected' : ''}>${esc(v.label.toUpperCase())} (${String(v.capacity)} SLOTS)</option>`
    ).join('');

    const slotOptions = includeSlotSelector
      ? (selectedVehicleInventory?.slots ?? [])
        .filter((s) => s.itemKey !== null && (s.quantity ?? 0) > 0)
        .map((s) => {
          const selected = s.slotIndex === selectedVehicleSlotIndex ? 'selected' : '';
          return `<option value="${String(s.slotIndex)}" ${selected}>SLOT ${String(s.slotIndex)} · ${esc(String(s.itemKey).toUpperCase())} · ${String(s.quantity ?? 0)}x</option>`;
        })
        .join('')
      : '';

    return `
      <div class="co-business">
        <div class="co-business-header"><span class="co-business-name">${includeSlotSelector ? 'FAHRZEUG & SLOT' : 'FAHRZEUG'}</span></div>
        ${vehicleList.length === 0 ? '<p class="co-empty">Kein Fahrzeug vorhanden.</p>' : `
          <p class="co-section-label">FAHRZEUG</p>
          <select class="markt-select" data-ops-vehicle>${vehicleOptions}</select>
          ${includeSlotSelector ? `
            <p class="co-section-label" style="margin-top:var(--space-2);">SLOT</p>
            <select class="markt-select" data-ops-slot>${slotOptions || '<option value="0">Kein belegter Slot</option>'}</select>
          ` : ''}
        `}
      </div>
    `;
  }

  function renderCellmateJobSection(): string {
    const job = cellmateJobData;
    if (!job) {
      return '<p class="co-empty">Kein aktiver Zellengenosse-Auftrag.</p>';
    }

    const riskLabel = ['', 'NIEDRIG', 'MITTEL', 'HOCH'][job.riskLevel] ?? String(job.riskLevel);
    const reward = `+${(Number(job.rewardCents) / 100).toFixed(0)} S-₦`;

    if (job.isCompleted || job.isFailed) {
      const statusLabel = job.isCompleted ? 'ABGESCHLOSSEN' : `FEHLGESCHLAGEN (+${String(job.flPenalty)} FL)`;
      return `
        <article class="co-order-card co-order-card--cellmate">
          <p class="co-order-batch">ZELLENGENOSSE</p>
          <p class="co-order-name">${esc(job.itemLabel.toUpperCase())} × ${String(job.quantity)}</p>
          <p class="co-order-note co-order-note--${job.isCompleted ? 'ok' : 'danger'}">${statusLabel}</p>
          <p class="co-order-note">Neuer Auftrag folgt in 48h.</p>
        </article>
      `;
    }

    if (job.acceptedAt && job.completesAt) {
      const remainingMs = Math.max(0, new Date(job.completesAt).getTime() - Date.now());
      const isDone = remainingMs <= 0;
      return `
        <article class="co-order-card co-order-card--cellmate" id="co-cellmate-job-card">
          <p class="co-order-batch">ZELLENGENOSSE</p>
          <p class="co-order-name">LIEFERUNG LÄUFT</p>
          <p class="co-order-note">${esc(job.itemLabel.toUpperCase())} × ${String(job.quantity)}</p>
          <p class="co-order-note">RISIKO: ${riskLabel} · STRAFE: +${String(job.flPenalty)} FL bei Erwischung</p>
          <div class="co-run-meta"><span>BELOHNUNG</span><span>${reward}</span></div>
          <p class="co-order-note">FERTIG IN: <span id="co-cellmate-countdown">${isDone ? 'FERTIG' : fmtCountdown(remainingMs)}</span></p>
          <div class="co-actions">
            <button class="co-btn co-btn-primary" data-co-action="cellmate-complete" data-jobid="${esc(job.id)}" ${isDone ? '' : 'disabled'}>ABSCHLIESSEN</button>
          </div>
        </article>
      `;
    }

    // Noch nicht angenommen
    const expiresMs = Math.max(0, new Date(job.expiresAt).getTime() - Date.now());
    return `
      <article class="co-order-card co-order-card--cellmate" id="co-cellmate-job-card">
        <p class="co-order-batch">ZELLENGENOSSE</p>
        <p class="co-order-name">${esc(job.itemLabel.toUpperCase())} × ${String(job.quantity)}</p>
        <p class="co-order-note">RISIKO: ${riskLabel} · STRAFE: +${String(job.flPenalty)} FL bei Erwischung</p>
        <div class="co-run-meta"><span>BELOHNUNG</span><span>${reward}</span></div>
        ${job.isTrustTest
          ? `<p class="co-order-note">PROVISION: ${reward} — Das Bargeld gehört dem Auftraggeber.</p>`
          : ''}
        <p class="co-order-note co-order-note--muted">VERFÜGBAR BIS: <span>${fmtCountdown(expiresMs)}</span></p>
        <div class="co-actions">
          <button class="co-btn co-btn-primary" data-co-action="cellmate-accept" data-jobid="${esc(job.id)}">ANNEHMEN</button>
          <button class="co-btn" data-co-action="cellmate-reject" data-jobid="${esc(job.id)}">ABLEHNEN</button>
        </div>
      </article>
    `;
  }

  function renderAuftragsbrettAcceptModal(): string {
    if (!auftragsbrettAcceptModal) return '';
    const m = auftragsbrettAcceptModal;
    const profile = getAuftraggeberProfile(m.auftraggeberTyp);
    const erhoehtVerdacht = (profile?.verdachtsaufschlag ?? 0) > 0;

    if (m.auftragTyp === 'TRANSPORT') {
      // Quelle+Ziel sind vom Angebot fixiert (nur Anzeige) — der Spieler wählt aktiv nur das
      // Fahrzeug (T3/Abschnitt 5, siehe Design-Kommentar in auftragsbrett.definitions.ts).
      const availableVehicles = vehicleList.filter((v) => v.isAvailable);
      const vehicleOptions = availableVehicles.map((v) =>
        `<option value="${esc(v.vehicleKey)}" ${v.vehicleKey === m.vehicleKey ? 'selected' : ''}>${esc(v.label.toUpperCase())}</option>`,
      ).join('');

      return `
        <div class="modal-backdrop" id="co-auftragsbrett-accept-modal">
          <div class="modal" role="dialog" aria-modal="true" aria-label="Transport-Auftrag annehmen">
            <div class="modal-header"><h2>AUFTRAG ANNEHMEN — ${esc(m.auftraggeberName.toUpperCase())}</h2></div>
            <div class="modal-body">
              <p class="co-order-note">Transportiert ${String(m.gebundeneSlots)} Slots von der Quelle zum Ziel.</p>
              ${erhoehtVerdacht ? '<p class="co-order-note co-order-note--warn">ERHÖHT DEN VERDACHT BEIDER OBJEKTE, BIS DER AUFTRAG ENDET.</p>' : ''}
              <div class="co-run-meta"><span>QUELLE:</span><span>${esc((m.quellePropertyLabel ?? '—').toUpperCase())}</span></div>
              <div class="co-run-meta"><span>ZIEL:</span><span>${esc((m.zielPropertyLabel ?? '—').toUpperCase())}</span></div>
              <label class="co-supply-label">FAHRZEUG</label>
              ${availableVehicles.length === 0
                ? '<p class="co-supply-empty">KEIN FREIES FAHRZEUG.</p>'
                : `<select class="co-supply-select" id="co-auftragsbrett-vehicle">${vehicleOptions}</select>`}
              <p class="co-order-note co-order-note--muted" id="co-auftragsbrett-duration-preview">${esc(m.durationPreviewText || 'Fahrzeug wählen für eine Zeitschätzung.')}</p>
              ${m.errorMsg ? `<p class="co-order-note co-order-note--warn">${esc(m.errorMsg)}</p>` : ''}
            </div>
            <div class="modal-actions">
              <button class="co-btn co-btn-primary" data-co-action="confirm-auftragsbrett-accept" ${availableVehicles.length === 0 || !m.vehicleKey ? 'disabled' : ''}>ANNEHMEN</button>
              <button class="co-btn" data-co-action="close-auftragsbrett-accept">ABBRECHEN</button>
            </div>
          </div>
        </div>`;
    }

    // NT-AUFTRAGSBRETT-01B (PR3) — Objektwahl kommt aus dem Board-Profil statt einem hartcodierten
    // KNASTKONTAKT-Sonderfall.
    const wantsHideout = profile?.objektwahl === 'VERSTECK';
    const eligible = auftragsbrettFreeProperties.filter(
      (p) => p.freeSlots >= m.gebundeneSlots && (!profile || p.type === (wantsHideout ? 'HIDEOUT' : 'WAREHOUSE')),
    );
    const options = renderPropertyOptions(
      eligible.map((p) => ({
        id: p.id, label: p.label, customName: p.customName, nickname: p.nickname,
        isHideout: p.type === 'HIDEOUT', freeSlots: p.freeSlots,
      })),
      { selectedId: m.propertyId },
    );

    return `
      <div class="modal-backdrop" id="co-auftragsbrett-accept-modal">
        <div class="modal" role="dialog" aria-modal="true" aria-label="Auftrag annehmen">
          <div class="modal-header"><h2>AUFTRAG ANNEHMEN — ${esc(m.auftraggeberName.toUpperCase())}</h2></div>
          <div class="modal-body">
            <p class="co-order-note">Bindet ${String(m.gebundeneSlots)} Slots im gewählten Objekt, bis die Liegezeit abgelaufen ist.</p>
            ${erhoehtVerdacht ? '<p class="co-order-note co-order-note--warn">ERHÖHT DEN VERDACHT DIESES OBJEKTS, BIS DER AUFTRAG ENDET.</p>' : ''}
            <label class="co-supply-label">OBJEKT</label>
            ${eligible.length === 0
              ? `<p class="co-supply-empty">${wantsHideout ? 'KEIN VERSTECK MIT GENUG FREIEM PLATZ.' : 'KEIN OBJEKT MIT GENUG FREIEM PLATZ.'}</p>`
              : `<select class="co-supply-select" id="co-auftragsbrett-property">${options}</select>`}
            ${m.errorMsg ? `<p class="co-order-note co-order-note--warn">${esc(m.errorMsg)}</p>` : ''}
          </div>
          <div class="modal-actions">
            <button class="co-btn co-btn-primary" data-co-action="confirm-auftragsbrett-accept" ${eligible.length === 0 || !m.propertyId ? 'disabled' : ''}>ANNEHMEN</button>
            <button class="co-btn" data-co-action="close-auftragsbrett-accept">ABBRECHEN</button>
          </div>
        </div>
      </div>`;
  }

  function renderOrdersContent(): string {
    // Enthält auch OPEN (noch nicht angenommene Angebote) — für die alte "AKTIVE AUFTRÄGE"-Liste
    // gefiltert weg, alte, bereits angenommene Alt-Aufträge bleiben lieferbar (F3 Bestandsschutz).
    const activeOrders = (ordersData?.active ?? []).filter(
      (order) => order.status === 'OPEN' || order.status === 'ACCEPTED' || order.status === 'PARTIAL',
    );
    // NT-CRIMEOPS-FIX-01/B1: "AKTIVE AUFTRÄGE" enthält ausschließlich angenommene, noch laufende
    // Aufträge — ein nicht angenommenes Angebot steht nur oben bei den Auftraggebern.
    const runningOrders = selectRunningOrders(activeOrders);

    const formatAgo = (iso: string): string => {
      const diffMs = Math.max(0, Date.now() - new Date(iso).getTime());
      const mins = Math.floor(diffMs / 60_000);
      if (mins < 60) return `vor ${String(Math.max(1, mins))}m`;
      const hours = Math.floor(mins / 60);
      if (hours < 24) return `vor ${String(hours)}h`;
      const days = Math.floor(hours / 24);
      return `vor ${String(days)} Tag${days > 1 ? 'en' : ''}`;
    };

    const fmtReward = (cents: string, currency: string): string => currency === 'BLACK_MONEY' ? fmtSeur(cents) : fmtEur(cents);

    // NT-AUFTRAGSBRETT-01B (PR5) — "das Profil steht auf der Karte, bevor ein Angebot da ist":
    // z.B. "NUR VERSTECK · 2–4 SLOTS · 6–12 STD · ERHÖHT VERDACHT". Liest ausschließlich aus dem
    // vom Backend gelieferten AuftraggeberProfileView, keine eigenen Zahlen im Frontend.
    const formatAuftraggeberProfileLine = (profile: AuftraggeberProfileView): string => {
      const objektwahlLabel = profile.objektwahl === 'LAGER' ? 'NUR LAGER' : 'NUR VERSTECK';
      const parts = [
        objektwahlLabel,
        `${String(profile.slotsMin)}–${String(profile.slotsMax)} SLOTS`,
        `${String(profile.liegezeitStundenMin)}–${String(profile.liegezeitStundenMax)} STD`,
      ];
      if (profile.verdachtsaufschlag > 0) parts.push('ERHÖHT VERDACHT');
      return parts.join(' · ');
    };

    // ── Auftragsbrett (NT-AUFTRAGSBRETT-01/03): Sektion AUFTRAGGEBER → Brett ────────
    // Die drei festen NPC-Karten (Hunger/Vertrauen) entfallen ersatzlos (F4/Abschnitt 6/7).
    const auftraggeberStatusHtml = (auftragsbrettData?.auftraggeber ?? []).map((ag) => {
      const cardClass = ag.auftraggeberTyp === 'KNASTKONTAKT' ? 'co-order-card co-order-card--knastkontakt' : 'co-order-card';
      if (!ag.unlockedAt) {
        const progress = ag.unlockProgress;
        const jobLine = progress
          ? `<p class="co-order-note">${esc(progress.requiredJobLabels.join(' / ').toUpperCase())} ${String(progress.currentCompletions)}/${String(progress.requiredCompletions)}× ABGESCHLOSSEN</p>`
          : '';
        // NT-AUFTRAGSBRETT-03 — KNASTKONTAKT hat kein Level-/Job-Kriterium (unlockProgress ist
        // hier immer null), sondern schaltet sich über eine Haftentlassung frei.
        const lockedHint = ag.auftraggeberTyp === 'KNASTKONTAKT'
          ? 'Meldet sich, sobald du das erste Mal im Knast warst.'
          : 'Meldet sich automatisch, sobald erfüllt.';
        return `
          <article class="${cardClass}">
            <p class="co-order-batch">${esc(ag.name.toUpperCase())}</p>
            ${LOCK_SVG}
            <div class="co-order-divider"></div>
            ${progress ? `<p class="co-order-lock">LEVEL ${String(progress.requiredLevel)} ERFORDERLICH</p>` : ''}
            ${jobLine}
            <p class="co-order-note co-order-note--muted">${esc(lockedHint)}</p>
          </article>
        `;
      }
      const cooldownMs = ag.cooldownUntil ? Math.max(0, new Date(ag.cooldownUntil).getTime() - Date.now()) : 0;
      // NT-AUFTRAGSBRETT-03 (K2) — Belohnungs-Bonusfenster nach der letzten Haftentlassung.
      const bonusMs = ag.bonusUntil ? Math.max(0, new Date(ag.bonusUntil).getTime() - Date.now()) : 0;
      return `
        <article class="${cardClass}">
          <p class="co-order-batch">${esc(ag.name.toUpperCase())}</p>
          <p class="co-order-note co-order-note--muted">${esc(formatAuftraggeberProfileLine(ag.profile))}</p>
          <p class="co-order-note">RUF: ${String(ag.completedCount)} ERLEDIGT · STUFE ${String(ag.rufStufe)}</p>
          ${bonusMs > 0 ? `<p class="co-order-note co-order-note--ok">FRISCH RAUS — BESSERE PREISE NOCH <span data-auftraggeber-bonus="${esc(ag.auftraggeberTyp)}">${fmtCountdown(bonusMs)}</span></p>` : ''}
          ${cooldownMs > 0 ? `<p class="co-order-note co-order-note--muted">KOMMT ZURÜCK IN <span data-auftraggeber-returns="${esc(ag.auftraggeberTyp)}">${fmtCountdown(cooldownMs)}</span></p>` : ''}
        </article>
      `;
    }).join('') || '<p class="co-empty">Noch kein Auftraggeber freigeschaltet.</p>';

    const BLOCK_REASON_LABEL: Record<AuftragsbrettBlockReason, string> = {
      KEIN_LAGER_MIT_PLATZ: 'KEIN LAGER MIT PLATZ',
      KEIN_VERSTECK_MIT_PLATZ: 'KEIN VERSTECK MIT PLATZ',
      ZU_WENIG_OBJEKTE: 'ZU WENIG OBJEKTE',
      GLEICHE_STRASSE: 'ALLE OBJEKTE IN DERSELBEN STRASSE',
      KEIN_FREIES_FAHRZEUG: 'KEIN FREIES FAHRZEUG',
    };
    const blockedNoteHtml = (auftragsbrettData?.blocked ?? []).map((b) => {
      // NT-AUFTRAGSBRETT-01B (Q2): auftraggeberTyp ist nie mehr null — immer strikt zugeordnet.
      const prefix = (auftragsbrettData?.auftraggeber.find((a) => a.auftraggeberTyp === b.auftraggeberTyp)?.name ?? b.auftraggeberTyp).toUpperCase();
      const detail = b.neededSlots !== null ? ` — ${String(b.neededSlots)} SLOTS NÖTIG` : '';
      return `<p class="co-order-note co-order-note--muted">${esc(prefix)}: ${esc(BLOCK_REASON_LABEL[b.reason])}${detail}.</p>`;
    }).join('');

    const angeboteHtml = (auftragsbrettData?.angebote ?? []).map((a) => {
      const expiresMs = Math.max(0, new Date(a.expiresAt).getTime() - Date.now());
      const detailHtml = a.auftragTyp === 'TRANSPORT'
        ? `
          <div class="co-run-meta"><span>QUELLE:</span><span>${esc((a.quellePropertyLabel ?? '—').toUpperCase())}</span></div>
          <div class="co-run-meta"><span>ZIEL:</span><span>${esc((a.zielPropertyLabel ?? '—').toUpperCase())}</span></div>
          <p class="co-order-note">${String(a.gebundeneSlots)} SLOTS · ENTFERNUNG ${String(a.steps ?? 0)} STRASSE${a.steps === 1 ? '' : 'N'}</p>
        `
        : `<p class="co-order-note">${String(a.gebundeneSlots)} SLOTS · ${fmtDurationMins(a.stunden * 60)}</p>`;
      return `
        <article class="co-order-card">
          <p class="co-order-batch">${esc(a.auftraggeberName.toUpperCase())}</p>
          <p class="co-order-name">${esc(a.auftragTyp)}</p>
          ${detailHtml}
          <p class="co-order-note">BELOHNUNG: ${fmtReward(a.rewardCents, a.rewardCurrency)}</p>
          <p class="co-order-note co-order-note--muted">LÄUFT AB IN: <span data-angebot-expires="${esc(a.id)}">${fmtCountdown(expiresMs)}</span></p>
          <div class="co-order-actions">
            <button class="co-btn co-btn-primary" data-co-action="open-auftragsbrett-accept" data-angebotid="${esc(a.id)}">ANNEHMEN</button>
          </div>
        </article>
      `;
    }).join('') || '<p class="co-empty">Keine Angebote verfügbar.</p>';

    const auftragsbrettAktiveHtml = (auftragsbrettData?.aktive ?? []).map((a) => {
      if (a.auftragTyp === 'TRANSPORT') {
        return `
          <div class="co-order-active">
            <div class="co-business-header">
              <span class="co-business-name">${esc(a.auftraggeberName.toUpperCase())}</span>
              <span class="co-status-badge aktiv">UNTERWEGS</span>
            </div>
            <p class="co-order-value">${esc(a.itemLabel.toUpperCase())} × ${String(a.gebundeneSlots)} SLOTS</p>
            <div class="co-run-meta"><span>QUELLE:</span><span>${esc(a.propertyLabel.toUpperCase())}</span></div>
            <div class="co-run-meta"><span>ZIEL:</span><span>${esc((a.zielPropertyLabel ?? '—').toUpperCase())}</span></div>
            ${a.transportPhase ? renderAuftragTransportPhaseHtml(a.transportPhase, Date.now(), a.id) : ''}
            <div class="co-run-meta"><span>BELOHNUNG BEI ANKUNFT:</span><span>${fmtReward(a.rewardCents, a.rewardCurrency)}</span></div>
            ${renderAuftragEreignisHtml(a.ereignis, a.id)}
            <div class="co-actions">
              <button class="co-btn" data-co-action="auftragsbrett-abort" data-auftragid="${esc(a.id)}">ABBRECHEN (KEINE BELOHNUNG)</button>
            </div>
          </div>
        `;
      }
      const remainingMs = a.bunkerBisAt ? Math.max(0, new Date(a.bunkerBisAt).getTime() - Date.now()) : 0;
      return `
        <div class="co-order-active">
          <div class="co-business-header">
            <span class="co-business-name">${esc(a.auftraggeberName.toUpperCase())}</span>
            <span class="co-status-badge aktiv">ABHOLUNG IN <span data-auftrag-bunkerbis="${esc(a.id)}">${fmtCountdown(remainingMs)}</span></span>
          </div>
          <p class="co-order-value">${esc(a.itemLabel.toUpperCase())} × ${String(a.gebundeneSlots)} SLOTS</p>
          <div class="co-run-meta"><span>OBJEKT:</span><span>${esc(a.propertyLabel.toUpperCase())}</span></div>
          <div class="co-run-meta"><span>BELOHNUNG BEI ABHOLUNG:</span><span>${fmtReward(a.rewardCents, a.rewardCurrency)}</span></div>
          ${renderAuftragEreignisHtml(a.ereignis, a.id)}
          <div class="co-actions">
            <button class="co-btn" data-co-action="auftragsbrett-abort" data-auftragid="${esc(a.id)}">ABBRECHEN (KEINE BELOHNUNG)</button>
          </div>
        </div>
      `;
    }).join('') || '<p class="co-empty">Keine aktiven Auftragsbrett-Aufträge.</p>';

    const auftragsbrettHistoryHtml = auftragsbrettHistoryData.map((h) => {
      const rewardText = h.status === 'ABGESCHLOSSEN' ? `${fmtReward(h.rewardCents, h.rewardCurrency)} erhalten` : 'abgebrochen';
      const timeRef = h.completedAt ?? h.acceptedAt;
      // NT-AUFTRAGSBRETT-05 — dauerhafte Sichtbarkeit nach Auflösung (Ersatz für ein nicht
      // existierendes "Event-Log", s. Schritt-0-Bericht Punkt 4).
      const ereignisLine = h.ereignis?.ergebnis
        ? `<p class="co-order-note co-order-note--muted">${esc(h.ereignis.label.toUpperCase())}: ${esc(h.ereignis.ergebnis)}</p>`
        : '';
      return `
        <div class="co-order-history-item ${h.status === 'ABGEBROCHEN' ? 'co-order-history-item--muted' : ''}">
          <span>[${esc(h.status)}] ${esc(h.auftraggeberName.toUpperCase())} - ${String(h.gebundeneSlots)} SLOTS</span>
          <span>${rewardText} - ${formatAgo(timeRef)}</span>
          <span>OBJEKT: ${esc(h.propertyLabel.toUpperCase())}</span>
          ${ereignisLine}
        </div>
      `;
    }).join('') || '<p class="co-empty">Keine Auftragsbrett-Historie.</p>';

    const activeHtml = runningOrders.map((order) => {
      const progressPct = Math.min(100, Math.floor((order.deliveredQty / Math.max(1, order.totalQty)) * 100));
      const matchingVehicles = vehicleList.filter((vehicle) => {
        const inventory = orderVehicleInventoryById.get(vehicle.id);
        if (!inventory) return false;
        return getOrderVehicleStock(inventory, order) > 0;
      });
      const hasWarehouseStock = order.warehouseStock > 0;
      // runningOrders enthält nur noch angenommene Aufträge (status !== OPEN) — "ERST ANNEHMEN"
      // kann hier nicht mehr vorkommen.
      const canDeliver = matchingVehicles.length > 0 || hasWarehouseStock;

      return `
        <div class="co-order-active">
          <div class="co-business-header">
            <span class="co-business-name">${esc(order.npcType)} - ${esc(order.npcName.toUpperCase())}</span>
            <span class="co-status-badge aktiv">LÄUFT AB <span data-order-expires="${esc(order.id)}">${fmtCountdown(Math.max(0, new Date(order.expiresAt).getTime() - Date.now()))}</span></span>
          </div>
          <p class="co-order-value">${String(order.totalQty)}x ${esc(order.itemLabel.toUpperCase())}</p>
          <div class="co-order-progress">
            <div class="co-progress-track"><div class="co-progress-fill" style="--co-progress:${String(progressPct)}%"></div></div>
            <div class="co-run-meta"><span>${String(order.deliveredQty)} / ${String(order.totalQty)} geliefert</span><span>REST ${String(order.remainingQty)}</span></div>
          </div>
          <div class="co-run-meta"><span>GESAMTBELOHNUNG:</span><span>${fmtEur(order.rewardNoxCents)}${order.rewardBlackMoneyCents !== '0' ? ` + ${fmtSeur(order.rewardBlackMoneyCents)}` : ''}</span></div>
          <div class="co-run-meta"><span>PRO EINHEIT:</span><span>${fmtEur(order.rewardPerUnitCents)}</span></div>
          <div class="co-run-meta"><span>BEREITS VERDIENT:</span><span>${fmtEur(order.alreadyEarnedCents)}</span></div>
          <div class="co-actions">
            <button class="co-btn co-btn-primary" data-co-action="deliver-order" data-orderid="${esc(order.id)}" ${canDeliver ? '' : 'disabled'}>LIEFERN</button>
          </div>
          ${canDeliver
            ? ''
            : `<p class="co-order-hint">Kein ${esc(order.itemLabel.toUpperCase())} im Fahrzeug oder Lager.</p>`}
        </div>
      `;
    }).join('') || '<p class="co-empty">Keine aktiven Aufträge.</p>';

    const historyHtml = (ordersData?.history ?? []).map((order) => {
      const muted = order.status === 'EXPIRED' || order.status === 'FAILED';
      const rewardText = order.status === 'DONE'
        ? `${fmtEur(order.rewardNoxCents)} erhalten`
        : 'abgelaufen';
      const timeRef = order.completedAt ?? order.expiresAt;
      return `
        <div class="co-order-history-item ${muted ? 'co-order-history-item--muted' : ''}">
          <span>[${esc(order.status)}] ${esc(order.npcName.toUpperCase())} - ${String(order.deliveredQty)}/${String(order.totalQty)} ${esc(order.itemLabel.toUpperCase())}</span>
          <span>${rewardText} - ${formatAgo(timeRef)}</span>
        </div>
      `;
    }).join('') || '<p class="co-empty">Keine Historie.</p>';

    const visibleMissionContacts = missionContactsData.filter((contact) => !isMissionDismissed(contact.id));

    const missionCardsHtml = visibleMissionContacts.map((contact) => {
      const remainingMs = Math.max(0, new Date(contact.availableUntil).getTime() - Date.now());
      const isCritical = remainingMs <= 60 * 60 * 1000;
      const mission = contact.mission;
      const missionStatus = mission?.status ?? 'EXPIRED';
      const canStart = missionStatus === 'AVAILABLE';
      const canOpen = missionStatus === 'RUNNING';

      return `
        <article class="co-order-card co-order-card--hehler">
          <p class="co-order-batch">${esc(contact.batch.toUpperCase())}</p>
          <p class="co-order-name">${esc(contact.name.toUpperCase())}</p>
          <p class="co-order-note ${isCritical ? 'co-order-note--danger' : ''}">VERFÜGBAR: <span data-mission-contact-remaining="${esc(contact.id)}">${fmtCountdown(remainingMs)}</span></p>
          ${mission ? `
            <p class="co-order-label">MISSION</p>
            <p class="co-order-value">${esc(mission.label.toUpperCase())}</p>
            <p class="co-order-note">${esc(mission.description)}</p>
            <p class="co-order-note">BASIS: ${fmtSeur(mission.baseRewardBlackMoneyCents)} · RISIKO: ${getMissionRiskLabel(mission.riskBase)} (${String(Math.round(mission.riskBase * 100))}%)</p>
            <div class="co-order-actions">
              ${canStart ? `<button class="co-btn co-btn-primary" data-co-action="mission-start" data-contactid="${esc(contact.id)}">MISSION STARTEN</button>` : ''}
              ${canOpen ? `<button class="co-btn co-btn-primary" data-co-action="mission-open" data-missionid="${esc(mission.id)}">MISSION ÖFFNEN</button>` : ''}
              <button class="co-btn" data-co-action="mission-dismiss" data-contactid="${esc(contact.id)}" data-availableuntil="${esc(contact.availableUntil)}">ABLEHNEN</button>
              ${!canStart && !canOpen ? `<button class="co-btn" disabled>${esc(missionStatus)}</button>` : ''}
            </div>
          ` : '<p class="co-order-note">Keine Mission verfügbar.</p>'}
        </article>
      `;
    }).join('') || '<p class="co-empty">Keine Knast-Kontakte verfügbar.</p>';

    const missionActiveHtml = missionActiveState
      ? `
        <div class="co-order-active">
          <div class="co-business-header">
            <span class="co-business-name">${esc(missionActiveState.missionType.toUpperCase())} - ${esc(missionActiveState.status)}</span>
            <span class="co-status-badge aktiv">RESTZEIT <span data-mission-running-remaining="${esc(missionActiveState.id)}">${fmtCountdown(Math.max(0, missionActiveState.remainingSeconds) * 1000)}</span></span>
          </div>
          <div class="co-run-meta"><span>RISIKO</span><span>${String(Math.round(missionActiveState.riskLevel * 100))}%</span></div>
          <div class="co-order-track"><div class="co-order-fill ${missionActiveState.riskLevel > 0.7 ? 'co-order-fill--hunger-low' : 'co-order-fill--trust'}" style="--co-progress:${String(Math.round(missionActiveState.riskLevel * 100))}%"></div></div>
          <div class="co-run-meta"><span>BELOHNUNG</span><span>${fmtSeur(missionActiveState.rewardBlackMoneyCents)}</span></div>
          <div class="co-actions">
            <button class="co-btn co-btn-primary" data-co-action="mission-open" data-missionid="${esc(missionActiveState.id)}">MISSION ANZEIGEN</button>
          </div>
        </div>
      `
      : '<p class="co-empty">Keine aktive Mission.</p>';

    const missionHistoryHtml = missionHistoryData.map((mission) => {
      const when = mission.completedAt ?? mission.startedAt ?? new Date().toISOString();
      return `
        <div class="co-order-history-item ${mission.status === 'COMPLETED' ? '' : 'co-order-history-item--muted'}">
          <span>[${esc(mission.status)}] ${esc(mission.missionType.toUpperCase())}</span>
          <span>${fmtSeur(mission.rewardBlackMoneyCents)} - ${formatAgo(when)}</span>
        </div>
      `;
    }).join('') || '<p class="co-empty">Keine Missions-Historie.</p>';

    const knastkontaktHtml = renderCellmateJobSection();

    return `
      <div class="co-business">
        <div class="co-business-header"><span class="co-business-name">AUFTRAGSBRETT</span></div>
        <div class="co-order-grid">${auftraggeberStatusHtml}</div>
        ${blockedNoteHtml}
        <div class="co-order-grid">${angeboteHtml}</div>
      </div>
      <div class="co-business">
        <div class="co-business-header"><span class="co-business-name">KNAST-KONTAKTE</span></div>
        <div class="co-order-grid">${missionCardsHtml}</div>
      </div>
      <div class="co-business co-business--cellmate">
        <div class="co-business-header"><span class="co-business-name">ZELLENGENOSSE</span></div>
        ${knastkontaktHtml}
      </div>
      ${renderVehicleControls(false)}
      <div class="co-business"><div class="co-business-header"><span class="co-business-name">AUFTRAGSBRETT — AKTIVE AUFTRÄGE</span></div>${auftragsbrettAktiveHtml}</div>
      <div class="co-business"><div class="co-business-header"><span class="co-business-name">AKTIVE AUFTRÄGE (ALT)</span></div>${activeHtml}</div>
      <div class="co-business"><div class="co-business-header"><span class="co-business-name">AKTIVE MISSION</span></div>${missionActiveHtml}</div>
      <div class="co-business"><div class="co-business-header"><span class="co-business-name">AUFTRAGSBRETT — HISTORIE</span></div>${auftragsbrettHistoryHtml}</div>
      <div class="co-business"><div class="co-business-header"><span class="co-business-name">AUFTRAGS-HISTORIE (ALT)</span></div>${historyHtml}</div>
      <div class="co-business"><div class="co-business-header"><span class="co-business-name">MISSIONS-HISTORIE</span></div>${missionHistoryHtml}</div>
      ${renderAuftragsbrettAcceptModal()}
    `;
  }

  switch (selectedSection) {
    case 'geldwaesche':   return renderGeldwaescheContent(status.geldwaesche);
    case 'auftraege':     return renderOrdersContent();
    case 'strassenhandel': return renderStreetMarketTab();
    case 'labor':         return status.labor.unlocked      ? renderLaborTab()          : renderLockedContent('LABOR', status.labor, LOCKED_SECTION_INTRO.labor);
    case 'dealer':        return status.dealer.unlocked     ? renderDealerContent()     : renderLockedContent('DEALER', status.dealer);
    case 'schmuggel':     return status.schmuggel.unlocked  ? renderSchmuggelContent()  : renderLockedContent('SCHMUGGEL', status.schmuggel);
    case 'gewächshaus':   return status.gewächshaus.unlocked ? renderGewaechshausTab() : renderLockedContent('GEWÄCHSHAUS', status.gewächshaus, LOCKED_SECTION_INTRO['gewächshaus']);
    case 'rolltisch':     return renderRolltischTab();
    case 'schwarzmarkt':  return renderSchwarzmarktTab();
    case 'immobilien':    return renderImmobilienTab();
    case 'viertel':       return renderViertelTab();
    case 'kontakte':      return renderKontakteContent(status.kontakte);
    case 'darknet':       return renderDarknetTab();
    default:              return '';
  }
}

// ─── Event-Handling ───────────────────────────────────────────────────────────

function attachListeners(): void {
  const root = document.getElementById('co-root');
  if (!root) return;

  // Slider live-preview
  root.addEventListener('input', (e) => {
    const slider = (e.target as Element).closest<HTMLInputElement>('[data-co-wash-slider]');
    if (!slider) return;
    const bid    = slider.dataset.coWashSlider ?? '';
    const amount = parseInt(slider.value, 10);
    const b      = crimeOpsData?.geldwaesche.businesses.find((x) => x.id === bid);
    if (!b) return;

    const amountEl  = document.getElementById(`co-wash-amount-${bid}`);
    const previewEl = document.getElementById(`co-wash-preview-${bid}`);
    const submitBtn = root.querySelector<HTMLButtonElement>(`[data-co-action="submit-wash"][data-bid="${CSS.escape(bid)}"]`);

    if (amountEl)  amountEl.textContent = fmtSeur(String(amount));
    if (previewEl) previewEl.innerHTML  = renderWashPreviewHtml(amount, b.effective.lossPercent, parseInt(b.effective.maxBatchCents, 10), b.effective.durationMinutes);
    if (submitBtn) submitBtn.dataset.amount = String(amount);
  });

  root.addEventListener('click', (e) => {
    const target = e.target as Element;

    // Kategorie-Accordion toggle (Mobile) — exklusiv: nur eine Kategorie gleichzeitig offen
    const categoryToggleBtn = target.closest<HTMLButtonElement>('[data-co-toggle-category]');
    if (categoryToggleBtn !== null) {
      const catId = categoryToggleBtn.dataset.coToggleCategory ?? '';
      if (catId) {
        const willOpen = !expandedCategories.has(catId);
        expandedCategories.clear();
        if (willOpen) expandedCategories.add(catId);
        root.querySelectorAll<HTMLElement>('[data-category]').forEach((catEl) => {
          const id = catEl.dataset.category ?? '';
          const isOpen = expandedCategories.has(id);
          catEl.classList.toggle('co-category--open', isOpen);
          const toggleSpan = catEl.querySelector('.co-category-toggle');
          if (toggleSpan) toggleSpan.textContent = isOpen ? '[-]' : '[+]';
        });
      }
      return;
    }

    // Sektion wechseln
    const tabBtn = target.closest<HTMLButtonElement>('[data-co-section]');
    if (tabBtn !== null) {
      const section = tabBtn.dataset.coSection as Section | undefined;
      if (section !== undefined && section !== selectedSection) {
        const previousSection = selectedSection;
        if (previousSection === 'immobilien') {
          cleanupImmobilienTab();
          isImmobilienTabMounted = false;
        }
        if (previousSection === 'viertel') {
          cleanupViertelTab();
          isViertelTabMounted = false;
        }
        if (previousSection === 'gewächshaus') {
          cleanupGewaechshausTab();
          isGewaechshausTabMounted = false;
        }
        if (previousSection === 'rolltisch') {
          cleanupRolltischTab();
          isRolltischTabMounted = false;
        }
        if (previousSection === 'strassenhandel') {
          cleanupStreetMarketTab();
          isStreetMarketTabMounted = false;
        }
        if (previousSection === 'labor') {
          cleanupLaborTab();
          isLaborTabMounted = false;
        }
        if (previousSection === 'darknet') {
          cleanupDarknetTab();
          isDarknetTabMounted = false;
        }
        if (previousSection === 'schwarzmarkt') {
          cleanupSchwarzmarktTab();
          isSchwarzmarktTabMounted = false;
        }
        selectedSection = section;
        sessionStorage.setItem('crimeops_section', section);
        syncCrimeOpsUrl(section);
        // Sektion-Daten zurücksetzen → Lade-State zeigen
        laborData = null; dealerData = null; schmuggelData = null;
        dealerTalentTree = null; dealerVehicleOptions = []; dealerFleeingModalDealerId = null; dealerVehiclePanelState.clear(); dealerTrainingConfirmId = null;
        dealerSupplyModal = null; dealerSupplyProperties = [];
        ordersData = null;
        npcMessagesData = [];
        missionContactsData = []; missionHistoryData = []; missionActiveState = null; missionModalMissionId = null; cellmateJobData = null;
        auftragsbrettData = null; auftragsbrettHistoryData = []; auftragsbrettFreeProperties = []; auftragsbrettAcceptModal = null;
        gaertnerShopData = null; gaertnerCart.clear();
        alchemistShopData = null; alchemistCart.clear();
        renderCurrentSection();
        root.querySelectorAll<HTMLButtonElement>('[data-co-section]').forEach((btn) => {
          btn.classList.toggle('is-active', btn.dataset.coSection === section);
        });
        // Mobile: Tab-Liste + aufgeklappte Kategorie nehmen viel Platz ein — Inhalt der
        // neuen Sektion in den sichtbaren Bereich scrollen, statt manuelles Scrollen zu verlangen.
        if (window.matchMedia(MOBILE_LAYOUT_QUERY).matches) {
          document.querySelector('.co-content-area')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        // Daten für neue Sektion laden
        void reloadSectionData();
      }
      return;
    }

    // Aktions-Buttons
    const actionBtn = target.closest<HTMLElement>('[data-co-action]');
    if (!actionBtn || actionBtn.hasAttribute('disabled')) return;
    switch (actionBtn.dataset.coAction) {
      case 'collect':
        void handleCollect(actionBtn.dataset.runid ?? '', actionBtn.dataset.bid ?? '');
        break;
      case 'start-wash': {
        const bid = actionBtn.dataset.bid ?? '';
        expandedWashBid = expandedWashBid === bid ? null : bid;
        renderCurrentSection();
        break;
      }
      case 'cancel-wash':
        expandedWashBid = null;
        renderCurrentSection();
        break;
      case 'submit-wash':
        void handleStartWash(
          actionBtn.dataset.bid ?? '',
          parseInt(actionBtn.dataset.amount ?? '0', 10),
        );
        break;
      case 'go-geschaefte':
        navigate('/immobilien/geschaefte');
        break;

      // ── Labor ────────────────────────────────────────────────────────
      case 'start-batch': {
        const rk   = actionBtn.dataset.recipe ?? '';
        const labs = laborData?.labs ?? [];
        if (labs.length === 0) { showToast('Kein Labor vorhanden.', 'error'); break; }
        const lab  = labs.find((l) => l.batches < l.maxBatches) ?? labs[0];
        if (!lab) { showToast('Alle Batch-Slots belegt.', 'error'); break; }
        void handleStartBatch(rk, lab.id);
        break;
      }
      case 'quality-check':
        void handleQualityCheck(actionBtn.dataset.batchid ?? '');
        break;
      case 'collect-batch':
        void handleCollectBatch(actionBtn.dataset.batchid ?? '');
        break;

      // ── Dealer ───────────────────────────────────────────────────────
      case 'hire-dealer':
        void handleHireDealer(actionBtn.dataset.npckey ?? '');
        break;
      case 'pause-dealer':
        void handlePauseDealer(actionBtn.dataset.dealerid ?? '');
        break;
      case 'collect-dealer':
        void handleCollectDealer(actionBtn.dataset.dealerid ?? '');
        break;
      case 'train-dealer':
        // NT-POLISH-01 (P5): erst Bestätigungsdialog mit Kosten + Dauer, nie direkt starten.
        dealerTrainingConfirmId = actionBtn.dataset.dealerid ?? null;
        renderCurrentSection();
        break;
      case 'close-train-dealer':
        dealerTrainingConfirmId = null;
        renderCurrentSection();
        break;
      case 'confirm-train-dealer': {
        const id = dealerTrainingConfirmId;
        dealerTrainingConfirmId = null;
        if (id) void handleTrainDealer(id);
        else renderCurrentSection();
        break;
      }
      case 'bail-dealer':
        void handleBailDealer(actionBtn.dataset.dealerid ?? '');
        break;
      case 'bail-vehicle':
        void handleBailVehicle(actionBtn.dataset.vehicleId ?? '');
        break;
      case 'assign-dealer-vehicle': {
        const rawVehicleId = actionBtn.dataset.vehicleId ?? '';
        const vehicleIdToSend = rawVehicleId === '' || rawVehicleId === '__release__' ? null : rawVehicleId;
        void handleAssignDealerVehicle(actionBtn.dataset.dealerid ?? '', vehicleIdToSend);
        break;
      }
      case 'skip-dealer-supply': {
        const dealerId = actionBtn.dataset.dealerid ?? '';
        const deliveryId = actionBtn.dataset.deliveryId ?? '';
        if (dealerId && deliveryId) void handleSkipDealerSupply(dealerId, deliveryId);
        break;
      }
      case 'toggle-dealer-vehicle-panel': {
        const dealerId = actionBtn.dataset.dealerid ?? '';
        if (!dealerId) break;
        const dealer = dealerData?.dealers.find((entry) => entry.id === dealerId) ?? null;
        const current = dealer !== null ? isDealerVehiclePanelOpen(dealer) : false;
        dealerVehiclePanelState.set(dealerId, !current);
        renderCurrentSection();
        break;
      }
      case 'toggle-dealer-inventory-panel': {
        const dealerId = actionBtn.dataset.dealerid ?? '';
        if (!dealerId) break;
        if (dealerInventoryPanelState.has(dealerId)) {
          dealerInventoryPanelState.delete(dealerId);
        } else {
          dealerInventoryPanelState.add(dealerId);
        }
        renderCurrentSection();
        break;
      }
      case 'resolve-fleeing':
        void handleResolveDealerFleeing(actionBtn.dataset.dealerid ?? '', actionBtn.dataset.fleeAction ?? 'ABANDON');
        break;
      case 'open-dealer-supply':
        void handleOpenDealerSupply(actionBtn.dataset.dealerid ?? '');
        break;
      case 'close-dealer-supply':
        dealerSupplyModal = null;
        renderCurrentSection();
        break;
      case 'confirm-dealer-supply':
        void handleConfirmDealerSupply();
        break;

      // ── Schmuggel ────────────────────────────────────────────────────
      case 'start-schmuggel':
        showToast('Schmuggelstart: Fahrzeug + Ware im Lagerhaus auswählen.', 'info');
        break;

      // ── Schmuggel-Run (Checkpoint-System) ────────────────────────────
      case 'select-smuggle-item':
        smuggleRunSelectedItem = actionBtn.dataset.itemKey ?? '';
        renderCurrentSection();
        break;
      case 'start-smuggle-run':
        void handleStartSmuggleRun();
        break;
      case 'decide-checkpoint': {
        const runId        = actionBtn.dataset.runId ?? '';
        const checkpointId = actionBtn.dataset.checkpointId ?? '';
        const decision     = actionBtn.dataset.decision as 'A' | 'B' | 'C' | undefined;
        if (runId && checkpointId && decision) void handleSmuggleCheckpoint(runId, checkpointId, decision);
        break;
      }

      // ── Aufträge (Phase 2 Erweiterung) ───────────────────────────────────
      case 'npc-message-accept':
        void handleAcceptNpcMessage(actionBtn.dataset.messageid ?? '');
        break;
      case 'npc-message-ignore':
        void handleIgnoreNpcMessage(actionBtn.dataset.messageid ?? '');
        break;
      case 'deliver-order':
        void openOrderDeliveryModal(actionBtn.dataset.orderid ?? '');
        break;
      case 'order-modal-qty-down':
        orderDeliveryModalQty = Math.max(1, orderDeliveryModalQty - 1);
        renderCurrentSection();
        break;
      case 'order-modal-qty-up':
        orderDeliveryModalQty += 1;
        renderCurrentSection();
        break;
      case 'close-order-delivery-modal':
        closeOrderDeliveryModal();
        break;
      case 'confirm-order-delivery':
        void handleDeliverOrder(actionBtn.dataset.orderid ?? '', orderDeliveryModalVehicleId, orderDeliveryModalQty);
        break;

      // ── Auftragsbrett (NT-AUFTRAGSBRETT-01/02) ───────────────────────────────
      case 'open-auftragsbrett-accept':
        void handleOpenAuftragsbrettAccept(actionBtn.dataset.angebotid ?? '');
        break;
      case 'close-auftragsbrett-accept':
        handleCloseAuftragsbrettAccept();
        break;
      case 'confirm-auftragsbrett-accept':
        void handleConfirmAuftragsbrettAccept();
        break;
      case 'auftragsbrett-abort':
        void handleAuftragsbrettAbort(actionBtn.dataset.auftragid ?? '');
        break;
      case 'auftragsbrett-ereignis-entscheiden':
        void handleAuftragsbrettEreignisEntscheiden(
          actionBtn.dataset.auftragid ?? '',
          (actionBtn.dataset.entscheidung ?? 'STANDARD') as AuftragEreignisEntscheidungView,
        );
        break;

      case 'mission-start':
        void handleStartMission(actionBtn.dataset.contactid ?? '');
        break;
      case 'mission-open':
        void handleOpenMission(actionBtn.dataset.missionid ?? '');
        break;
      case 'mission-dismiss':
        handleMissionDismiss(actionBtn.dataset.contactid ?? '', actionBtn.dataset.availableuntil ?? '');
        break;
      // ── Knastkontakt (Zellengenosse) ──────────────────────────────────────
      case 'cellmate-accept':
        void handleCellmateAccept(actionBtn.dataset.jobid ?? '');
        break;
      case 'cellmate-complete':
        void handleCellmateComplete(actionBtn.dataset.jobid ?? '');
        break;
      case 'cellmate-reject':
        void handleCellmateReject(actionBtn.dataset.jobid ?? '');
        break;

      // ── Straßenhandel ─────────────────────────────────────────────────────
      case 'trade-qty-down':
        tradeSellQty = Math.max(1, tradeSellQty - 1);
        renderCurrentSection();
        break;
      case 'trade-qty-up':
        tradeSellQty = Math.min(999, tradeSellQty + 1);
        renderCurrentSection();
        break;
      case 'trade-sell':
        void handleTradeSell();
        break;

      // ── Immobilien ───────────────────────────────────────────────────────
      case 'enable-garden': {
        const propId = actionBtn.dataset.propid ?? '';
        actionBtn.setAttribute('disabled', 'true');
        actionBtn.textContent = '...';
        api.post<unknown>(`/properties/${encodeURIComponent(propId)}/enable-garden`, {})
          .then(() => {
            showToast('Garten aktiviert!', 'info');
            void reloadSectionData();
          })
          .catch((err: unknown) => {
            actionBtn.removeAttribute('disabled');
            actionBtn.textContent = 'ALS GARTEN';
            showToast(err instanceof ApiError ? err.message : 'Fehler.', 'error');
          });
        break;
      }
      case 'disable-garden': {
        const propId = actionBtn.dataset.propid ?? '';
        actionBtn.setAttribute('disabled', 'true');
        actionBtn.textContent = '...';
        api.post<unknown>(`/properties/${encodeURIComponent(propId)}/disable-garden`, {})
          .then(() => {
            showToast('Garten deaktiviert.', 'info');
            void reloadSectionData();
          })
          .catch((err: unknown) => {
            actionBtn.removeAttribute('disabled');
            actionBtn.textContent = 'GARTEN ENTFERNEN';
            showToast(err instanceof ApiError ? err.message : 'Fehler.', 'error');
          });
        break;
      }
      case 'goto-verstecke':
        navigate('/immobilien/verstecke');
        break;
      case 'goto-lager':
        navigate('/immobilien/lager');
        break;

      // ── Der Gärtner ───────────────────────────────────────────────────────
      case 'load-gaertner-shop':
        void handleLoadGaertnerShop();
        break;

      case 'gaertner-cart-add': {
        const strain = actionBtn.dataset.strain ?? '';
        if (strain && gaertnerShopData) {
          const maxOrd = gaertnerShopData.maxPerOrder;
          const cur    = gaertnerCart.get(strain) ?? 0;
          if (cur < maxOrd) gaertnerCart.set(strain, cur + 1);
          renderCurrentSection();
        }
        break;
      }

      case 'gaertner-cart-remove': {
        const strain = actionBtn.dataset.strain ?? '';
        gaertnerCart.delete(strain);
        renderCurrentSection();
        break;
      }

      case 'gaertner-cart-qty': {
        const strain = actionBtn.dataset.strain ?? '';
        const delta  = parseInt(actionBtn.dataset.delta ?? '0', 10);
        if (strain && gaertnerShopData) {
          const maxOrd = gaertnerShopData.maxPerOrder;
          const cur    = gaertnerCart.get(strain) ?? 0;
          const next   = Math.max(1, Math.min(maxOrd, cur + delta));
          gaertnerCart.set(strain, next);
          renderCurrentSection();
        }
        break;
      }

      case 'gaertner-buy':
        if (gaertnerCart.size > 0) {
          void handleGaertnerOrder();
        }
        break;

      // ── Der Alchemist ─────────────────────────────────────────────────────
      case 'load-alchemist-shop':
        void handleLoadAlchemistShop();
        break;

      case 'alchemist-cart-add': {
        const itemKey = actionBtn.dataset.item ?? '';
        if (itemKey && alchemistShopData) {
          const maxOrd = alchemistShopData.maxPerOrder;
          const curQty = alchemistCart.get(itemKey) ?? 0;
          if (curQty < maxOrd) alchemistCart.set(itemKey, curQty + 1);
          renderCurrentSection();
        }
        break;
      }

      case 'alchemist-cart-remove': {
        const itemKey = actionBtn.dataset.item ?? '';
        alchemistCart.delete(itemKey);
        renderCurrentSection();
        break;
      }

      case 'alchemist-cart-qty': {
        const itemKey = actionBtn.dataset.item ?? '';
        const delta   = parseInt(actionBtn.dataset.delta ?? '0', 10);
        if (itemKey && alchemistShopData) {
          const maxOrd = alchemistShopData.maxPerOrder;
          const curQty = alchemistCart.get(itemKey) ?? 0;
          const next   = Math.max(1, Math.min(maxOrd, curQty + delta));
          alchemistCart.set(itemKey, next);
          renderCurrentSection();
        }
        break;
      }

      case 'alchemist-buy':
        if (alchemistCart.size > 0) {
          void handleAlchemistOrder();
        }
        break;

    }
  });

  // Diverse Formular-Change-Events (Aufträge, Fahrzeuge, Kontakte, Dealer, Schmuggel)
  root.addEventListener('change', (e) => {
    const target = e.target as Element;
    const ordersNpcSel = target.closest<HTMLSelectElement>('[data-orders-npc]');
    if (ordersNpcSel) {
      selectedOrderNpcType = ordersNpcSel.value as 'HEHLER' | 'SCHWARZMARKT' | 'SCHROTTHAENDLER';
      ordersData = null;
      void reloadSectionData();
      return;
    }

    const opsVehicleSel = target.closest<HTMLSelectElement>('[data-ops-vehicle]');
    if (opsVehicleSel) {
      selectedVehicleId = opsVehicleSel.value;
      selectedVehicleInventory = null;
      void reloadSectionData();
      return;
    }

    const opsSlotSel = target.closest<HTMLSelectElement>('[data-ops-slot]');
    if (opsSlotSel) {
      selectedVehicleSlotIndex = parseInt(opsSlotSel.value, 10);
      if (selectedSection === 'strassenhandel') {
        void reloadSectionData();
      } else {
        renderCurrentSection();
      }
      return;
    }

    const orderModalVehicleSel = target.closest<HTMLSelectElement>('[data-order-modal-vehicle]');
    if (orderModalVehicleSel) {
      orderDeliveryModalVehicleId = orderModalVehicleSel.value;
      renderCurrentSection();
      return;
    }

    const orderModalQtyInput = target.closest<HTMLInputElement>('[data-order-modal-qty]');
    if (orderModalQtyInput) {
      const parsed = parseInt(orderModalQtyInput.value, 10);
      orderDeliveryModalQty = Number.isFinite(parsed) ? parsed : 1;
      renderCurrentSection();
      return;
    }

    const contactTargetSel = target.closest<HTMLSelectElement>('[data-contact-target]');
    if (contactTargetSel) {
      propsDataForGaertner = contactTargetSel.value;
      return;
    }

    const dealerVehicleAssignSel = target.closest<HTMLSelectElement>('[data-dealer-vehicle-assign]');
    if (dealerVehicleAssignSel) {
      const dealerId = dealerVehicleAssignSel.dataset.dealerVehicleAssign ?? '';
      if (dealerId) {
        const selectedVehicleId = dealerVehicleAssignSel.value;
        void handleAssignDealerVehicle(dealerId, selectedVehicleId.length > 0 ? selectedVehicleId : null);
      }
      return;
    }

    const smuggleVehicleSel = target.closest<HTMLSelectElement>('[data-co-action="select-smuggle-vehicle"]');
    if (smuggleVehicleSel) {
      smuggleRunSelectedVehicle = smuggleVehicleSel.value;
      renderCurrentSection();
      return;
    }

    const smuggleQtyInput = target.closest<HTMLInputElement>('[data-co-action="set-smuggle-qty"]');
    if (smuggleQtyInput) {
      const parsed = parseInt(smuggleQtyInput.value, 10);
      smuggleRunSelectedQty = Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
      return;
    }

    // Supply-Modal: direkte Mengeneingabe — Klemmung [0, verfügbar], leer/nicht-numerisch = 0
    const supplyQtyInputEl = target.closest<HTMLInputElement>('[data-supply-qty-input]');
    if (supplyQtyInputEl && dealerSupplyModal) {
      const itemKey = supplyQtyInputEl.dataset.supplyQtyInput ?? '';
      const item = dealerSupplyModal.items.find((i) => i.itemKey === itemKey);
      if (item) {
        const raw = parseInt(supplyQtyInputEl.value, 10);
        item.qty = Number.isFinite(raw) ? Math.max(0, Math.min(item.available, raw)) : 0;
        dealerSupplyModal = { ...dealerSupplyModal, errorMsg: null };
        updateSupplyModalDerivedDom();
      }
      return;
    }

    // Quelllager-Wechsel ändert legitim den Item-Bestand — nur der Item-Listen-Container wird
    // neu aufgebaut, Modal-Chrome (Titel/Schritt-Labels/Footer) bleibt unangetastet.
    if ((target as HTMLElement).id === 'co-supply-source' && dealerSupplyModal) {
      const newSourceId = (target as HTMLSelectElement).value;
      const sourceProp = dealerSupplyProperties.find((p) => p.id === newSourceId);
      dealerSupplyModal = {
        ...dealerSupplyModal,
        sourcePropertyId: newSourceId,
        items: buildSupplyItems(sourceProp),
        errorMsg: null,
      };
      refreshSupplyItemsList();
      return;
    }

    // Fahrzeugwechsel ändert nur die Kapazitäts-Ableitung (Item-Liste bleibt gleich).
    if ((target as HTMLElement).id === 'co-supply-vehicle' && dealerSupplyModal) {
      dealerSupplyModal = { ...dealerSupplyModal, vehicleKey: (target as HTMLSelectElement).value, errorMsg: null };
      updateSupplyModalDerivedDom();
      return;
    }

    if ((target as HTMLElement).id === 'co-auftragsbrett-property' && auftragsbrettAcceptModal) {
      auftragsbrettAcceptModal = { ...auftragsbrettAcceptModal, propertyId: (target as HTMLSelectElement).value, errorMsg: null };
      return;
    }

    if ((target as HTMLElement).id === 'co-auftragsbrett-vehicle' && auftragsbrettAcceptModal) {
      auftragsbrettAcceptModal = { ...auftragsbrettAcceptModal, vehicleKey: (target as HTMLSelectElement).value, errorMsg: null };
      scheduleAuftragsbrettDurationPreview();
      return;
    }
  });

  root.addEventListener('click', (e2) => {
    const tgt = e2.target as Element;

    // ── Supply-Modal Qty-Buttons ──────────────────────────────────────────────
    const supplyQtyIncBtn = tgt.closest<HTMLButtonElement>('[data-supply-qty-inc]');
    if (supplyQtyIncBtn && dealerSupplyModal) {
      const itemKey = supplyQtyIncBtn.dataset.supplyQtyInc ?? '';
      const item = dealerSupplyModal.items.find((i) => i.itemKey === itemKey);
      if (item && item.qty < item.available) {
        item.qty += 1;
        dealerSupplyModal = { ...dealerSupplyModal, errorMsg: null };
        updateSupplyModalDerivedDom();
      }
      return;
    }
    const supplyQtyDecBtn = tgt.closest<HTMLButtonElement>('[data-supply-qty-dec]');
    if (supplyQtyDecBtn && dealerSupplyModal) {
      const itemKey = supplyQtyDecBtn.dataset.supplyQtyDec ?? '';
      const item = dealerSupplyModal.items.find((i) => i.itemKey === itemKey);
      if (item && item.qty > 1) {
        item.qty -= 1;
        dealerSupplyModal = { ...dealerSupplyModal, errorMsg: null };
        updateSupplyModalDerivedDom();
      }
      return;
    }

    const supplyQtyMaxBtn = tgt.closest<HTMLButtonElement>('[data-supply-qty-max]');
    if (supplyQtyMaxBtn && dealerSupplyModal) {
      const sm = dealerSupplyModal;
      const itemKey = supplyQtyMaxBtn.dataset.supplyQtyMax ?? '';
      const item = sm.items.find((i) => i.itemKey === itemKey);
      if (item) {
        const selectedVehicle = dealerVehicleOptions.find((v) => v.vehicleKey === sm.vehicleKey);
        const cap = selectedVehicle?.capacity ?? 0;
        const otherQty = sm.items.filter((i) => i.selected && i.itemKey !== itemKey).reduce((s, i) => s + i.qty, 0);
        const maxFromVehicle = cap > 0 ? cap - otherQty : item.available;
        const maxFromSlots = sm.freeSlots - otherQty;
        const itemMax = Math.max(1, Math.min(item.available, maxFromVehicle, maxFromSlots));
        item.selected = true;
        item.qty = itemMax;
        dealerSupplyModal = { ...dealerSupplyModal, errorMsg: null };
        updateSupplyModalDerivedDom();
      }
      return;
    }

    // ── Supply-Modal Checkboxen ───────────────────────────────────────────────
    const supplyCheckbox = tgt.closest<HTMLInputElement>('[data-supply-item-check]');
    if (supplyCheckbox && dealerSupplyModal) {
      const itemKey = supplyCheckbox.dataset.supplyItemCheck ?? '';
      const item = dealerSupplyModal.items.find((i) => i.itemKey === itemKey);
      if (item) {
        item.selected = (tgt as HTMLInputElement).checked;
        if (item.selected && item.qty === 0) item.qty = 1;
        dealerSupplyModal = { ...dealerSupplyModal, errorMsg: null };
        updateSupplyModalDerivedDom();
      }
      return;
    }

    const dealerVehicleBtn = tgt.closest<HTMLButtonElement>('[data-co-dealer-vehicle]');
    if (dealerVehicleBtn) {
      const dealerId = dealerVehicleBtn.dataset.coDealerVehicle ?? '';
      const vehicleId = dealerVehicleBtn.dataset.vehicleId ?? '';
      if (!dealerId || !vehicleId) return;
      void handleAssignDealerVehicle(dealerId, vehicleId);
      return;
    }

  });
}

/**
 * Aktualisiert Dealer-Karten-Felder (Einnahmen, Verdacht, Inventar) direkt im DOM
 * statt das gesamte co-section-content neu zu rendern.
 * Gibt false zurück, wenn ein Full-Rerender nötig ist (Anzahl, Status oder Earn-Button geändert).
 */
function tryDealerInPlaceUpdate(dealers: DealerResponse[]): boolean {
  const cards = document.querySelectorAll<HTMLElement>('[data-dealer-id]');
  if (cards.length !== dealers.length) return false;

  for (const dealer of dealers) {
    const card = document.querySelector<HTMLElement>(`[data-dealer-id="${CSS.escape(dealer.id)}"]`);
    if (!card) return false;

    // Status-Änderung erfordert Full-Rerender (andere Buttons, anderer Card-Style)
    const badge = card.querySelector<HTMLElement>('.co-status-badge');
    if (!badge?.classList.contains(getDealerStatusClass(dealer.status))) return false;

    // NT-VIERTEL-04-ECKEN: STANDORT-Zeile + Umsiedlung-Countdown haben keine data-*-Hooks (seltene,
    // spielergesteuerte Änderung) — während einer laufenden Umsiedlung immer Full-Rerender, damit
    // der Countdown und der spätere Ecken-Wechsel sichtbar bleiben.
    if (dealer.relocateCompletesAt !== null) return false;

    // EINSAMMELN-Button: erscheint/verschwindet je nach Einnahmen
    const pending = Number.parseInt(dealer.pendingEarnings, 10);
    const collectBtn = card.querySelector<HTMLButtonElement>('[data-co-action="collect-dealer"]');
    if ((pending > 0) !== !!collectBtn) return false;

    const suspicionPct = Math.max(0, Math.min(100, Math.round(dealer.suspicion)));
    const inventoryPct = dealer.effectiveSlots > 0
      ? Math.max(0, Math.min(100, Math.round((dealer.usedSlots / dealer.effectiveSlots) * 100)))
      : 0;
    const inventoryFree = Math.max(0, dealer.effectiveSlots - dealer.usedSlots);
    const suspicionColor = dealer.suspicionZone === 'HIGH'
      ? 'var(--accent-red)'
      : dealer.suspicionZone === 'MEDIUM'
        ? 'var(--accent-orange)'
        : 'var(--accent-green)';

    const earningsEl = card.querySelector<HTMLElement>(`[data-dealer-earnings="${CSS.escape(dealer.id)}"]`);
    if (earningsEl) {
      earningsEl.textContent = fmtSeur(dealer.pendingEarnings);
      earningsEl.className = pending > 0 ? 'val-green' : 'val-muted';
    }

    const suspValEl = card.querySelector<HTMLElement>(`[data-dealer-susp-val="${CSS.escape(dealer.id)}"]`);
    if (suspValEl) { suspValEl.textContent = `${String(suspicionPct)}%`; suspValEl.style.color = suspicionColor; }

    const suspBarEl = card.querySelector<HTMLElement>(`[data-dealer-susp-bar="${CSS.escape(dealer.id)}"]`);
    if (suspBarEl) { suspBarEl.style.setProperty('--co-progress', `${String(suspicionPct)}%`); suspBarEl.style.background = suspicionColor; }

    const invDisplayEl = card.querySelector<HTMLElement>(`[data-dealer-inv-display="${CSS.escape(dealer.id)}"]`);
    if (invDisplayEl) invDisplayEl.textContent = `${String(dealer.usedSlots)} / ${String(dealer.effectiveSlots)} SLOTS`;

    const invBarEl = card.querySelector<HTMLElement>(`[data-dealer-inv-bar="${CSS.escape(dealer.id)}"]`);
    if (invBarEl) invBarEl.style.setProperty('--co-progress', `${String(inventoryPct)}%`);

    const slotsFreeEl = card.querySelector<HTMLElement>(`[data-dealer-slots-free="${CSS.escape(dealer.id)}"]`);
    if (slotsFreeEl) slotsFreeEl.textContent = String(inventoryFree);
  }

  return true;
}

// NT-HOTFIX-01 (H7): true, solange ein Polling-Refresh (30-s-Timer) läuft. Renders in dieser Phase
// werden zurückgestellt, wenn der Spieler gerade ein Dropdown/Eingabefeld/Modal in der Sektion
// benutzt, und danach nachgeholt (utils/background-render.ts).
let isBackgroundRefresh = false;
const CRIME_OPS_RENDER_KEY = 'crime-ops-section';

function renderCurrentSection(): void {
  if (isBackgroundRefresh) {
    renderWhenIdle(CRIME_OPS_RENDER_KEY, 'co-section-content', renderCurrentSectionNow);
    return;
  }
  renderCurrentSectionNow();
}

function renderCurrentSectionNow(): void {
  if (!crimeOpsData) return;
  const dashMain = document.querySelector<HTMLElement>('.dash-main');
  const savedMainScrollTop = dashMain?.scrollTop ?? 0;
  const savedWindowScrollY = window.scrollY;
  const contentEl = document.getElementById('co-section-content');
  if (contentEl) contentEl.innerHTML = renderSectionContent(crimeOpsData);
  renderOrderDeliveryModalOverlay();
  renderNpcMessageModalOverlay();

  if (dashMain !== null) {
    dashMain.scrollTop = savedMainScrollTop;
  }
  if (savedWindowScrollY > 0) {
    window.scrollTo({ top: savedWindowScrollY, behavior: 'auto' });
  }
}

async function handleStartWash(businessId: string, amount: number): Promise<void> {
  if (amount < 100) return;
  const btn = document.querySelector<HTMLButtonElement>(`[data-co-action="submit-wash"][data-bid="${CSS.escape(businessId)}"]`);
  if (btn) { btn.disabled = true; btn.textContent = 'STARTET...'; }
  try {
    await api.post<unknown>(`/laundry/businesses/${encodeURIComponent(businessId)}/runs/start`, {
      blackMoneyAmountCents: String(amount),
    });
    expandedWashBid = null;
    void refreshBalance(); // NT-HOTFIX-01 (H6): S-₦ abgebucht
    await reloadStatus();
  } catch (err) {
    if (btn) { btn.disabled = false; btn.textContent = 'STARTEN'; }
    showToast(err instanceof ApiError ? err.message : 'Start fehlgeschlagen.', 'error');
  }
}

// ─── Labor-Handler ────────────────────────────────────────────────────────────

async function handleStartBatch(recipeKey: string, propertyId: string): Promise<void> {
  try {
    await api.post<unknown>('/labor-v2/start', { recipeKey, propertyId });
    showToast('Batch gestartet!', 'info');
    laborData = null;
    await reloadSectionData();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Start fehlgeschlagen.', 'error');
  }
}

async function handleQualityCheck(batchId: string): Promise<void> {
  try {
    await api.post<unknown>(`/labor-v2/${encodeURIComponent(batchId)}/quality-check`, {});
    showToast('+15% Output-Bonus gesichert!', 'info');
    void reloadSectionData();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Fehler.', 'error');
  }
}

async function handleCollectBatch(batchId: string): Promise<void> {
  try {
    const r = await api.post<{ outputQty: number; outputItemKey: string }>(`/labor-v2/${encodeURIComponent(batchId)}/collect`, {});
    showToast(`${String(r.outputQty)}× ${r.outputItemKey} eingesammelt!`, 'info');
    laborData = null;
    void reloadSectionData();
  } catch (err) {
    if (!tryOfferEmergencyGarage(err, () => { void reloadSectionData(); })) {
      showToast(err instanceof ApiError ? err.message : 'Fehler.', 'error');
    }
  }
}

// ─── Dealer-Handler ───────────────────────────────────────────────────────────

async function handleHireDealer(npcKey: string): Promise<void> {
  try {
    await api.post<unknown>('/dealer/purchase', { npcKey });
    showToast('Dealer angeheuert!', 'info');
    void refreshBalance();
    dealerData = null;
    void reloadSectionData();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Fehler.', 'error');
  }
}

async function handleCollectDealer(dealerId: string): Promise<void> {
  try {
    const r = await api.post<{ collectedBlackMoneyCents: string }>(`/dealer/${encodeURIComponent(dealerId)}/collect`, {});
    const bm = (parseInt(r.collectedBlackMoneyCents, 10) / 100).toLocaleString('de-DE', { maximumFractionDigits: 0 });
    showToast(`${bm} S-₦ eingesammelt!`, 'info');
    void refreshBalance();
    dealerData = null;
    void reloadSectionData();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Fehler.', 'error');
  }
}

async function handlePauseDealer(dealerId: string): Promise<void> {
  try {
    await api.post<unknown>(`/dealer/${encodeURIComponent(dealerId)}/pause`, {});
    showToast('Status gewechselt.', 'info');
    dealerData = null;
    void reloadSectionData();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Fehler.', 'error');
  }
}

async function handleTrainDealer(dealerId: string): Promise<void> {
  try {
    await api.post<unknown>(`/dealer/${encodeURIComponent(dealerId)}/train`, {});
    showToast('Schulung gestartet.', 'info');
    void refreshBalance(); // NOX abgebucht
    dealerData = null;
    void reloadSectionData();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Fehler.', 'error');
  }
}

async function handleBailDealer(dealerId: string): Promise<void> {
  try {
    const result = await api.post<{ bailPaidCents: string }>(`/dealer/${encodeURIComponent(dealerId)}/bail`, {});
    showToast(`${fmtEur(result.bailPaidCents)} Kaution gezahlt.`, 'info');
    dealerData = null;
    void reloadSectionData();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Fehler.', 'error');
  }
}

async function handleBailVehicle(vehicleId: string): Promise<void> {
  if (!vehicleId) return;
  try {
    const result = await api.post<{ bailPaidCents: string }>(`/vehicle/${encodeURIComponent(vehicleId)}/bail`, {});
    showToast(`${fmtEur(result.bailPaidCents)} Kaution gezahlt — Fahrzeug frei.`, 'info');
    void refreshBalance();
    dealerData = null;
    void reloadSectionData();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Fehler.', 'error');
  }
}

async function handleSkipDealerSupply(dealerId: string, deliveryId: string): Promise<void> {
  try {
    await api.post<{ scheineSpent: number; scheineRemaining: number }>(
      `/dealer/${encodeURIComponent(dealerId)}/supply/${encodeURIComponent(deliveryId)}/skip`, {},
    );
    showToast('Belieferung sofort angekommen.', 'info');
    void refreshBalance();
    dealerData = null;
    void reloadSectionData();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Fehler.', 'error');
  }
}

async function handleAssignDealerVehicle(dealerId: string, vehicleId: string | null = null): Promise<void> {
  try {
    await api.post<unknown>(`/dealer/${encodeURIComponent(dealerId)}/vehicle`, { vehicleId });
    showToast(vehicleId ? 'Fahrzeug zugewiesen.' : 'Fahrzeug freigegeben.', 'info');
    dealerData = null;
    void reloadSectionData();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Fehler.', 'error');
  }
}

async function handleResolveDealerFleeing(dealerId: string, action: string): Promise<void> {
  const fleeAction = action === 'COLLECT' ? 'COLLECT' : 'ABANDON';
  try {
    await api.post<unknown>(`/dealer/${encodeURIComponent(dealerId)}/resolve-fleeing`, { action: fleeAction });
    dealerFleeingModalDealerId = null;
    dealerData = null;
    showToast(fleeAction === 'COLLECT' ? 'Rechenschaft gefordert.' : 'Dealer verloren.', 'info');
    void reloadSectionData();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Fehler.', 'error');
  }
}

// Gibt Anzeigename + Qualitäts-/Varianten-Badge(s) zurück, wenn itemKey ein Cannabis-Ernte- oder Joint/Blunt-Key ist.
function parseSupplyItemLabel(itemKey: string, itemType: string): string {
  if (itemType === 'GARDEN_OUTPUT') {
    const m = /^([a-z][a-z0-9_]*)_q([1-5])$/.exec(itemKey);
    const rawStrainKey = m?.[1];
    if (m && rawStrainKey !== undefined) {
      const strainName = rawStrainKey.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
      const q = QUALITY_BADGE_META[Number(m[2])];
      if (q) {
        return `${esc(strainName)} ${badgeHtml(q, 'co-supply-q-badge')}`;
      }
    }
  }
  if (itemType === 'ROLLING_OUTPUT' && getQualityBadge(itemKey)) {
    return `${esc(getCleanItemName(itemKey))} ${renderItemBadges(itemKey, 'co-supply-q-badge')}`;
  }
  return esc(itemKey.replace(/_/g, ' '));
}

function buildSupplyItems(sourceProp: DealerSupplyPropertySource | undefined): DealerSupplyModalItem[] {
  if (!sourceProp) return [];
  return sourceProp.inventoryItems
    .filter((i) => i.quantity > 0 && i.isDealerDrug)
    .map((i) => ({ itemKey: i.itemKey, itemType: i.itemType, available: i.quantity, selected: false, qty: 1 }));
}

async function handleOpenDealerSupply(dealerId: string): Promise<void> {
  const dealer = dealerData?.dealers.find((d) => d.id === dealerId);
  if (!dealer) return;

  try {
    interface PropResponse { warehouses: { id: string; label: string; customName: string | null; nickname: string | null; inventoryItems: { itemType: string; itemKey: string; quantity: number; illegal: boolean; isDealerDrug: boolean }[] }[]; hideouts: { id: string; label: string; customName: string | null; nickname: string | null; inventoryItems: { itemType: string; itemKey: string; quantity: number; illegal: boolean; isDealerDrug: boolean }[] }[] }
    const propsResp = await api.get<PropResponse>('/properties');
    dealerSupplyProperties = [
      ...propsResp.warehouses.map((p) => ({ id: p.id, label: p.label, customName: p.customName, nickname: p.nickname, isHideout: false, inventoryItems: p.inventoryItems })),
      ...propsResp.hideouts.map((p) => ({ id: p.id, label: p.label, customName: p.customName, nickname: p.nickname, isHideout: true, inventoryItems: p.inventoryItems })),
    ];
  } catch {
    dealerSupplyProperties = [];
  }

  const firstProp = dealerSupplyProperties[0];
  const availableVehicle = dealerVehicleOptions.find((v) => v.isAvailable);
  const freeSlots = Math.max(0, dealer.effectiveSlots - dealer.usedSlots);

  dealerSupplyModal = {
    dealerId: dealer.id,
    dealerName: dealer.name,
    freeSlots,
    vehicleKey: availableVehicle?.vehicleKey ?? '',
    sourcePropertyId: firstProp?.id ?? '',
    items: buildSupplyItems(firstProp),
    errorMsg: null,
  };
  supplyDurationPreviewText = ''; // Vorschau eines evtl. vorherigen Modals nicht übernehmen
  renderCurrentSection();
}

async function handleConfirmDealerSupply(): Promise<void> {
  if (!dealerSupplyModal) return;
  const sm = dealerSupplyModal;
  const selectedItems = sm.items.filter((i) => i.selected && i.qty > 0);
  if (selectedItems.length === 0) { dealerSupplyModal = { ...sm, errorMsg: 'Mindestens eine Ware auswählen.' }; renderCurrentSection(); return; }
  if (!sm.vehicleKey) { dealerSupplyModal = { ...sm, errorMsg: 'Fahrzeug auswählen.' }; renderCurrentSection(); return; }

  try {
    const result = await api.post<{ arrivesAt: string }>(`/dealer/${encodeURIComponent(sm.dealerId)}/supply`, {
      sourcePropertyId: sm.sourcePropertyId,
      vehicleKey: sm.vehicleKey,
      items: selectedItems.map((i) => ({ itemKey: i.itemKey, itemType: i.itemType, quantity: i.qty })),
    });
    dealerSupplyModal = null;
    dealerData = null;
    const arrival = new Date(result.arrivesAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    showToast(`Lieferung unterwegs — Ankunft ca. ${arrival} Uhr.`, 'info');
    void reloadSectionData();
  } catch (err) {
    dealerSupplyModal = { ...sm, errorMsg: err instanceof ApiError ? err.message : 'Lieferung fehlgeschlagen.' };
    renderCurrentSection();
  }
}

// ─── Gewächshaus-Handler ──────────────────────────────────────────────────────

async function handleAcceptNpcMessage(messageId: string): Promise<void> {
  if (!messageId) return;
  try {
    await api.post<unknown>(`/orders/messages/${encodeURIComponent(messageId)}/accept`, {});
    showToast('Kontakt angenommen.', 'info');
    await reloadSectionData();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Annahme fehlgeschlagen.', 'error');
  }
}

async function handleIgnoreNpcMessage(messageId: string): Promise<void> {
  if (!messageId) return;
  try {
    await api.post<unknown>(`/orders/messages/${encodeURIComponent(messageId)}/ignore`, {});
    showToast('Nachricht ignoriert.', 'info');
    await reloadSectionData();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Ignorieren fehlgeschlagen.', 'error');
  }
}

async function handleDeliverOrder(orderId: string, vehicleId: string, quantity: number): Promise<void> {
  if (!orderId) {
    showToast('Ungültiger Auftrag.', 'error');
    return;
  }

  const activeOrder = (ordersData?.active ?? []).find((order) => order.id === orderId);
  if (!activeOrder) {
    showToast('Auftrag nicht gefunden.', 'error');
    return;
  }

  const isWarehouse = vehicleId === 'warehouse' || vehicleId === '';
  if (!isWarehouse && !vehicleId) {
    showToast('Quelle wählen.', 'error');
    return;
  }

  try {
    await api.post<unknown>(`/orders/${encodeURIComponent(orderId)}/deliver`, {
      vehicleId,
      quantity,
    });
    orderDeliveryModalOrderId = null;
    orderDeliveryModalVehicleId = '';
    orderDeliveryModalQty = 1;
    showToast('Lieferung verbucht.', 'info');
    void refreshBalance(); // NT-HOTFIX-01 (H6)
    await reloadSectionData();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Lieferung fehlgeschlagen.', 'error');
  }
}

// ── Auftragsbrett (NT-AUFTRAGSBRETT-01/02) ──────────────────────────────────────

async function handleOpenAuftragsbrettAccept(angebotId: string): Promise<void> {
  if (!angebotId) return;
  const angebot = auftragsbrettData?.angebote.find((a) => a.id === angebotId);
  if (!angebot) return;

  if (angebot.auftragTyp === 'TRANSPORT') {
    // Quelle/Ziel sind vom Angebot fixiert — kein /free-slots-Abruf nötig, nur ein freies
    // Fahrzeug vorauswählen (vehicleList ist bereits beim Laden der Sektion geladen).
    const firstVehicle = vehicleList.find((v) => v.isAvailable);
    auftragsbrettAcceptModal = {
      angebotId,
      auftraggeberTyp: angebot.auftraggeberTyp,
      auftraggeberName: angebot.auftraggeberName,
      auftragTyp: 'TRANSPORT',
      gebundeneSlots: angebot.gebundeneSlots,
      propertyId: '',
      errorMsg: null,
      quellePropertyId: angebot.quellePropertyId,
      zielPropertyId: angebot.zielPropertyId,
      quellePropertyLabel: angebot.quellePropertyLabel,
      zielPropertyLabel: angebot.zielPropertyLabel,
      vehicleKey: firstVehicle?.vehicleKey ?? '',
      durationPreviewText: '',
    };
    renderCurrentSection();
    if (firstVehicle) scheduleAuftragsbrettDurationPreview();
    return;
  }

  try {
    auftragsbrettFreeProperties = await api.get<FreePropertySlotView[]>('/auftragsbrett/free-slots');
    // NT-AUFTRAGSBRETT-01B (PR3) — Objektwahl kommt aus dem Board-Profil statt einem hartcodierten
    // KNASTKONTAKT-Sonderfall (jeder Auftraggeber-Typ akzeptiert nur seine eigene Objektwahl).
    const profile = getAuftraggeberProfile(angebot.auftraggeberTyp);
    const candidateProperties = profile
      ? auftragsbrettFreeProperties.filter((p) => p.type === (profile.objektwahl === 'VERSTECK' ? 'HIDEOUT' : 'WAREHOUSE'))
      : auftragsbrettFreeProperties;
    const firstEligible = candidateProperties.find((p) => p.freeSlots >= angebot.gebundeneSlots);
    auftragsbrettAcceptModal = {
      angebotId,
      auftraggeberTyp: angebot.auftraggeberTyp,
      auftraggeberName: angebot.auftraggeberName,
      auftragTyp: 'BUNKERN',
      gebundeneSlots: angebot.gebundeneSlots,
      propertyId: firstEligible?.id ?? '',
      errorMsg: null,
      quellePropertyId: null,
      zielPropertyId: null,
      quellePropertyLabel: null,
      zielPropertyLabel: null,
      vehicleKey: '',
      durationPreviewText: '',
    };
    renderCurrentSection();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Objekte konnten nicht geladen werden.', 'error');
  }
}

function handleCloseAuftragsbrettAccept(): void {
  auftragsbrettAcceptModal = null;
  renderCurrentSection();
}

async function handleConfirmAuftragsbrettAccept(): Promise<void> {
  const modal = auftragsbrettAcceptModal;
  if (!modal) return;
  if (modal.auftragTyp === 'TRANSPORT' ? !modal.vehicleKey : !modal.propertyId) return;
  try {
    const body = modal.auftragTyp === 'TRANSPORT' ? { vehicleKey: modal.vehicleKey } : { propertyId: modal.propertyId };
    await api.post<unknown>(`/auftragsbrett/${encodeURIComponent(modal.angebotId)}/accept`, body);
    auftragsbrettAcceptModal = null;
    showToast('Auftrag angenommen.', 'info');
    void refreshBalance(); // NT-HOTFIX-01 (H6): ANZAHLUNG zahlt sofort aus
    await reloadSectionData();
  } catch (err) {
    auftragsbrettAcceptModal = { ...modal, errorMsg: err instanceof ApiError ? err.message : 'Annahme fehlgeschlagen.' };
    renderCurrentSection();
  }
}

async function handleAuftragsbrettAbort(auftragId: string): Promise<void> {
  if (!auftragId) return;
  try {
    await api.post<unknown>(`/auftragsbrett/${encodeURIComponent(auftragId)}/abort`, {});
    showToast('Auftrag abgebrochen — keine Belohnung.', 'info');
    await reloadSectionData();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Abbruch fehlgeschlagen.', 'error');
  }
}

// NT-AUFTRAGSBRETT-05
async function handleAuftragsbrettEreignisEntscheiden(auftragId: string, entscheidung: AuftragEreignisEntscheidungView): Promise<void> {
  if (!auftragId) return;
  try {
    const updated = await api.post<AuftragView>(`/auftragsbrett/${encodeURIComponent(auftragId)}/ereignis/entscheiden`, { entscheidung });
    if (updated.ereignis?.ergebnis) showToast(updated.ereignis.ergebnis, 'info');
    void refreshBalance(); // NT-HOTFIX-01 (H6)
    await reloadSectionData();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Entscheidung fehlgeschlagen.', 'error');
  }
}

async function handleTradeSell(): Promise<void> {
  if (!selectedVehicleId) {
    showToast('Fahrzeug wählen.', 'error');
    return;
  }

  try {
    const r = await api.post<{ totalNox: string }>('/trade/sell', {
      vehicleId: selectedVehicleId,
      slotIndex: selectedVehicleSlotIndex,
      qty: tradeSellQty,
    });
    showToast(`Verkauft: ${fmtEur(r.totalNox)}`, 'info');
    void refreshBalance(); // NT-HOTFIX-01 (H6)
    await reloadSectionData();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Verkauf fehlgeschlagen.', 'error');
  }
}

async function handleCollect(runId: string, _businessId: string): Promise<void> {
  const btn = document.querySelector<HTMLButtonElement>(`[data-co-action="collect"][data-runid="${CSS.escape(runId)}"]`);
  if (btn) { btn.disabled = true; btn.textContent = '...'; }
  try {
    await api.post<unknown>(`/laundry/runs/${encodeURIComponent(runId)}/collect`, {});
    void refreshBalance(); // NT-HOTFIX-01 (H6)
    await reloadStatus();
  } catch (err) {
    if (btn) { btn.disabled = false; btn.textContent = 'EINSAMMELN'; }
    showToast(err instanceof ApiError ? err.message : 'Fehler beim Einsammeln.', 'error');
  }
}

async function handleLoadGaertnerShop(): Promise<void> {
  gaertnerShopLoading = true;
  renderCurrentSection();
  try {
    gaertnerShopData    = await api.get<GaertnerShopData>('/contacts/gaertner');
    gaertnerShopLoading = false;
    renderCurrentSection();
  } catch (err) {
    gaertnerShopLoading = false;
    showToast(err instanceof ApiError ? err.message : 'Gärtner ist gerade beschäftigt.', 'error');
    renderCurrentSection();
  }
}

async function handleGaertnerOrder(): Promise<void> {
  if (gaertnerCart.size === 0) return;
  const targetId = propsDataForGaertner;
  if (!targetId) {
    showToast('Kein aktives Lager für Lieferung gefunden.', 'error');
    return;
  }
  if (targetId.startsWith('dealer:')) {
    showToast('Dealer-Ziele sind nur im Dealer-Versand verfügbar.', 'error');
    return;
  }
  const items = Array.from(gaertnerCart.entries()).map(([strainKey, quantity]) => ({ strainKey, quantity }));
  try {
    const res = await api.post<{
      purchased: boolean; totalQuantity: number; trustLabel: string;
      leveledUp: boolean; nextLevel: { label: string; purchasesLeft: number } | null;
      nextOrderAt: string;
    }>('/contacts/gaertner/buy', {
      items,
      targetPropertyId: targetId,
    });
    const msg = res.leveledUp
      ? `Bestellt! Trust-Aufstieg: ${res.trustLabel}`
      : `${String(res.totalQuantity)} Samen ins Lager — nächste Bestellung in 12h.`;
    showToast(msg, 'info');
    void refreshBalance();
    gaertnerCart.clear();
    gaertnerShopData = null;
    await reloadStatus();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Bestellung fehlgeschlagen.', 'error');
  }
}

async function handleLoadAlchemistShop(): Promise<void> {
  alchemistShopLoading = true;
  renderCurrentSection();
  try {
    alchemistShopData    = await api.get<AlchemistShopData>('/contacts/alchemist');
    alchemistShopLoading = false;
    renderCurrentSection();
  } catch (err) {
    alchemistShopLoading = false;
    showToast(err instanceof ApiError ? err.message : 'Alchemist ist gerade beschäftigt.', 'error');
    renderCurrentSection();
  }
}

async function handleAlchemistOrder(): Promise<void> {
  if (alchemistCart.size === 0) return;
  const targetId = propsDataForGaertner;
  if (!targetId) {
    showToast('Kein aktives Lager für Lieferung gefunden.', 'error');
    return;
  }
  if (targetId.startsWith('dealer:')) {
    showToast('Dealer-Ziele sind nur im Dealer-Versand verfügbar.', 'error');
    return;
  }
  const items = Array.from(alchemistCart.entries()).map(([itemKey, quantity]) => ({ itemKey, quantity }));
  try {
    const res = await api.post<{
      purchased: boolean; totalQuantity: number; trustLabel: string;
      leveledUp: boolean; nextLevel: { label: string; purchasesLeft: number } | null;
      nextOrderAt: string;
    }>('/contacts/alchemist/buy', {
      items,
      targetPropertyId: targetId,
    });
    const msg = res.leveledUp
      ? `Bestellt! Trust-Aufstieg: ${res.trustLabel}`
      : `${String(res.totalQuantity)} Einheiten ins Lager — nächste Bestellung in 12h.`;
    showToast(msg, 'info');
    void refreshBalance();
    alchemistCart.clear();
    alchemistShopData = null;
    await reloadStatus();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Bestellung fehlgeschlagen.', 'error');
  }
}

// ─── Schmuggel-Run Handler ────────────────────────────────────────────────────

async function handleStartSmuggleRun(): Promise<void> {
  if (!smuggleRunSelectedItem || !smuggleRunSelectedVehicle) {
    showToast('Ware und Fahrzeug auswählen.', 'error');
    return;
  }
  const qty = Math.max(1, smuggleRunSelectedQty);
  try {
    await api.post<unknown>('/smuggling/runs', {
      itemKey:    smuggleRunSelectedItem,
      vehicleKey: smuggleRunSelectedVehicle,
      quantity:   qty,
    });
    smuggleRunSelectedItem = '';
    [smuggleRunStatus, smuggleRunHistory] = await Promise.all([
      api.get<SmuggleRunStatusData>('/smuggling/status'),
      api.get<SmuggleHistoryEntry[]>('/smuggling/runs/history'),
    ]);
    renderCurrentSection();
    showToast('Schmuggel-Run gestartet.', 'info');
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Run konnte nicht gestartet werden.', 'error');
    renderCurrentSection();
  }
}

async function handleSmuggleCheckpoint(runId: string, checkpointId: string, decision: 'A' | 'B' | 'C'): Promise<void> {
  try {
    const result = await api.post<{ outcome: 'continue' | 'completed' | 'failed' }>(`/smuggling/runs/${encodeURIComponent(runId)}/checkpoint`, { checkpointId, decision });
    [smuggleRunStatus, smuggleRunHistory] = await Promise.all([
      api.get<SmuggleRunStatusData>('/smuggling/status'),
      api.get<SmuggleHistoryEntry[]>('/smuggling/runs/history'),
    ]);
    if (result.outcome === 'failed') {
      showToast('BESCHLAGNAHMT – Ware verloren. FL +2. Fahrzeug 4h gesperrt.', 'error');
    } else if (result.outcome === 'completed') {
      showToast('Schmuggel-Run erfolgreich abgeschlossen. Ware im Lager.', 'info');
    }
    renderCurrentSection();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Entscheidung fehlgeschlagen.', 'error');
    renderCurrentSection();
  }
}

// showToast aus utils/toast.ts importiert

// ─── Progress in-place ────────────────────────────────────────────────────────

function updateProgressInPlace(): void {
  const now = Date.now();

  // Mission-Ablauf global prüfen, unabhängig vom aktiven Tab
  if (missionActiveState?.status === 'RUNNING' && missionActiveState.startedAt) {
    const endMs = new Date(missionActiveState.startedAt).getTime() + missionActiveState.durationSeconds * 1000;
    if (endMs <= now) {
      missionActiveState = { ...missionActiveState, status: 'EXPIRED' as const };
      checkAndShowMissionEventModal();
      if (selectedSection === 'auftraege') void reloadSectionData();
    }
  }

  if (selectedSection === 'auftraege' && ordersData) {
    let shouldReloadOrders = false;

    for (const order of ordersData.active) {
      const remaining = Math.max(0, new Date(order.expiresAt).getTime() - now);
      const nodes = document.querySelectorAll<HTMLElement>(`[data-order-expires="${CSS.escape(order.id)}"]`);
      nodes.forEach((node) => {
        node.textContent = fmtCountdown(remaining);
      });
      if (remaining <= 0) shouldReloadOrders = true;
    }

    for (const npc of ordersData.npcs) {
      if (!npc.id) continue;
      const npcId = npc.id;

      if (npc.returnsAt) {
        const returnRemaining = Math.max(0, new Date(npc.returnsAt).getTime() - now);
        const returnNode = document.querySelector<HTMLElement>(`[data-order-returns-at="${CSS.escape(npcId)}"]`);
        if (returnNode) returnNode.textContent = fmtCountdown(returnRemaining);
        if (returnRemaining <= 0) shouldReloadOrders = true;
      }
    }

    for (const contact of missionContactsData) {
      const remaining = Math.max(0, new Date(contact.availableUntil).getTime() - now);
      const node = document.querySelector<HTMLElement>(`[data-mission-contact-remaining="${CSS.escape(contact.id)}"]`);
      if (node) {
        node.textContent = fmtCountdown(remaining);
        const note = node.closest('.co-order-note');
        if (note) {
          note.classList.toggle('co-order-note--danger', remaining <= 60 * 60 * 1000);
        }
      }
      if (remaining <= 0) shouldReloadOrders = true;
    }

    if (missionActiveState?.status === 'RUNNING' && missionActiveState.startedAt) {
      const startMs = new Date(missionActiveState.startedAt).getTime();
      const endMs = startMs + missionActiveState.durationSeconds * 1000;
      const remaining = Math.max(0, endMs - now);
      const nodes = document.querySelectorAll<HTMLElement>(`[data-mission-running-remaining="${CSS.escape(missionActiveState.id)}"]`);
      nodes.forEach((node) => {
        node.textContent = fmtCountdown(remaining);
      });
      if (remaining <= 0) shouldReloadOrders = true;
    }

    // Zellengenosse-Countdown in-place aktualisieren
    if (cellmateJobData?.acceptedAt && cellmateJobData.completesAt) {
      const cellmateRemaining = Math.max(0, new Date(cellmateJobData.completesAt).getTime() - now);
      const countdownEl = document.getElementById('co-cellmate-countdown');
      if (countdownEl) {
        countdownEl.textContent = cellmateRemaining <= 0 ? 'FERTIG' : fmtCountdown(cellmateRemaining);
      }
      if (cellmateRemaining <= 0) {
        // Button freischalten ohne vollen Re-Render
        const completeBtn = document.querySelector<HTMLButtonElement>('[data-co-action="cellmate-complete"]');
        if (completeBtn) completeBtn.disabled = false;
      }
    }

    if (shouldReloadOrders) {
      void reloadSectionData();
      return;
    }
  }

  // NT-AUFTRAGSBRETT-02: TRANSPORT-Phasenbalken in-place patchen (dieselbe Lehre wie oben —
  // kein Full-Re-Render pro Tick). Die restlichen Countdowns auf dem Brett (bunkerBisAt,
  // Angebots-/Auftraggeber-Ablauf) bleiben unverändert statisch bis zum nächsten vollen Reload
  // (bereits bestehendes Stufe-1-Verhalten, hier nicht erweitert).
  if (selectedSection === 'auftraege' && auftragsbrettData) {
    for (const a of auftragsbrettData.aktive) {
      if (a.auftragTyp !== 'TRANSPORT' || !a.transportPhase) continue;
      const phaseUi = computeTransportPhaseUi(a.transportPhase, now);
      if (phaseUi) {
        const labelEl = document.querySelector<HTMLElement>(`[data-auftrag-transport-phase-label="${CSS.escape(a.id)}"] .co-phase-label`);
        if (labelEl) {
          const activeColorVar = phaseUi.segments.find((s) => s.isActive)?.colorVar ?? '--accent-gold';
          labelEl.textContent = phaseUi.activeLabel;
          labelEl.style.color = `var(${activeColorVar})`;
        }
        for (const s of phaseUi.segments) {
          const segEl = document.querySelector<HTMLElement>(`[data-auftrag-transport-seg="${a.id}:${s.key}"]`);
          if (segEl) segEl.style.width = `${String(s.fillPct)}%`;
        }
      } else {
        const total = new Date(a.transportPhase.arrivesAt).getTime() - new Date(a.transportPhase.startedAt).getTime();
        const elapsed = now - new Date(a.transportPhase.startedAt).getTime();
        const pct = total <= 0 ? 100 : Math.max(0, Math.min(100, Math.round((elapsed / total) * 100)));
        const barEl = document.querySelector<HTMLElement>(`[data-auftrag-transport-bar="${CSS.escape(a.id)}"]`);
        if (barEl) barEl.style.setProperty('--co-progress', `${String(pct)}%`);
      }
      if (new Date(a.transportPhase.arrivesAt).getTime() <= now) void reloadSectionData();
    }
  }

  if (selectedSection === 'dealer' && dealerData) {
    let shouldReloadDealer = false;
    for (const dealer of dealerData.dealers) {
      if (dealer.trainingCompletesAt) {
        const remaining = Math.max(0, new Date(dealer.trainingCompletesAt).getTime() - now);
        const el = document.querySelector<HTMLElement>(`[data-dealer-training="${CSS.escape(dealer.id)}"]`);
        if (el) el.textContent = `AUSLERNEN IN ${fmtCountdown(remaining)}`;
        if (remaining <= 0) shouldReloadDealer = true;
      }
      if (dealer.arrestedUntil) {
        const remaining = Math.max(0, new Date(dealer.arrestedUntil).getTime() - now);
        const el = document.querySelector<HTMLElement>(`[data-dealer-arrest="${CSS.escape(dealer.id)}"]`);
        if (el) el.textContent = `FREILASSUNG IN ${fmtCountdown(remaining)}`;
        if (remaining <= 0) shouldReloadDealer = true;
      }
      for (const d of dealer.pendingSupplyDeliveries) {
        const remaining = Math.max(0, new Date(d.arrivesAt).getTime() - now);
        const timeEl = document.querySelector<HTMLElement>(`[data-dealer-delivery-time="${CSS.escape(d.id)}"]`);
        if (timeEl) timeEl.textContent = `${String(d.itemCount)}× — ${fmtCountdown(remaining)}`;

        // NT-TRANSPORT-BELADUNG-01: Phasen-Segmente in-place patchen (kein Full-Re-Render pro Tick,
        // Lehre aus NT-UI-LAYOUT-RERENDER-FIX-01) — Fallback ohne Phasen-Daten patcht weiterhin nur
        // den einzelnen --co-progress-Balken.
        const phaseUi = computeTransportPhaseUi(d, now);
        if (phaseUi) {
          const labelEl = document.querySelector<HTMLElement>(`[data-dealer-delivery-phase-label="${CSS.escape(d.id)}"] .co-phase-label`);
          if (labelEl) {
            const activeColorVar = phaseUi.segments.find((s) => s.isActive)?.colorVar ?? '--accent-gold';
            labelEl.textContent = phaseUi.activeLabel;
            labelEl.style.color = `var(${activeColorVar})`;
          }
          for (const s of phaseUi.segments) {
            const segEl = document.querySelector<HTMLElement>(`[data-dealer-delivery-seg="${CSS.escape(d.id)}:${s.key}"]`);
            if (segEl) segEl.style.width = `${String(s.fillPct)}%`;
          }
        } else {
          const total = new Date(d.arrivesAt).getTime() - new Date(d.startedAt).getTime();
          const elapsed = now - new Date(d.startedAt).getTime();
          const pct = total <= 0 ? 100 : Math.max(0, Math.min(100, Math.round((elapsed / total) * 100)));
          const barEl = document.querySelector<HTMLElement>(`[data-dealer-delivery-bar="${CSS.escape(d.id)}"]`);
          if (barEl) barEl.style.setProperty('--co-progress', `${String(pct)}%`);
        }
        if (remaining <= 0) shouldReloadDealer = true;
      }
    }
    if (shouldReloadDealer) { void reloadSectionData(); return; }
  }

  if (crimeOpsData && selectedSection === 'geldwaesche') {
    for (const b of crimeOpsData.geldwaesche.businesses) {
      const run = b.activeRun;
      if (run?.status !== 'RUNNING') continue;

      const startMs = new Date(run.startedAt).getTime();
      const endMs   = new Date(run.completesAt).getTime();

      if (now >= endMs) { void reloadStatus(); return; }

      const totalMs = endMs - startMs;
      const elapsed = now - startMs;
      const pct     = totalMs <= 0 ? 100 : Math.min(100, Math.floor((elapsed / totalMs) * 100));

      const fill   = document.getElementById(`co-prog-${b.id}`);
      const pctEl  = document.getElementById(`co-pct-${b.id}`);
      const timeEl = document.getElementById(`co-time-${b.id}`);

      if (fill)   fill.style.setProperty('--co-progress', `${String(pct)}%`);
      if (pctEl)  pctEl.textContent = `${String(pct)}%`;
      if (timeEl) timeEl.textContent = timeLeft(run.completesAt);
    }
  }

}

// ─── Mission-Event-Popup (global, Tab-übergreifend) ───────────────────────────

/** Zeigt, welche Entscheidungen seit dem letzten Abruf automatisch aufgelöst wurden (AFK). */
function showAutoResolvedMissionToast(entries: BreakInMissionAutoResolvedEvent[]): void {
  if (entries.length === 0) return;
  const first = entries[0];
  const message = entries.length === 1 && first
    ? first.logMessage
    : `${String(entries.length)} Ereignisse wurden automatisch aufgelöst, während du nicht reagiert hast.`;
  showToast(message, 'info');
}

function checkAndShowMissionEventModal(): void {
  if (missionActiveState?.status !== 'RUNNING' || !missionActiveState.nextEvent) {
    hideMissionEventModal();
    return;
  }
  const cooldownActive = missionActiveState.nextEventAvailableAt
    ? new Date(missionActiveState.nextEventAvailableAt).getTime() > Date.now()
    : false;
  const activeMissionId = missionActiveState.id;
  const contact = missionContactsData.find((c) => c.mission?.id === activeMissionId);
  showMissionEventModal({
    missionId: missionActiveState.id,
    contactName: contact?.name ?? 'Unbekannter Kontakt',
    missionType: missionActiveState.missionType,
    event: missionActiveState.nextEvent,
    nextEventAvailableAt: cooldownActive ? (missionActiveState.nextEventAvailableAt ?? null) : null,
    onDecide: (missionId, eventId, optionIndex) => handleMissionDecision(missionId, eventId, optionIndex),
    onClose: () => { hideMissionEventModal(); },
  });
}

// ─── Daten laden ─────────────────────────────────────────────────────────────

async function reloadSectionData(): Promise<void> {
  if (!crimeOpsData) return;
  try {
    if (selectedSection === 'auftraege' || selectedSection === 'strassenhandel') {
      if (selectedSection === 'auftraege') {
        orderVehicleInventoryById.clear();
      }
      const vehicleResp = await api.get<{ vehicles: VehicleSummary[] }>('/vehicle/list');
      vehicleList = vehicleResp.vehicles;
      const firstVehicle = vehicleList[0];
      if (!selectedVehicleId && firstVehicle) {
        selectedVehicleId = firstVehicle.id;
      }

      if (selectedVehicleId) {
        selectedVehicleInventory = await api.get<VehicleInventoryData>(`/vehicle/${encodeURIComponent(selectedVehicleId)}/inventory`);
        if (selectedSection === 'auftraege') {
          orderVehicleInventoryById.set(selectedVehicleId, selectedVehicleInventory);
        }
        const filledSlots = selectedVehicleInventory.slots.filter((s) => s.itemKey !== null && (s.quantity ?? 0) > 0);
        const slotExists = filledSlots.some((s) => s.slotIndex === selectedVehicleSlotIndex);
        const firstFilledSlot = filledSlots[0];
        if (!slotExists && firstFilledSlot) {
          selectedVehicleSlotIndex = firstFilledSlot.slotIndex;
        }
      } else {
        selectedVehicleInventory = null;
      }
    }

    if (selectedSection === 'labor' && crimeOpsData.labor.unlocked) {
      if (!isLaborTabMounted) {
        // Bei Seitenreload aus sessionStorage existiert #lt-root noch nicht → erst rendern
        if (!document.getElementById('lt-root')) renderCurrentSection();
        mountLaborTab();
        isLaborTabMounted = true;
        return;
      }
      await refreshLaborTab();
      return; // Tab verwaltet eigenes Laden
    } else if (selectedSection === 'auftraege') {
      // Notification-Fetch/Toast lief früher hier lokal (nur wenn dieser Tab aktiv war) — jetzt
      // global in services/global-toast.ts (NT-TOAST-GLOBAL-01), unabhängig vom aktiven Tab.
      const [active, history, npcs, messages, missionContacts, missionHistory, cellmateJob, auftragsbrett, auftragsbrettHistory] = await Promise.all([
        api.get<{ orders: NpcOrderItem[] }>('/orders'),
        api.get<{ orders: NpcOrderItem[] }>('/orders/history'),
        api.get<{ npcs: OrderNpcView[] }>('/orders/npcs'),
        api.get<{ messages: NpcMessageView[] }>('/orders/messages'),
        api.get<{ contacts: BreakInMissionContact[] }>('/missions/contacts'),
        api.get<{ missions: BreakInMissionState[] }>('/missions/history'),
        // api.get() entpackt die {data, meta}-Hülle bereits automatisch — hier direkt den
        // entpackten Wert typisieren, nicht nochmal { data: ... } erwarten (war zuvor immer null).
        api.get<CellmateSmuggleJobView | null>('/cellmate/jobs').catch(() => null),
        api.get<AuftragsbrettBoardView>('/auftragsbrett'),
        api.get<AuftragHistoryItem[]>('/auftragsbrett/history'),
      ]);
      ordersData = { active: active.orders, history: history.orders, npcs: npcs.npcs };
      npcMessagesData = messages.messages;
      missionContactsData = missionContacts.contacts;
      missionHistoryData = missionHistory.missions;
      cellmateJobData = cellmateJob;
      // NT-HOTFIX-01 (H6): GET /auftragsbrett schließt fällige Aufträge lazy ab und zahlt aus — ist
      // ein vorher aktiver Auftrag verschwunden, hat sich der Kontostand geändert.
      const previouslyActive = new Set((auftragsbrettData?.aktive ?? []).map((a) => a.id));
      const nowActive = new Set(auftragsbrett.aktive.map((a) => a.id));
      if ([...previouslyActive].some((id) => !nowActive.has(id))) void refreshBalance();
      auftragsbrettData = auftragsbrett;
      auftragsbrettHistoryData = auftragsbrettHistory;

      await ensureOrderInventoriesLoaded();
      if (!ordersData.npcs.some((n) => n.npcType === selectedOrderNpcType)) {
        selectedOrderNpcType = ordersData.npcs[0]?.npcType ?? 'SCHROTTHAENDLER';
      }

      const runningMissionId = missionContactsData.find((c) => c.mission?.status === 'RUNNING')?.mission?.id ?? null;
      const missionIdToRefresh = runningMissionId ?? missionModalMissionId;
      if (missionIdToRefresh) {
        try {
          const missionState = await api.get<{ mission: BreakInMissionState }>(`/missions/${encodeURIComponent(missionIdToRefresh)}`);
          missionActiveState = missionState.mission;
          showAutoResolvedMissionToast(missionState.mission.autoResolvedEvents);
        } catch {
          missionActiveState = null;
          if (missionModalMissionId === missionIdToRefresh) {
            missionModalMissionId = null;
          }
        }
      } else {
        missionActiveState = null;
      }
      checkAndShowMissionEventModal();
    } else if (selectedSection === 'dealer' && crimeOpsData.dealer.unlocked) {
      const [dealers, talents, vehicles] = await Promise.all([
        api.get<DealerResponse[]>('/dealer'),
        api.get<TalentTreeData>('/talents/tree'),
        api.get<{ vehicles: VehicleSummary[] }>('/vehicle/list'),
      ]);
      dealerTalentTree = talents;
      dealerVehicleOptions = vehicles.vehicles;
      const prevDealerData = dealerData;
      dealerData = {
        dealers,
        supplySources: [],
        maxDealers: getDealerLimit(),
        canHireMore: getDealerLimit() > dealers.length,
      };
      dealerFleeingModalDealerId = dealers.find((dealer) => dealer.status === 'FLEEING')?.id ?? null;

      // NT-VIERTEL-07-FEINSCHLIFF (B5): Sprung aus dem Viertel-Tab (BELIEFERN/FAHRZEUG) — Panel/
      // Modal für den Ziel-Dealer vorab öffnen (Muster: crimeops_hideout_id in
      // immobilien-tab.ts). Danach immer volles Rendern statt In-Place-Update, damit die neue
      // Öffnung sichtbar wird.
      let jumpedToDealer = false;
      const preSupplyId = sessionStorage.getItem('crimeops_dealer_supply_id');
      if (preSupplyId) {
        sessionStorage.removeItem('crimeops_dealer_supply_id');
        if (dealers.some((d) => d.id === preSupplyId)) { jumpedToDealer = true; void handleOpenDealerSupply(preSupplyId); }
      }
      const preVehicleId = sessionStorage.getItem('crimeops_dealer_vehicle_id');
      if (preVehicleId) {
        sessionStorage.removeItem('crimeops_dealer_vehicle_id');
        if (dealers.some((d) => d.id === preVehicleId)) { jumpedToDealer = true; dealerVehiclePanelState.set(preVehicleId, true); }
      }

      // In-Place-Update wenn DOM bereits aufgebaut — vermeidet Flackern beim 30s-Poll
      if (!jumpedToDealer && prevDealerData !== null && tryDealerInPlaceUpdate(dealers)) return;
    } else if (selectedSection === 'schmuggel' && crimeOpsData.schmuggel.unlocked) {
      [schmuggelData, smuggleRunStatus, smuggleRunHistory] = await Promise.all([
        api.get<SchmuggelStatusData>('/schmuggel/status'),
        api.get<SmuggleRunStatusData>('/smuggling/status'),
        api.get<SmuggleHistoryEntry[]>('/smuggling/runs/history'),
      ]);
    } else if (selectedSection === 'strassenhandel') {
      if (!isStreetMarketTabMounted) {
        if (!document.getElementById('sm-root')) renderCurrentSection();
        await mountStreetMarketTab();
        isStreetMarketTabMounted = true;
        return;
      }
      await refreshStreetMarketTab();
      return; // Tab verwaltet eigenes Laden
    } else if (selectedSection === 'gewächshaus') {
      if (!isGewaechshausTabMounted) {
        if (!document.getElementById('gw-tab-root')) renderCurrentSection();
        mountGewaechshausTab();
        isGewaechshausTabMounted = true;
        return;
      }
      await refreshGewaechshausTab();
      return; // Tab verwaltet eigenes Laden
    } else if (selectedSection === 'rolltisch') {
      if (!isRolltischTabMounted) {
        if (!document.getElementById('rt-root')) renderCurrentSection();
        mountRolltischTab();
        isRolltischTabMounted = true;
        return;
      }
      await refreshRolltischTab();
      return; // Tab verwaltet eigenes Laden
    } else if (selectedSection === 'schwarzmarkt') {
      if (!isSchwarzmarktTabMounted) {
        if (!document.getElementById('sm-root')) renderCurrentSection();
        mountSchwarzmarktTab();
        isSchwarzmarktTabMounted = true;
        return;
      }
      await refreshSchwarzmarktTab();
      return; // Tab verwaltet eigenes Laden
    } else if (selectedSection === 'immobilien') {
      if (!isImmobilienTabMounted) {
        if (!document.getElementById('immo-tab-root')) renderCurrentSection();
        mountImmobilienTab();
        isImmobilienTabMounted = true;
        return;
      }
      await refreshImmobilienTab();
      return; // Tab verwaltet eigenes Laden
    } else if (selectedSection === 'viertel') {
      if (!isViertelTabMounted) {
        if (!document.getElementById('viertel-tab-root')) renderCurrentSection();
        mountViertelTab();
        isViertelTabMounted = true;
        return;
      }
      await refreshViertelTab();
      return; // Tab verwaltet eigenes Laden
    } else if (selectedSection === 'darknet') {
      if (!isDarknetTabMounted) {
        if (!document.getElementById('dn-root')) renderCurrentSection();
        mountDarknetTab();
        isDarknetTabMounted = true;
        return;
      }
      await refreshDarknetTab();
      return; // Tab verwaltet eigenes Laden
    }
  } catch { /* still ignorieren */ }
  renderCurrentSection();
}

/** Polling-Variante von reloadStatus() — Renders pausieren bei offener Auswahl (H7). */
async function reloadStatusInBackground(): Promise<void> {
  if (reloadStatusPromise) return; // läuft schon (z. B. nach einer Spieler-Aktion) — nicht als Hintergrund markieren
  isBackgroundRefresh = true;
  try {
    await reloadStatus();
  } finally {
    isBackgroundRefresh = false;
  }
}

async function reloadStatus(): Promise<void> {
  if (reloadStatusPromise) {
    await reloadStatusPromise;
    return;
  }

  reloadStatusPromise = (async () => {
    try {
      // Lade Status + Properties parallel; Properties-Fehler sind nicht kritisch
      api.get<{
        warehouses: { id: string; isActive: boolean; label?: string; customName?: string | null; nickname?: string | null; subType?: string; slots?: number; currentLoad?: number }[];
        hideouts: { id: string; isActive: boolean; label?: string; customName?: string | null; nickname?: string | null; subType?: string; slots?: number; currentLoad?: number }[];
      }>('/properties')
        .then((p) => {
          const toTarget = (x: (typeof p.warehouses)[number], isHideout: boolean): ContactDeliveryTarget => ({
            id: x.id, label: x.label ?? null, subType: x.subType ?? null,
            customName: x.customName ?? null, nickname: x.nickname ?? null, isHideout,
            freeSlots: x.slots !== undefined && x.currentLoad !== undefined ? x.slots - x.currentLoad : null,
          });
          const activeWarehouses = p.warehouses.filter((w) => w.isActive).map((w) => toTarget(w, false));
          const activeHideouts   = p.hideouts.filter((h) => h.isActive).map((h) => toTarget(h, true));

          contactDeliveryTargets = [...activeWarehouses, ...activeHideouts];
          const hasSelected = contactDeliveryTargets.some((t) => t.id === propsDataForGaertner);
          if (!hasSelected) {
            propsDataForGaertner = contactDeliveryTargets[0]?.id ?? '';
          }
        })
        .catch(() => {
          contactDeliveryTargets = [];
        });

      const status = await api.get<CrimeOpsStatus>('/crime-ops/status');
      crimeOpsData = status;

      // Tab-Buttons: nur ersetzen wenn sich enabled/locked-Zustand geändert hat
      const root = document.getElementById('co-root');
      if (root) {
        const tabsEl = root.querySelector('.co-tabs');
        if (tabsEl) {
          const tmp = document.createElement('div');
          tmp.innerHTML = renderTabs(status);
          const newTabsEl = tmp.firstElementChild;
          if (newTabsEl && tabsEl.innerHTML !== newTabsEl.innerHTML) {
            tabsEl.replaceWith(newTabsEl);
          }
        }
      }

      await reloadSectionData();
    } catch (err) {
      const contentEl = document.getElementById('co-section-content');
      if (contentEl) {
        const msg = err instanceof ApiError ? err.message : 'Verbindungsfehler.';
        contentEl.innerHTML = `<p style="font-family:var(--font-mono);font-size:12px;color:var(--accent-red);padding:var(--space-4) 0;">${esc(msg)}</p>`;
      }
    }
  })();

  try {
    await reloadStatusPromise;
  } finally {
    reloadStatusPromise = null;
  }
}

// ─── Lifecycle ────────────────────────────────────────────────────────────────

export function renderCrimeOpsTab(): string {
  addStyles();
  return `
    <section class="dash-content" style="padding:0;" id="co-root">
      <div class="co-header">
        <h2 class="co-title">CRIME OPS</h2>
        <p class="co-subtitle">Kriminelle Operationen verwalten.</p>
      </div>
      <div class="co-tabs">
        <button class="co-tab-btn is-active" data-co-section="geldwaesche">GELDWÄSCHE</button>
        <button class="co-tab-btn" data-co-section="auftraege">AUFTRAGGEBER</button>
        <button class="co-tab-btn" data-co-section="strassenhandel">STRASSENHANDEL</button>
        <button class="co-tab-btn" data-co-section="dealer">DEALER</button>
        <button class="co-tab-btn" data-co-section="labor">LABOR</button>
        <button class="co-tab-btn" data-co-section="schmuggel">SCHMUGGEL</button>
      </div>
      <div class="co-content-area">
        <div id="co-section-content">
          <p style="font-family:var(--font-mono);font-size:12px;color:var(--text-muted);text-align:center;padding:var(--space-8) 0;letter-spacing:0.08em;">LADE...</p>
        </div>
      </div>
    </section>
  `;
}

export function mountCrimeOpsTab(): void {
  addStyles();

  // Stale-Timer stoppen + Kind-Tab-Flags zurücksetzen: renderAll() baut das DOM neu auf,
  // aber ruft mountCrimeOpsTab() ohne vorherige cleanupCrimeOps()-Sequenz auf.
  // Ohne diesen Reset bleibt isLaborTabMounted/isGewaechshausTabMounted etc. auf true
  // und reloadSectionData() ruft refreshXxxTab() auf einem nicht-existierenden Root auf →
  // stilles Fail → Tab bleibt dauerhaft auf "LADE...".
  if (refreshTimer !== null) { clearInterval(refreshTimer); refreshTimer = null; }
  if (progressTimer !== null) { clearInterval(progressTimer); progressTimer = null; }
  cleanupGewaechshausTab(); isGewaechshausTabMounted = false;
  cleanupRolltischTab();    isRolltischTabMounted    = false;
  cleanupLaborTab();        isLaborTabMounted        = false;
  cleanupImmobilienTab();   isImmobilienTabMounted   = false;
  cleanupViertelTab();      isViertelTabMounted      = false;
  cleanupStreetMarketTab(); isStreetMarketTabMounted = false;
  cleanupDarknetTab();      isDarknetTabMounted      = false;
  cleanupSchwarzmarktTab(); isSchwarzmarktTabMounted = false;

  // Vorauswahl, Priorität: eigener Sektions-Pfad (/labor, /viertel …, NT-LAYOUT-01) →
  // externer Navigationspunkt (verstecke.ts, lager.ts, gewächshaus-tab.ts via 'crimeops_tab') →
  // zuletzt aktive Sektion (überlebt Job-Popup-Re-Render + F5).
  // NT-LAYOUT-01: 'crimeops_tab' nimmt jetzt jede gültige Sektion an — vorher nur
  // immobilien/schwarzmarkt, der „ZUM VIERTEL"-Sprung aus lager.ts/verstecke.ts lief dadurch ins Leere.
  const valid: Section[] = ['geldwaesche', 'auftraege', 'strassenhandel', 'dealer', 'labor', 'schmuggel', 'gewächshaus', 'rolltisch', 'schwarzmarkt', 'immobilien', 'viertel', 'kontakte', 'darknet'];
  const routeSection = getCrimeOpsSectionForPath(window.location.pathname);
  const preTab = sessionStorage.getItem('crimeops_tab') as Section | null;
  sessionStorage.removeItem('crimeops_tab');
  const saved = sessionStorage.getItem('crimeops_section') as Section | null;
  if (routeSection !== null) {
    selectedSection = routeSection;
  } else if (preTab !== null && valid.includes(preTab)) {
    selectedSection = preTab;
  } else if (saved !== null && valid.includes(saved)) {
    selectedSection = saved;
  }
  sessionStorage.setItem('crimeops_section', selectedSection);
  syncCrimeOpsUrl(selectedSection);

  // Accordion: Kategorie des aktiven Tabs initial aufklappen
  expandedCategories.clear();
  const initCat = getCategoryForSection(selectedSection);
  if (initCat) expandedCategories.add(initCat);

  attachListeners();
  void reloadStatus();
  progressTimer = setInterval(updateProgressInPlace, 1_000);
  refreshTimer  = setInterval(() => { void reloadStatusInBackground(); }, 30_000);
}

export function cleanupCrimeOps(): void {
  clearRequiredBadges();
  if (refreshTimer !== null) { clearInterval(refreshTimer); refreshTimer = null; }
  cancelPendingRender(CRIME_OPS_RENDER_KEY);
  isBackgroundRefresh = false;
  if (progressTimer !== null) { clearInterval(progressTimer); progressTimer = null; }
  crimeOpsData    = null;
  selectedSection = 'geldwaesche';
  expandedCategories.clear();
  expandedWashBid = null;
  laborData        = null;
  dealerData       = null;
  dealerVehiclePanelState.clear(); // NT-POLISH-01 (P3): kein Aufklapp-Zustand über Seitenwechsel
  dealerTrainingConfirmId = null;
  schmuggelData        = null;
  smuggleRunStatus     = null;
  smuggleRunHistory    = [];
  smuggleRunSelectedItem    = '';
  smuggleRunSelectedVehicle = '';
  smuggleRunSelectedQty     = 1;
  ordersData       = null;
  vehicleList      = [];
  selectedVehicleInventory = null;
  selectedVehicleId = '';
  selectedVehicleSlotIndex = 0;
  selectedOrderNpcType = 'SCHROTTHAENDLER';
  tradeSellQty = 1;
  orderDeliveryModalOrderId = null;
  orderDeliveryModalVehicleId = '';
  orderDeliveryModalQty = 1;
  missionContactsData = [];
  missionHistoryData = [];
  missionActiveState = null;
  missionModalMissionId = null;
  missionActionInFlight = false;
  cellmateJobData = null;
  orderVehicleInventoryById.clear();
  auftragsbrettData = null;
  auftragsbrettHistoryData = [];
  auftragsbrettFreeProperties = [];
  auftragsbrettAcceptModal = null;
  cleanupImmobilienTab();
  cleanupViertelTab();
  cleanupGewaechshausTab();
  cleanupRolltischTab();
  cleanupLaborTab();
  cleanupStreetMarketTab();
  cleanupDarknetTab();
  cleanupSchwarzmarktTab();
  isImmobilienTabMounted    = false;
  isViertelTabMounted       = false;
  isGewaechshausTabMounted  = false;
  isRolltischTabMounted     = false;
  isLaborTabMounted         = false;
  isStreetMarketTabMounted  = false;
  isDarknetTabMounted       = false;
  isSchwarzmarktTabMounted  = false;
  dealerSupplyModal      = null;
  dealerSupplyProperties = [];
}
